/*
 * Seeded verbatim from the wedge (apps/wedge/src/prototype/prep-tokens.jsx TOKENS / TOKENS_DARK / TOKENS_SUN and
 * the theme-dependent extras in prep-app.jsx applyTheme). test/wedge-parity.test.mjs fails if these drift from the
 * wedge source, so the wedge can switch to this package with zero visual change. Contrast numbers in comments are
 * measured values carried over from the wedge; never soften a value below the ratio its comment states.
 */

export type ThemeName = 'light' | 'dark';

export interface ColorTokens {
  bg: string;
  bgElev: string;
  surface: string;
  surfaceAlt: string;
  separator: string;
  separatorBold: string;
  /* Full-border card edge. Must clear WCAG 1.4.11 non-text 3:1 against both the card fill and the canvas. */
  cardBorder: string;
  canvas: string;
  canvasBar: string;
  label: string;
  labelSec: string;
  labelTer: string;
  labelQuad: string;
  accent: string;
  accentDark: string;
  accentSoft: string;
  accentTint: string;
  /* Ink for text on an accent fill. Theme-inverse of bg: light fills are deep so ink is light, dark fills are bright
     so ink is the dark canvas (near-white on #34D399 is 1.9:1 and fails). */
  onAccent: string;
  red: string;
  redSoft: string;
  amber: string;
  amberSoft: string;
  gold: string;
  onGold: string;
  radarFill: string;
  radarStroke: string;
  tierFoundation: string;
  tierOwner: string;
  tierElite: string;
  tierLegend: string;
  blue: string;
  blueSoft: string;
  purple: string;
  purpleSoft: string;
  onPurple: string;
  font: string;
  mono: string;
  fontDisplay: string;
  ease: string;
  spring: string;
}

/* Theme-dependent values the wedge sets in applyTheme() rather than in TOKENS. */
export interface ChromeTokens {
  barBg: string;
  scrim: string;
  /* Opaque popover fill: dark --surface is 90% transparent glass, which reads through small popovers with no blur. */
  popoverBg: string;
  scrimModal: string;
}

/* Elevation. The wedge hardcodes these per component today (same values in both themes); they become tokens here so
   a theme can tune them later. Not part of the wedge runtime set, so the wedge bridge leaves them literal. */
export interface ElevationTokens {
  shadowRaised: string;
  shadowDock: string;
  shadowSheet: string;
  shadowDialog: string;
  shadowToast: string;
}

/* Library-only tokens (docs/specs/component-library/CATALOG.md section 4). The wedge does not write these yet, so the
   wedge bridge leaves them literal until its phase 2 migration. */
export interface LibraryTokens {
  /* Rose, the sixth hue (owner Growth group, legacy tile class rose). Light #BE123C: 6.29:1 on white, 5.80:1 on bg,
     5.24:1 on roseSoft. Dark #FB7185: 5.52:1 on the dark card, 4.54:1 on roseSoft over it. */
  rose: string;
  roseSoft: string;
  /* Ink on rose fills: white on light #BE123C 6.29:1; the dark canvas on #FB7185 6.68:1. */
  onRose: string;
  /* Teal: the email channel hue (plan item 3). Light #0F766E is 5.0:1 on the console page and 5.47:1 on white;
     tealInk #0C5F59 is 6.7:1 on tealSoft #E6F5F2. Dark #2DD4BF is 9.2:1 on the dark card; tealSoft is its 15%
     tint and the ink stays the base hue. Email semantic; keep blue for generic blue. */
  teal: string;
  tealSoft: string;
  tealInk: string;
  /* Ink on red fills. Dark: white on #EF4444 is 3.76:1 and fails, the canvas ink is 4.78:1 (same rule as onAccent). */
  onRed: string;
  /* Readable accent/blue/amber text on their soft fills; the base hues are ~4.0-4.2:1 there in light. */
  accentInk: string;
  blueInk: string;
  amberInk: string;
  backdrop: string;
  brandEmerald: string;
  brandAmber: string;
  /* Brand-mark palette: theme-invariant, verbatim from the wedge RVRadarMark / RVRadarLogo / RVWordmark art. A logo
     is not themed, so these never change per theme and are not for UI chrome. */
  brandEmeraldDeep: string;
  brandAmberSoft: string;
  brandAmberDeep: string;
  brandAmberGlow: string;
  logoCanvas: string;
  logoFaceInner: string;
  logoFaceOuter: string;
  logoRingShadow: string;
  logoRingLight: string;
  logoRingMid: string;
  logoRingDeep: string;
  logoRingHighlight: string;
  logoSweepStart: string;
  logoSweepEnd: string;
  logoSweepEdge: string;
  logoSweepLead: string;
  logoCoreLight: string;
  logoCoreDeep: string;
  logoCoreRim: string;
  logoCoreShadow: string;
  /* Wordmark REP and tagline fills. Per theme to match the official light/dark logo assets; the OnDark pair is the
     forceLight lockup on a forced dark field in any theme. */
  wordmarkInk: string;
  wordmarkTagline: string;
  wordmarkInkOnDark: string;
  wordmarkTaglineOnDark: string;
  avEmerald: string;
  avForest: string;
  avTeal: string;
  avCyan: string;
  avOcean: string;
  avPurple: string;
  avBronze: string;
  avCopper: string;
  avSlate: string;
  onAvatar: string;
  avatarRim: string;
  shadowAvatar: string;
  shadowButton: string;
  /* Fixed decorative switch thumb and shadow; state uses native semantics and thumb position. */
  switchThumb: string;
  shadowSwitch: string;
  shadowHover: string;
  /* Card depth (wedge .rv-card), theme-split. */
  shadowCard: string;
  easeIn: string;
}

/* Tokens the wedge writes at runtime in applyTheme(); inside the wedge these follow the live wedge vars. */
export type WedgeRuntimeTokens = ColorTokens & ChromeTokens;
export type ThemeTokens = WedgeRuntimeTokens & ElevationTokens & LibraryTokens;

/* Avatar palette: wedge RV_AV_TONES + RV_AV_SLATE, deep tones measured >= 5.36:1 under white ink. */
const avatar = {
  avEmerald: '#047857',
  avForest: '#166534',
  avTeal: '#0F766E',
  avCyan: '#0E7490',
  avOcean: '#155E75',
  avPurple: '#7B34C4',
  avBronze: '#A0520B',
  avCopper: '#9A3F12',
  avSlate: '#475569',
  onAvatar: '#FFFFFF',
  avatarRim: 'linear-gradient(160deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 40%, rgba(0,0,0,0.16) 100%)',
};

const shared = {
  backdrop: '#000000',
  brandEmerald: '#34D399',
  brandAmber: '#F59E0B',
  brandEmeraldDeep: '#10B981',
  brandAmberSoft: '#FBBF24',
  brandAmberDeep: '#B45309',
  /* The radar "$" blip drop-shadow: brand amber at 0xAA alpha. */
  brandAmberGlow: '#F59E0BAA',
  logoCanvas: '#001B15',
  logoFaceInner: '#123F33',
  logoFaceOuter: '#06271F',
  logoRingShadow: '#06261E',
  logoRingLight: '#66F1B7',
  logoRingMid: '#14D692',
  logoRingDeep: '#00A96F',
  logoRingHighlight: '#8AFFE0',
  logoSweepStart: '#16E89D',
  logoSweepEnd: '#10D991',
  logoSweepEdge: '#3AF0AE',
  logoSweepLead: '#65F6BE',
  logoCoreLight: '#FFC12E',
  logoCoreDeep: '#FF9500',
  logoCoreRim: '#FFCF53',
  logoCoreShadow: '#6B3E00',
  wordmarkInkOnDark: '#F0F0F0',
  wordmarkTaglineOnDark: '#B0B0B0',
  shadowButton: '0 2px 8px rgba(0,0,0,0.22)',
  /* White thumb vs accent: 4.55:1 light, 1.92:1 dark. Decorative; not text or the control outline. */
  switchThumb: '#FFFFFF',
  shadowSwitch: '0 2px 4px rgba(0,0,0,0.2)',
  shadowHover: '0 8px 22px var(--rv-accent-tint)',
  easeIn: 'cubic-bezier(0.55, 0, 1, 0.45)',
  ...avatar,
};

const elevation: ElevationTokens = {
  shadowRaised: '0 2px 10px rgba(0,0,0,0.18)',
  shadowDock: '0 12px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.14)',
  shadowSheet: '0 -16px 44px rgba(0,0,0,0.16)',
  shadowDialog: '0 24px 60px rgba(0,0,0,0.30)',
  shadowToast: '0 12px 30px rgba(0,0,0,0.25)',
};

/* Fixed shared role for scopes that do not load the canonical token stylesheet. */
export const fixedTokens = Object.freeze({ shadowButton: shared.shadowButton, switchThumb: shared.switchThumb, shadowSwitch: shared.shadowSwitch });

export const light: ThemeTokens = {
  bg: '#F4F6F8',
  bgElev: '#EAEEF2',
  surface: '#FFFFFF',
  surfaceAlt: '#F7FAFB',
  separator: 'rgba(17,34,51,0.10)',
  separatorBold: 'rgba(17,34,51,0.26)',
  /* #00875A: 4.55:1 vs #FFFFFF card interior, ~4.3:1 vs darkest canvas stop #EEF4F3. */
  cardBorder: '#00875A',
  canvas: 'linear-gradient(168deg, #FFFFFF 0%, #F5F8F9 52%, #EEF4F3 100%)',
  canvasBar: 'linear-gradient(180deg, #FFFFFF 0%, #F3F7F6 100%)',
  label: '#161C24',
  labelSec: '#475569',
  labelTer: '#5B6B7B',
  labelQuad: '#AEB8C4',
  accent: '#00875A',
  accentDark: '#016241',
  accentSoft: '#E6F4EE',
  accentTint: 'rgba(0,135,90,0.10)',
  /* Kept equal to the wedge TOKENS object for parity. Nobody renders it: the wedge measures ink against the live
     accent at runtime (rvInkOn) and tokens.css mirrors that (#FFFFFF here, 4.55:1). */
  onAccent: '#F5F5F7',
  red: '#D70015',
  redSoft: '#FFE8EA',
  amber: '#B25E09',
  amberSoft: '#FFF1DC',
  /* #F59E0B on white is ~1.9:1; #B25E09 clears 3:1 non-text. */
  gold: '#B25E09',
  onGold: '#FFFFFF',
  radarFill: '0.5',
  radarStroke: '3.2',
  /* Deep variants measured on #FFFFFF: foundation 4.55, owner 4.67, elite 5.36, legend 6.48. */
  tierFoundation: '#00875A',
  tierOwner: '#B25E09',
  tierElite: '#0E7490',
  tierLegend: '#6E3CD2',
  blue: '#0A66FF',
  blueSoft: '#E5EEFF',
  purple: '#6E3CD2',
  purpleSoft: '#EFE7FB',
  onPurple: '#F5F5F7',
  font: "'Jost', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  mono: "'Share Tech Mono', 'SF Mono', 'JetBrains Mono', Menlo, Monaco, 'Cascadia Mono', monospace",
  fontDisplay: "'Orbitron', 'Jost', sans-serif",
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  barBg: 'rgba(255,255,255,0.85)',
  scrim: 'rgba(255,255,255,0.92)',
  popoverBg: '#FFFFFF',
  scrimModal: 'rgba(18,26,22,0.40)',
  ...elevation,
  ...shared,
  /* 5.38:1 on #D70015. */
  onRed: '#FFFFFF',
  rose: '#BE123C',
  roseSoft: '#FFE4E8',
  onRose: '#FFFFFF',
  /* #0F766E: 5.0:1 on the console page, 5.47:1 on white; tealInk #0C5F59 6.7:1 on tealSoft #E6F5F2. */
  teal: '#0F766E',
  tealSoft: '#E6F5F2',
  tealInk: '#0C5F59',
  wordmarkInk: '#111111',
  wordmarkTagline: '#555555',
  /* 6.56:1 on accentSoft #E6F4EE. */
  accentInk: '#016241',
  /* 5.35:1 on blueSoft #E5EEFF; also the wedge TabBar drive hue verbatim. */
  blueInk: '#0057D9',
  /* 5.35:1 on amberSoft #FFF1DC; also the wedge TabBar search hue verbatim. */
  amberInk: '#9A5007',
  shadowCard: '0 2px 10px rgba(16,24,20,0.07)',
  shadowAvatar: 'inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 1px rgba(0,0,0,0.14), 0 1px 2px rgba(15,23,42,0.16), 0 2px 6px -2px rgba(15,23,42,0.14)',
};

/* The dark theme IS the RepVantage brand palette (deep-field #0A1A10, emerald #34D399, Jost / Share Tech Mono /
   Orbitron), aligned to the marketing site by the wedge on purpose. */
export const dark: ThemeTokens = {
  bg: '#0A1A10',
  bgElev: '#061008',
  surface: 'rgba(52,211,153,0.03)',
  surfaceAlt: 'rgba(52,211,153,0.06)',
  separator: 'rgba(52,211,153,0.15)',
  separatorBold: 'rgba(52,211,153,0.42)',
  /* 3.68:1 vs #0A1A10 canvas, 3.38:1 vs the translucent emerald tiles. */
  cardBorder: 'rgba(52,211,153,0.55)',
  canvas:
    'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
  canvasBar: 'linear-gradient(180deg, #0C1E15 0%, #0A1A12 100%)',
  label: '#E2E8F0',
  labelSec: '#94A3B8',
  labelTer: '#8494A8',
  labelQuad: '#475569',
  accent: '#34D399',
  accentDark: '#16A34A',
  accentSoft: 'rgba(52,211,153,0.15)',
  accentTint: 'rgba(52,211,153,0.40)',
  onAccent: '#0A1A10',
  red: '#EF4444',
  redSoft: 'rgba(239,68,68,0.15)',
  amber: '#F59E0B',
  amberSoft: 'rgba(245,158,11,0.15)',
  gold: '#F59E0B',
  onGold: '#241A00',
  radarFill: '0.3',
  radarStroke: '2.4',
  tierFoundation: '#34D399',
  tierOwner: '#F59E0B',
  tierElite: '#22D3EE',
  tierLegend: '#A78BFA',
  blue: '#22D3EE',
  blueSoft: 'rgba(34,211,238,0.15)',
  purple: '#C084FC',
  purpleSoft: 'rgba(192,132,252,0.15)',
  /* #F5F5F7 on #C084FC is ~1.9:1 and fails, so the ink is the dark canvas. */
  onPurple: '#1B0B2E',
  font: "'Jost', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  mono: "'Share Tech Mono', 'SF Mono', 'JetBrains Mono', Menlo, Monaco, 'Cascadia Mono', monospace",
  fontDisplay: "'Orbitron', 'Jost', sans-serif",
  ease: 'cubic-bezier(0.16, 1, 0.3, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  barBg: 'rgba(10,26,16,0.86)',
  scrim: 'rgba(10,26,16,0.92)',
  popoverBg: '#0E2C1E',
  scrimModal: 'rgba(5,14,9,0.72)',
  ...elevation,
  ...shared,
  /* 4.78:1 on #EF4444. */
  onRed: '#0A1A10',
  rose: '#FB7185',
  roseSoft: 'rgba(251,113,133,0.15)',
  onRose: '#0A1A10',
  /* #2DD4BF: 9.2:1 on the dark card; tealSoft is the 15% tint and the ink is the base hue. */
  teal: '#2DD4BF',
  tealSoft: 'rgba(45,212,191,0.15)',
  tealInk: '#2DD4BF',
  wordmarkInk: '#F0F0F0',
  wordmarkTagline: '#8A8F8C',
  accentInk: '#34D399',
  blueInk: '#22D3EE',
  amberInk: '#F59E0B',
  shadowCard: '0 1px 0 rgba(255,255,255,0.05) inset, 0 8px 24px rgba(0,0,0,0.22)',
  shadowAvatar: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 1px rgba(0,0,0,0.22), 0 0 0 1px rgba(52,211,153,0.14), 0 4px 10px -4px rgba(0,0,0,0.5)',
};

export const themes: Record<ThemeName, ThemeTokens> = { light, dark };

/*
 * Sun-glare layer: NOT a third theme. An override map applied on top of the light theme for reps in direct
 * sunlight: pure white canvas, near-black ink, heavier separators, deep accent (>= 7:1 on white). Keys are the
 * camelCase token names; only these keys change.
 */
export const sun: Partial<ThemeTokens> = {
  bg: '#FFFFFF',
  bgElev: '#EFEFF2',
  surface: '#FFFFFF',
  surfaceAlt: '#FFFFFF',
  separator: 'rgba(0,0,0,0.32)',
  separatorBold: 'rgba(0,0,0,0.55)',
  cardBorder: '#015C3E',
  canvas: '#FFFFFF',
  canvasBar: '#F2F2F4',
  label: '#000000',
  labelSec: '#2F3439',
  labelTer: '#40474E',
  labelQuad: '#5C646C',
  accent: '#016241',
  accentDark: '#014D33',
  accentSoft: '#D6EEE2',
  accentTint: 'rgba(1,98,65,0.16)',
  onAccent: '#FFFFFF',
  red: '#B00012',
  amber: '#8A4A00',
  /* The primary "I walked in" CTA rides --gold; base gold on white is 1.9:1. */
  gold: '#B25E09',
  onGold: '#FFFFFF',
  /* The call button rides --purple and is used in the truck in daylight. */
  purple: '#5B2FB8',
  onPurple: '#FFFFFF',
  barBg: 'rgba(255,255,255,0.97)',
  scrim: 'rgba(255,255,255,0.97)',
  popoverBg: '#FFFFFF',
  /* 7.36:1 on sun red #B00012. */
  /* Sun deepens rose like the other hues: 8.02:1 on white. */
  rose: '#9F1239',
  onRed: '#FFFFFF',
  accentInk: '#014D33',
  amberInk: '#8A4A00',
};

/* Rep-selectable accents (wedge ACCENTS in prep-app.jsx). Key = light-theme accent. */
export interface AccentRamp {
  dark: string;
  soft: string;
  tint: string;
  darkMain: string;
  darkSoft: string;
  darkTint: string;
}

export const accents: Record<string, AccentRamp> = {
  '#00875A': { dark: '#016241', soft: '#E6F4EE', tint: 'rgba(0,135,90,0.10)', darkMain: '#34D399', darkSoft: 'rgba(52,211,153,0.15)', darkTint: 'rgba(52,211,153,0.40)' },
  '#0A66FF': { dark: '#0747B6', soft: '#E5EEFF', tint: 'rgba(10,102,255,0.10)', darkMain: '#0A84FF', darkSoft: 'rgba(10,132,255,0.15)', darkTint: 'rgba(10,132,255,0.12)' },
  '#D97757': { dark: '#A35530', soft: '#FBEDE5', tint: 'rgba(217,119,87,0.10)', darkMain: '#FF9F6A', darkSoft: 'rgba(255,159,106,0.15)', darkTint: 'rgba(255,159,106,0.12)' },
  '#1D1D1F': { dark: '#000000', soft: '#EEEEF1', tint: 'rgba(29,29,31,0.08)', darkMain: '#FFFFFF', darkSoft: 'rgba(255,255,255,0.12)', darkTint: 'rgba(255,255,255,0.08)' },
};

/*
 * Product layer. Every product inherits the base themes above; a product may only add a partial override here,
 * emitted as a [data-rv-product="<name>"] block. Empty is the goal: every key added needs a comment saying why the
 * product must differ from the base.
 */
export type ProductName = 'wedge' | 'marketing' | 'remix' | 'recon' | 'consoles' | 'owner' | 'admin' | 'shop' | 'comply' | 'signshop' | 'demo';

export interface ProductOverride {
  light?: Partial<ThemeTokens>;
  dark?: Partial<ThemeTokens>;
  /* Product-only roles with no base token, keyed by full custom property name and emitted verbatim. Same rule: each
     needs a why-comment, and a role that a second product needs is promoted to a base token instead. */
  roles?: { light?: Record<string, string>; dark?: Record<string, string> };
}

/*
 * Owner console shell chrome (sidebar, top bar, page backdrop) for the /next/ app, verbatim from the legacy
 * legacy owner console chrome rules (.sidebar, .brandrow, .navitem, .navlabel, .topbar, .pill, .btn,
 * #sampleBanner, body backdrop) so the port is a visual no-op. Legacy draws these with literal rgba values and
 * per-theme rule overrides; the /next/ app may not carry literals, so each value is a role here. Non-color roles
 * (weights, sizes, bar width) are the light-theme rule overrides legacy applies to the same elements. Nothing in
 * the legacy page reads the --chrome-* names.
 */
const consolesChrome: Required<NonNullable<ProductOverride['roles']>> = {
  dark: {
    /* Legacy light sets body text to weight 450 (owner light theme v3.1); dark stays 400. */
    '--chrome-body-weight': '400',
    /* Legacy --display stack. T.fontDisplay falls back to Jost, which draws the title's middle dot taller than
       Orbitron and grows the bar title from 19px to 22px; legacy falls back to sans-serif. */
    '--chrome-display': "'Orbitron',sans-serif",
    '--chrome-page':
      'radial-gradient(1200px 600px at 82% -10%,rgba(52,211,153,0.07),transparent 60%),radial-gradient(900px 500px at -10% 110%,rgba(34,211,238,0.05),transparent 60%),radial-gradient(700px 700px at 50% 120%,rgba(192,132,252,0.03),transparent 60%),var(--rv-bg)',
    /* Command-center backdrop: faint tactical grid behind content, scanlines above (dark only). */
    '--chrome-grid':
      'linear-gradient(rgba(52,211,153,0.028) 1px,transparent 1px),linear-gradient(90deg,rgba(52,211,153,0.028) 1px,transparent 1px)',
    '--chrome-grid-mask': 'radial-gradient(1200px 800px at 50% 0%,#000 30%,transparent 100%)',
    '--chrome-scan': 'repeating-linear-gradient(0deg,rgba(3,9,5,0.4) 0 1px,transparent 1px 3px)',
    '--chrome-edge': 'var(--rv-separator)',
    '--chrome-sidebar': 'linear-gradient(180deg,rgba(52,211,153,0.055),rgba(52,211,153,0.015) 34%,rgba(52,211,153,0.035))',
    '--chrome-sidebar-shadow': 'none',
    /* Narrow screens: the sidebar becomes an opaque off-canvas drawer over a blurred scrim. */
    '--chrome-drawer': 'linear-gradient(180deg,rgba(52,211,153,0.06),rgba(52,211,153,0.02)),var(--rv-bg)',
    '--chrome-drawer-shadow': '20px 0 60px rgba(0,0,0,.5)',
    '--chrome-scrim': 'rgba(4,10,6,.55)',
    '--chrome-rule': 'linear-gradient(90deg,rgba(52,211,153,0.35),rgba(52,211,153,0.02))',
    '--chrome-rule-foot': 'linear-gradient(90deg,rgba(52,211,153,0.30),rgba(52,211,153,0.02))',
    '--chrome-word-glow': 'drop-shadow(0 0 10px rgba(52,211,153,.18))',
    '--chrome-mark-glow': 'drop-shadow(0 0 6px rgba(52,211,153,.35))',
    '--chrome-ng-size': '10.5px',
    '--chrome-ng-track': '1.8px',
    '--chrome-nav-weight': '400',
    '--chrome-nav-weight-on': '400',
    '--chrome-nav-hover': 'var(--rv-surface-alt)',
    '--chrome-nav-active': 'linear-gradient(90deg,rgba(52,211,153,0.16),rgba(52,211,153,0.04))',
    '--chrome-nav-active-edge': 'var(--rv-separator)',
    '--chrome-nav-bar': 'var(--rv-accent)',
    '--chrome-nav-bar-glow': '0 0 10px var(--rv-accent-tint)',
    '--chrome-nav-bar-w': '2px',
    '--chrome-nav-icon': 'var(--rv-accent)',
    '--chrome-nav-icon-alpha': '0.9',
    '--chrome-who-weight': '400',
    '--chrome-foot-size': '11px',
    '--chrome-bar': 'linear-gradient(180deg,rgba(10,26,16,0.88),rgba(10,26,16,0.66))',
    '--chrome-bar-shadow': 'none',
    '--chrome-title-weight': '700',
    '--chrome-title-glow': '0 0 22px rgba(52,211,153,0.22)',
    '--chrome-pill': 'rgba(52,211,153,0.02)',
    '--chrome-pill-edge': 'var(--rv-separator)',
    '--chrome-pill-size': '11px',
    '--chrome-pill-weight': '400',
    '--chrome-pill-ink': 'var(--rv-label-sec)',
    '--chrome-live-ink': 'var(--rv-accent)',
    '--chrome-live': 'var(--rv-accent-soft)',
    '--chrome-live-edge': 'rgba(52,211,153,0.35)',
    '--chrome-live-glow-lo': '0 0 14px rgba(52,211,153,0.12),inset 0 0 8px rgba(52,211,153,0.05)',
    '--chrome-live-glow-hi': '0 0 26px rgba(52,211,153,0.3),inset 0 0 10px rgba(52,211,153,0.12)',
    '--chrome-dot': 'var(--rv-accent)',
    '--chrome-dot-glow': '0 0 8px var(--rv-accent-tint)',
    '--chrome-btn': 'linear-gradient(180deg,rgba(52,211,153,0.08),rgba(52,211,153,0.03))',
    '--chrome-btn-edge': 'var(--rv-separator)',
    '--chrome-btn-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10)',
    '--chrome-btn-hover': 'rgba(52,211,153,0.10)',
    '--chrome-btn-hover-edge': 'var(--rv-separator-bold)',
    '--chrome-btn-hover-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10),0 6px 18px rgba(0,0,0,.25)',
    /* Legacy .btn.accent and .modal .btn.danger (Button variant="console" tones). Dark has no generic .btn:disabled
       look; the blocked-send rules dim to .45. */
    '--chrome-btn-accent': 'linear-gradient(180deg,var(--rv-accent),rgba(52,211,153,0.82))',
    '--chrome-btn-accent-ink': 'var(--rv-bg)',
    '--chrome-btn-accent-edge': 'var(--rv-accent)',
    '--chrome-btn-accent-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10)',
    '--chrome-btn-accent-hover': 'linear-gradient(180deg,var(--rv-accent),rgba(52,211,153,0.82))',
    '--chrome-btn-accent-hover-shadow': '0 8px 24px rgba(52,211,153,.3)',
    '--chrome-btn-accent-hover-filter': 'brightness(1.06)',
    '--chrome-btn-danger-ink': 'var(--rv-amber)',
    '--chrome-btn-danger-edge': 'rgba(245,158,11,.45)',
    '--chrome-btn-danger-hover-edge': 'rgba(245,158,11,.45)',
    '--chrome-btn-disabled-opacity': '0.45',
    '--chrome-sample': 'repeating-linear-gradient(-45deg,rgba(245,158,11,0.18) 0 14px,rgba(245,158,11,0.07) 14px 28px)',
    '--chrome-sample-ink': 'var(--rv-amber)',
    '--chrome-sample-edge': 'rgba(245,158,11,0.55)',
    '--chrome-sample-shadow': '0 8px 24px rgba(245,158,11,0.10)',
    '--chrome-sample-text-shadow': '0 1px 2px rgba(0,0,0,.45)',
  },
  light: {
    '--chrome-body-weight': '450',
    '--chrome-display': "'Orbitron',sans-serif",
    '--chrome-page': 'radial-gradient(1200px 600px at 82% -10%,rgba(52,211,153,0.07),transparent 60%),var(--rv-bg)',
    '--chrome-grid': 'none',
    '--chrome-grid-mask': 'none',
    '--chrome-scan': 'none',
    '--chrome-edge': 'rgba(26,26,46,0.12)',
    '--chrome-sidebar': '#FFFFFF',
    '--chrome-sidebar-shadow': '1px 0 0 rgba(26,26,46,0.03),6px 0 24px rgba(26,26,46,0.04)',
    '--chrome-drawer': '#FFFFFF',
    '--chrome-drawer-shadow': '1px 0 0 rgba(26,26,46,0.03),6px 0 24px rgba(26,26,46,0.04)',
    '--chrome-scrim': 'rgba(4,10,6,.55)',
    '--chrome-rule': 'rgba(26,26,46,0.10)',
    '--chrome-rule-foot': 'rgba(26,26,46,0.10)',
    '--chrome-word-glow': 'none',
    '--chrome-mark-glow': 'drop-shadow(0 0 6px rgba(52,211,153,.35))',
    '--chrome-ng-size': '11.5px',
    '--chrome-ng-track': '1.4px',
    '--chrome-nav-weight': '500',
    '--chrome-nav-weight-on': '600',
    '--chrome-nav-hover': 'rgba(16,185,129,0.08)',
    '--chrome-nav-active': 'linear-gradient(90deg,rgba(16,185,129,0.16),rgba(16,185,129,0.05))',
    '--chrome-nav-active-edge': 'rgba(5,150,105,0.28)',
    '--chrome-nav-bar': '#10B981',
    '--chrome-nav-bar-glow': 'none',
    '--chrome-nav-bar-w': '3px',
    '--chrome-nav-icon': '#047857',
    '--chrome-nav-icon-alpha': '1',
    '--chrome-who-weight': '600',
    '--chrome-foot-size': '12px',
    '--chrome-bar': 'rgba(255,255,255,0.92)',
    '--chrome-bar-shadow': '0 1px 0 rgba(26,26,46,0.04),0 4px 14px rgba(26,26,46,0.04)',
    '--chrome-title-weight': '800',
    '--chrome-title-glow': 'none',
    '--chrome-pill': '#FFFFFF',
    '--chrome-pill-edge': 'rgba(26,26,46,0.16)',
    '--chrome-pill-size': '12px',
    '--chrome-pill-weight': '600',
    '--chrome-pill-ink': 'var(--rv-label-sec)',
    '--chrome-live-ink': '#052E1C',
    '--chrome-live': '#D1FAE5',
    '--chrome-live-edge': 'rgba(5,150,105,0.45)',
    '--chrome-live-glow-lo': 'none',
    '--chrome-live-glow-hi': 'none',
    '--chrome-dot': '#10B981',
    '--chrome-dot-glow': '0 0 0 3px rgba(16,185,129,0.18)',
    '--chrome-btn': '#FFFFFF',
    '--chrome-btn-edge': 'rgba(26,26,46,0.18)',
    '--chrome-btn-shadow': '0 1px 2px rgba(26,26,46,0.06)',
    '--chrome-btn-hover': '#F0FBF6',
    '--chrome-btn-hover-edge': 'rgba(5,150,105,0.55)',
    '--chrome-btn-hover-shadow': '0 2px 8px rgba(26,26,46,0.08)',
    /* Light .btn rules out-rank .modal .btn.danger, so danger draws as the plain button there. Light .btn:disabled
       greys every tone. */
    '--chrome-btn-accent': 'linear-gradient(180deg,#34D399,#10B981)',
    '--chrome-btn-accent-ink': '#052E1C',
    '--chrome-btn-accent-edge': '#059669',
    '--chrome-btn-accent-shadow': '0 1px 2px rgba(5,150,105,0.25)',
    '--chrome-btn-accent-hover': 'linear-gradient(180deg,#4ADEAA,#0FB07A)',
    '--chrome-btn-accent-hover-shadow': '0 6px 16px rgba(5,150,105,0.25)',
    '--chrome-btn-accent-hover-filter': 'none',
    '--chrome-btn-danger-ink': 'var(--rv-label)',
    '--chrome-btn-danger-edge': 'rgba(26,26,46,0.18)',
    '--chrome-btn-danger-hover-edge': 'rgba(5,150,105,0.55)',
    '--chrome-btn-disabled-opacity': '1',
    '--chrome-btn-disabled': '#EEF0F3',
    '--chrome-btn-disabled-ink': '#5E6676',
    '--chrome-btn-disabled-edge': 'rgba(26,26,46,0.14)',
    '--chrome-btn-disabled-shadow': 'none',
    '--chrome-sample': 'repeating-linear-gradient(-45deg,rgba(245,158,11,0.20) 0 14px,rgba(245,158,11,0.10) 14px 28px)',
    '--chrome-sample-ink': '#6B3A00',
    '--chrome-sample-edge': 'rgba(180,83,9,0.45)',
    '--chrome-sample-shadow': 'none',
    '--chrome-sample-text-shadow': 'none',
  },
};

/*
 * Owner console type scale, glows and overlay layers for the owner app, verbatim from the legacy
 * owner console page (oc-type-css :root block, the hard-coded emerald glows, the overlay z-index stack).
 * Renamed --type-*, --glow-*, --layer-* because legacy keeps its own --fs-*, --fw-*, --lh-*, --ls-*, --sp-* in
 * oc-type-css, with phone steps in media queries; this block outranks those rules, so the legacy names here would
 * freeze the desktop sizes on phones. Not --console-*: that prefix is for roles a core variant reads
 * (packages/ui src/internal/consoleRoles.ts). The phone steps (title 19px, section 14px and head 10px at max-width
 * 760px; title 18px and body 13.5px at max-width 380px) have no role: a role is per theme, not per width.
 */
const consolesTypeDark: Record<string, string> = {
  /* Size ladder: page title > modal title > section heading > sub heading > body > meta > table and tile label > caption. */
  '--type-fs-title': '22px',
  '--type-fs-modal-title': '13.5px',
  '--type-fs-heading': '11.5px',
  '--type-fs-label': '10.5px',
  '--type-fs-sub': '12.5px',
  '--type-fs-body': '14px',
  '--type-fs-body-sm': '13px',
  '--type-fs-quote': '13.5px',
  '--type-fs-meta': '12px',
  '--type-fs-caption': '11px',
  '--type-fw-title': '700',
  '--type-fw-heading': '700',
  '--type-fw-sub': '600',
  '--type-fw-body': '400',
  '--type-fw-meta': '400',
  '--type-lh-title': '1.2',
  '--type-lh-heading': '1.3',
  '--type-lh-body': '1.55',
  '--type-lh-meta': '1.45',
  /* Display-font micro labels (section headings, th, tile labels) are tracked out; body text is not. */
  '--type-ls-heading': '1.3px',
  '--type-ls-label': '1px',
  '--type-sp-section': '18px',
  '--type-sp-head': '12px',
  '--type-sp-para': '10px',
  '--type-sp-line': '6px',
  /* Glows legacy draws with literal emerald and red rgba (the bar glow is --console-bar-glow, read by ProgressBar).
     Tab: the active tab halo (.dtab.active). Stage: the live stage pill (.stagepill.on) and its error state. Score:
     the hot lead score badge (.ldscore.s3). */
  '--glow-tab': '0 0 18px rgba(52,211,153,0.10)',
  '--glow-stage': '0 0 14px rgba(52,211,153,0.18)',
  '--glow-stage-err': '0 0 12px rgba(239,68,68,0.2)',
  '--glow-score': '0 0 12px rgba(52,211,153,0.18)',
  /* Overlay stack, low to high: the legacy layers the /next/ app ports. Side drawers sit under a center modal, and a
     popover, the command palette and toasts sit above both. scale.ts z stops at toast 9500; legacy toasts are 9997. */
  '--layer-scrim': '9090',
  '--layer-drawer': '9100',
  '--layer-drawer-top': '9160',
  '--layer-modal': '9200',
  '--layer-popover': '9300',
  '--layer-palette': '9400',
  '--layer-toast': '9997',
};

const consolesTypeLight: Record<string, string> = {
  ...consolesTypeDark,
  /* Light sets small type one step larger and body text to 450 (owner light theme v3.1 small-font floor). */
  '--type-fs-heading': '12px',
  '--type-fs-label': '11.5px',
  '--type-fs-meta': '12.5px',
  '--type-fs-caption': '12px',
  '--type-fw-body': '450',
  /* Light .dtab.active swaps the halo for a hairline and a brand underline; the other glows stay, as legacy. */
  '--glow-tab': 'none',
};

/* Marketing Graphic studio canvas palette (legacy OCM_PAL and the ocmBackground stops).
   The graphic is fixed brand artwork with its own dark and light look, so these roles carry the same value under both
   console themes. Exact legacy values so the canvas port is a pixel no-op; the dark look reuses --ink-title, the
   canvas stops of base dark and the brand emerald and amber tokens. */
const consolesStudio: Record<string, string> = {
  '--studio-sub-dark': '#C0CCDC',
  '--studio-muted-dark': '#93A1B3',
  '--studio-cta-ink-dark': '#062014',
  '--studio-veil-dark': '#07120C',
  '--studio-ink-light': '#0F1F17',
  '--studio-sub-light': '#34344E',
  '--studio-muted-light': '#5B6472',
  '--studio-em-light': '#0E9F6E',
  '--studio-cyan-light': '#0E7490',
  '--studio-bg-light-top': '#F3FAF6',
  '--studio-bg-light-mid': '#FAFBFC',
  '--studio-bg-light-bot': '#EDF6F0',
};

/*
 * Owner console (owner.repvantage.io). Values are verbatim from the legacy owner console palette, so
 * the inlined token block (scripts/build-console-tokens.mjs) is a visual no-op. Base keys the owner page already
 * matches (dark bg, accent, separator, amber, purple, red and their soft fills) are not repeated here.
 */
const consoles: ProductOverride = {
  dark: {
    /* Owner glass fills are fainter than base (3% / 6% vs 10% / 14%); the owner page layers them over a backdrop grid. */
    surface: 'rgba(52,211,153,0.03)',
    surfaceAlt: 'rgba(52,211,153,0.06)',
    /* Owner hover edge (--borderHover) is lighter than the base bold separator. */
    separatorBold: 'rgba(52,211,153,0.28)',
    /* Owner text ladder is brighter than base. labelTer and labelQuad were #93A1B3 / #8898AC (4.27 / 4.24:1 on the
       brightest owner tile glass); these clear 5.39 / 5.42:1 there (tools/dark-contrast-audit.mjs, 2026-10-05). */
    label: '#EDF2F8',
    labelSec: '#C0CCDC',
    labelTer: '#A8B5C5',
    labelQuad: '#9DABBD',
    /* Owner dark hues are lighter than base: the owner page sets hue text on hue-tinted chips and nav badges over the
       emerald glass, where base red #EF4444 is 3.21:1. Worst measured background per hue: red 4.90, amber 4.66, blue
       5.27, purple 5.18, rose 5.22:1. Soft fills keep the base hue (redSoft etc.), and every solid hue fill on the
       page is a bar, dot or swatch with no text on it. Amber stays warmer than gold #FBBF24 so the two read apart. */
    red: '#FA8080',
    amber: '#FBB454',
    blue: '#67E8F9',
    purple: '#CC99FD',
    rose: '#FC8B9B',
    /* Owner gold is the lighter amber #FBBF24; base gold equals base amber, and the owner page shows both side by side. */
    gold: '#FBBF24',
    /* Owner type stacks are the legacy --font / --mono / --display verbatim, so computed font-family matches the legacy
       page exactly (base stacks carry extra system fallbacks). */
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
  },
  light: {
    /* Owner light canvas and emerald-tinted glass fills (canonical owner light tokens, v3.1). */
    bg: '#F3F5F8',
    surface: 'rgba(16,185,129,0.045)',
    surfaceAlt: 'rgba(16,185,129,0.075)',
    /* Owner borders are navy ink, not slate; hover edge is emerald. */
    separator: 'rgba(26,26,46,0.14)',
    separatorBold: 'rgba(5,150,105,0.55)',
    /* Owner light text is a navy ink ladder, not base slate. */
    label: '#1A1A2E',
    labelSec: '#33374B',
    labelTer: '#4B5363',
    labelQuad: '#5E6676',
    /* Owner light hues are deeper than base and the soft fills are tints, not opaque pastels (v3.1 owner light theme). */
    accent: '#036B4D',
    accentSoft: 'rgba(16,185,129,0.14)',
    accentTint: 'rgba(5,150,105,0.55)',
    amber: '#92400E',
    amberSoft: 'rgba(245,158,11,0.16)',
    purple: '#6D28D9',
    purpleSoft: 'rgba(139,92,246,0.13)',
    blue: '#155E75',
    blueSoft: 'rgba(6,182,212,0.13)',
    red: '#B91C1C',
    redSoft: 'rgba(239,68,68,0.12)',
    gold: '#854D0E',
    /* Owner light rose is the deeper legacy --rose (8.02:1 on the white card, 6.86:1 on its tint). */
    rose: '#9F1239',
    roseSoft: 'rgba(244,63,94,0.12)',
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
  },
  roles: {
    dark: {
      ...consolesStudio,
      /* Third glass step (active / pressed tiles); base has two surface steps. */
      '--surface3': 'rgba(52,211,153,0.10)',
      /* Legacy .tdbtn hover fill (Button variant="action"): --surface3. */
      '--console-act-hover': 'var(--surface3)',
      '--console-fill-3': 'var(--surface3)',
      /* Frosted chrome (topbar, drawers, popovers): translucent, unlike the opaque base popover fill. */
      '--glassDim': 'rgba(10,26,16,0.6)',
      '--glassDeep': 'rgba(7,17,11,0.78)',
      /* Legacy owner names: rose and its fill (base T.rose now carries the same hue for library code) and the gold soft
         fill, which has no base token. */
      '--rose': '#FC8B9B',
      '--roseDim': 'rgba(251,113,133,0.15)',
      '--goldDim': 'rgba(251,191,36,0.16)',
      /* Card top-edge highlight and hover lift. */
      '--edge': 'inset 0 1px 0 rgba(52,211,153,0.10)',
      '--lift': '0 14px 34px rgba(3,14,7,0.5),0 2px 8px rgba(2,10,5,0.35)',
      /* Modal look="panel" shadow: the legacy --lift above. */
      '--console-panel-shadow': '0 14px 34px rgba(3,14,7,0.5),0 2px 8px rgba(2,10,5,0.35)',
      /* Owner typographic ink levels (title > heading > body > sub > meta > caption), from the type-hierarchy pass. */
      '--ink-title': '#EDF2F8',
      '--ink-heading': '#EDF2F8',
      '--ink-body': '#D5DEEA',
      '--ink-sub': '#B9C6D6',
      '--ink-meta': '#9DABBD',
      '--ink-caption': '#8898AC',
      '--ink-num': '#EDF2F8',
      '--ink-quote-rule': 'rgba(52,211,153,0.42)',
      /* Owner nav group title hues, one per group, each 4.5:1 on its own sidebar. */
      '--ng-overview': '#34d399',
      '--ng-pipeline': '#22D3EE',
      '--ng-revenue': '#F59E0B',
      '--ng-people': '#C084FC',
      '--ng-growth': '#FB7185',
      '--ng-products': '#FBBF24',
      '--ng-admin': '#C0CCDC',
      ...consolesChrome.dark,
      ...consolesTypeDark,
      /* Console widget roles, read by the core variant="console" Card, Stat, SectionHeader and ProgressBar (packages/ui
         src/internal/consoleRoles.ts) with base-token fallbacks, so the /next/ widgets match legacy .card, .tile and
         .wtitle. Values are the legacy computed styles. A role left unset in one theme falls back to the base token
         (dark card border = separator, tile hover border = separatorBold, accent bar = the tile hue). */
      '--console-card-fill': 'linear-gradient(168deg,rgba(52,211,153,0.06),rgba(52,211,153,0.015) 46%,rgba(52,211,153,0.035))',
      '--console-card-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10),0 2px 6px rgba(2,10,5,0.35)',
      '--console-card-pad': '17px 19px',
      '--console-tile-fill': 'linear-gradient(165deg,rgba(52,211,153,0.11),rgba(52,211,153,0.025) 58%)',
      '--console-tile-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10),0 8px 22px rgba(3,14,7,0.4),0 1px 3px rgba(2,10,5,0.3)',
      '--console-tile-shadow-hover':
        'inset 0 1px 0 rgba(52,211,153,0.10),0 14px 34px rgba(3,14,7,0.5),0 2px 8px rgba(2,10,5,0.35),0 0 28px rgba(52,211,153,0.07)',
      '--console-tile-pad': '16px 17px 14px',
      '--console-tile-label-size': '10.5px',
      '--console-caret-size': '11px',
      '--console-tile-value-size': '33px',
      /* Legacy @media(max-width:560px) .tile .v. */
      '--console-tile-value-size-sm': '28px',
      '--console-tile-value-weight': '400',
      '--console-tile-value-tracking': '0px',
      '--console-tile-sub-size': '11px',
      '--console-tile-sub-weight': '400',
      /* Glow strength (color-mix percent of the hue): dark glows, light is flat, as legacy. */
      '--console-tile-glow': '30%',
      /* The blank-hue tile glows accent at 28% (legacy .tile .v), the hued ones at 30%. */
      '--console-tile-glow-neutral': '28%',
      /* Legacy .tile .accentbar per hue (owner and admin): the live hue fades to a fixed rgba of the dark hue. */
      '--console-tile-bar-neutral': 'linear-gradient(180deg,var(--rv-accent) 10%,rgba(52,211,153,0.04))',
      '--console-tile-bar-blue': 'linear-gradient(180deg,var(--rv-blue) 10%,rgba(34,211,238,0.04))',
      '--console-tile-bar-amber': 'linear-gradient(180deg,var(--rv-amber) 10%,rgba(245,158,11,0.04))',
      '--console-tile-bar-purple': 'linear-gradient(180deg,var(--rv-purple) 10%,rgba(192,132,252,0.04))',
      '--console-tile-bar-rose': 'linear-gradient(180deg,var(--rv-rose) 10%,rgba(251,113,133,0.04))',
      '--console-tile-bar-gold': 'linear-gradient(180deg,var(--rv-gold) 10%,rgba(251,191,36,0.04))',
      /* Legacy dark .tile .v glow: a fixed rgba per hue, 0.28 on the blank hue, 0.3 on the others. */
      '--console-tile-value-shadow-neutral': '0 0 24px rgba(52,211,153,0.28)',
      '--console-tile-value-shadow-blue': '0 0 24px rgba(34,211,238,0.3)',
      '--console-tile-value-shadow-amber': '0 0 24px rgba(245,158,11,0.3)',
      '--console-tile-value-shadow-purple': '0 0 24px rgba(192,132,252,0.3)',
      '--console-tile-value-shadow-rose': '0 0 24px rgba(251,113,133,0.3)',
      '--console-tile-value-shadow-gold': '0 0 24px rgba(251,191,36,0.3)',
      '--console-title-glow': '45%',
      '--console-title-size': '14.5px',
      '--console-aux-size': '12px',
      '--console-info-size': '10px',
      /* Chip text (legacy .chip 11.5px, tier chips 10px). */
      '--console-chip-size': '11.5px',
      '--console-chip-size-sm': '10px',
      '--console-chip-weight': '400',
      /* Chip edges (legacy .chip.ok/.cy/.pu at 35%, .warn/.red at 40% of the hue) and the neutral chip glass fill.
         Ink and fill are the base hue and soft tokens in dark. Ink on fill over the card: accent 6.16, amber 5.81,
         blue 6.49, purple 4.89:1. Red departs from legacy on purpose: legacy #EF4444 is 3.79:1 there and fails AA, so
         the red ink is #F87171 at 5.16:1. */
      '--console-chip-red-ink': '#F87171',
      '--console-chip-accent-edge': 'rgba(52,211,153,0.35)',
      '--console-chip-amber-edge': 'rgba(245,158,11,0.4)',
      '--console-chip-blue-edge': 'rgba(34,211,238,0.35)',
      '--console-chip-purple-edge': 'rgba(192,132,252,0.35)',
      '--console-chip-red-edge': 'rgba(239,68,68,0.4)',
      '--console-chip-neutral-fill': 'rgba(52,211,153,0.04)',
      /* Card title hue rotation (legacy oc-titles-css --t1..--t6), by card position. Each is AA large text on the
         card fill: 8.44, 8.97, 6.14, 7.55, 6.03, 9.71:1. */
      '--console-title-1': 'var(--rv-accent)',
      '--console-title-2': 'var(--rv-blue)',
      '--console-title-3': 'var(--rv-purple)',
      '--console-title-4': 'var(--rv-amber)',
      '--console-title-5': 'var(--rv-rose)',
      '--console-title-6': 'var(--rv-gold)',
      /* Timeline (legacy .tlstep rail and .ldact feed): the rail is the literal emerald gradient in both themes. */
      '--console-tl-rail': 'linear-gradient(180deg,rgba(52,211,153,0.35),rgba(52,211,153,0.06))',
      /* Timeline history (legacy Users drawer v2 #ocbDrawer .dv-ev): the drawer's own --dv-* palette. Icon ink on its
         tint over the drawer fill (#10241A): accent 6.33, blue 6.68, purple 4.97, amber 5.93, red 5.23:1. */
      '--console-tl-hist-text': '#E2E8F0',
      '--console-tl-hist-title': '#F1F5F9',
      '--console-tl-hist-sec': '#A3B1C4',
      '--console-tl-hist-muted': '#8494AB',
      '--console-tl-hist-border': 'rgba(52,211,153,.14)',
      '--console-tl-accent-ink': '#34D399',
      '--console-tl-accent-fill': 'rgba(52,211,153,.14)',
      '--console-tl-accent-edge': 'rgba(52,211,153,.38)',
      '--console-tl-blue-ink': '#22D3EE',
      '--console-tl-blue-fill': 'rgba(34,211,238,.14)',
      '--console-tl-blue-edge': 'rgba(34,211,238,.38)',
      '--console-tl-purple-ink': '#C084FC',
      '--console-tl-purple-fill': 'rgba(192,132,252,.14)',
      '--console-tl-purple-edge': 'rgba(192,132,252,.38)',
      '--console-tl-amber-ink': '#F59E0B',
      '--console-tl-amber-fill': 'rgba(245,158,11,.14)',
      '--console-tl-amber-edge': 'rgba(245,158,11,.38)',
      '--console-tl-red-ink': '#F87171',
      '--console-tl-red-fill': 'rgba(239,68,68,.14)',
      '--console-tl-red-edge': 'rgba(239,68,68,.38)',
      /* Teal, the email channel hue (item 3). Tile bar, value glow, chip edge and timeline, mirroring blue. Dark
         teal #2DD4BF is 9.2:1 on the dark card. Chip ink and fill fall back to the base teal and tealSoft. */
      '--console-tile-bar-teal': 'linear-gradient(180deg,var(--rv-teal) 10%,rgba(45,212,191,0.04))',
      '--console-tile-value-shadow-teal': '0 0 24px rgba(45,212,191,0.3)',
      '--console-chip-teal-edge': 'rgba(45,212,191,0.35)',
      '--console-tl-teal-ink': '#2DD4BF',
      '--console-tl-teal-fill': 'rgba(45,212,191,.14)',
      '--console-tl-teal-edge': 'rgba(45,212,191,.38)',
      /* Console avatar (legacy .avatar): accent gradient rounded square, bg-colored display initials. Presence rings
         (legacy .p-today/.p-week/.p-dormant) replace the depth shadow. */
      '--console-avatar-fill': 'linear-gradient(150deg,var(--rv-accent),rgba(52,211,153,0.65))',
      '--console-avatar-ink': 'var(--rv-bg)',
      '--console-avatar-shadow': '0 0 0 1px rgba(52,211,153,0.35),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-today': '0 0 0 2px var(--rv-accent),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-week': '0 0 0 2px rgba(52,211,153,0.35),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-dormant': '0 0 0 2px rgba(107,114,128,0.45),0 2px 8px rgba(0,0,0,.3)',
      /* Bar (legacy .fbar track and .fbar .fill), read by ProgressBar variant="console": a faint emerald well with an
         inset shade, and the accent fill fading to 35% with a soft glow. Decorative, aria-hidden or labelled by the
         progressbar role, so no contrast floor applies. */
      '--console-bar-track': 'rgba(52,211,153,0.05)',
      '--console-bar-inset': 'inset 0 1px 3px rgba(0,0,0,0.3)',
      '--console-bar-fill-end': 'rgba(52,211,153,0.35)',
      '--console-bar-glow': '0 0 14px rgba(52,211,153,0.2)',
      /* Overlay roles, read by the core Popover, ConfirmDialog and Modal look="console" (side drawer). Popover = legacy
         #mpop, dialog = legacy .mback/.modal and .sendwarn, drawer = the Users drawer v2 #ocbDrawer (dv-* vars). */
      '--console-pop-fill': 'var(--glassDeep)',
      /* Dashboard Customize widget control (legacy .wctrls button), read by IconButton variant="ctrl". */
      '--console-ctrl-fill': 'var(--glassDeep)',
      /* Exception count badge (legacy .xbadge), read by Pill shape="exception". */
      '--console-exception-size': '9px',
      /* Sidebar nav count (legacy .navbadge), read by Pill shape="exception" nav. */
      '--console-navbadge-size': '10px',
      '--console-exception-amber-edge': 'rgba(245,158,11,0.4)',
      '--console-exception-blue-edge': 'rgba(34,211,238,0.4)',
      /* Dashboard edit mode chrome (legacy .editing .grid, .editbar, .gridghost): the legacy emerald in both themes. */
      '--console-edit-line': 'rgba(52,211,153,0.05)',
      '--console-edit-outline': 'rgba(52,211,153,0.22)',
      '--console-edit-bar-edge': 'rgba(52,211,153,0.3)',
      '--console-edit-bar-from': 'rgba(52,211,153,0.14)',
      '--console-edit-bar-to': 'rgba(52,211,153,0.04)',
      '--console-edit-ghost': 'rgba(52,211,153,0.10)',
      '--console-exception-weight': '400',
      /* Dashboard tab row (legacy .dtab), read by Tabs variant="consoleDash". */
      '--console-dtab-size': '10.5px',
      '--console-dtab-size-sm': '10px',
      '--console-dtab-weight': '400',
      '--console-dtab-track': '1px',
      '--console-dtab-ink': 'var(--rv-label-sec)',
      '--console-dtab-on-fill': 'linear-gradient(180deg,rgba(52,211,153,0.14),rgba(52,211,153,0.05))',
      '--console-dtab-on-edge': 'rgba(52,211,153,0.3)',
      '--console-dtab-on-shadow': '0 0 18px rgba(52,211,153,0.10),inset 0 1px 0 rgba(52,211,153,0.18)',
      '--console-pop-edge': 'rgba(52,211,153,0.4)',
      '--console-pop-shadow': '0 18px 50px rgba(0,0,0,.55)',
      '--console-pop-title-size': '10.5px',
      '--console-pop-title-weight': '400',
      '--console-pop-title-tracking': '1.2px',
      '--console-pop-meta-size': '11px',
      '--console-dialog-fill': 'linear-gradient(170deg,rgba(52,211,153,0.07),rgba(52,211,153,0.02) 55%),var(--glassDeep)',
      '--console-dialog-edge': 'rgba(52,211,153,0.3)',
      '--console-dialog-shadow': '0 34px 90px rgba(0,0,0,.6),var(--edge)',
      '--console-dialog-scrim': 'rgba(4,10,6,.6)',
      '--console-dialog-rule': 'linear-gradient(90deg,rgba(52,211,153,0.35),rgba(52,211,153,0.03) 75%)',
      /* The owner .modal title and body, as the ConfirmDialog fallbacks; products.admin overrides them. */
      '--console-dialog-title-size': '13.5px',
      '--console-dialog-title-weight': '800',
      '--console-dialog-title-tracking': '1.6px',
      '--console-dialog-title-leading': '1.3',
      '--console-dialog-title-pad-right': '0px',
      '--console-dialog-body-size': '14px',
      '--console-dialog-body-ink': 'var(--ink-body, var(--rv-label-sec))',
      '--console-warn-edge': 'rgba(239,68,68,.5)',
      '--console-drawer-page':
        'radial-gradient(58% 40% at 50% 18%,rgba(52,211,153,.16) 0%,rgba(52,211,153,.04) 46%,rgba(13,27,17,0) 72%),radial-gradient(60% 40% at 90% 4%,rgba(245,158,11,.06) 0%,rgba(13,27,17,0) 60%),linear-gradient(160deg,#0C1E15 0%,#091210 55%,#0D1B11 100%)',
      '--console-drawer-head': 'rgba(10,26,16,.55)',
      '--console-drawer-edge': 'rgba(52,211,153,.14)',
      '--console-drawer-shadow': '-24px 0 60px rgba(0,0,0,.45)',
      '--console-drawer-scrim': 'rgba(4,10,6,.5)',
      '--console-drawer-stripe': 'linear-gradient(90deg,#34D399 0%,#22D3EE 35%,#C084FC 70%,#F59E0B 100%)',
      /* Drawer ink 15.41:1 on the page; tab ink 5.72:1 and active tab 9.19:1 on the header. */
      '--console-drawer-ink': '#E2E8F0',
      '--console-drawer-tab-ink': '#8494AB',
      '--console-drawer-tab-on': '#34D399',
      '--console-drawer-tab-bar': '#34D399',
      /* Field (legacy .finput and select.finput), read by TextField, Select variant="console". Text is T.label, 16.85:1 on
         the field fill. Legacy dark sets no placeholder color (browser default); the role takes the base tertiary
         ink, 9.10:1. Legacy dark marks focus by the edge only; the outline is the library focus ring. */
      '--console-field-fill': 'rgba(4,12,7,0.5)',
      '--console-field-edge': 'var(--rv-separator)',
      '--console-field-focus-edge': 'var(--rv-accent-tint)',
      '--console-field-focus-ring': 'none',
      '--console-field-focus-outline': 'var(--rv-accent)',
      '--console-field-placeholder': 'var(--rv-label-ter)',
      /* Inline field (legacy Enterprise view IS style: --card fill, --line edge), read by TextField and Select
         variant="console-inline". Dark --card is --surface1, the base surface glass; text 16.85:1 over it. */
      '--console-inline-field-fill': 'var(--rv-surface)',
      /* Table (legacy th, td and tbody row rules), read by Table variant="console". The header ink is --ink-meta
         (6.94:1 on the card glass), cell text T.label; stripe, hover and the rules are fills with no text floor. Dark
         th has no fill. */
      '--console-th-fill': 'transparent',
      '--console-th-edge': '1px solid rgba(52,211,153,0.22)',
      '--console-th-pad': '8px 10px',
      '--console-th-size': '10.5px',
      '--console-td-pad': '10px',
      '--console-td-edge': 'rgba(52,211,153,0.06)',
      '--console-row-stripe': 'rgba(52,211,153,0.024)',
      '--console-row-hover': 'rgba(52,211,153,0.07)',
      '--console-row-hover-bar': 'inset 2px 0 0 var(--rv-accent)',
      /* Users drawer v2 inner palette (legacy oc-xray-v2-css, the --dv-* custom properties on #ocbDrawer), read by
         Button and IconButton variant="drawer" and by the drawer compositions in the owner app. Per hue: the fill
         (dots, bars, the primary button), the ink (colored text), the tint (soft fill) and the 38% edge. Dark inks are
         the fills. Text contrast, measured on a drawer card (surface over the #10241A solid): em 7.78, cy 8.28,
         pu 5.66, am 6.96, red 5.41:1; on their own tint: 5.78, 6.10, 4.57, 5.45, 4.85:1. title 13.65, sec 6.87,
         am-strong 8.96:1. dim is the disabled ink only (2.75:1, exempt). The primary button ink #06281C on the em fill
         is 8.23:1. */
      '--console-dv-em': '#34D399',
      '--console-dv-cy': '#22D3EE',
      '--console-dv-pu': '#C084FC',
      '--console-dv-am': '#F59E0B',
      '--console-dv-red': '#F87171',
      '--console-dv-em-i': '#34D399',
      '--console-dv-cy-i': '#22D3EE',
      '--console-dv-pu-i': '#C084FC',
      '--console-dv-am-i': '#F59E0B',
      '--console-dv-red-i': '#F87171',
      '--console-dv-am-strong': '#FBBF24',
      '--console-dv-em-t': 'rgba(52,211,153,.14)',
      '--console-dv-cy-t': 'rgba(34,211,238,.14)',
      '--console-dv-pu-t': 'rgba(192,132,252,.14)',
      '--console-dv-am-t': 'rgba(245,158,11,.14)',
      '--console-dv-red-t': 'rgba(239,68,68,.14)',
      '--console-dv-em-b': 'rgba(52,211,153,.38)',
      '--console-dv-cy-b': 'rgba(34,211,238,.38)',
      '--console-dv-pu-b': 'rgba(192,132,252,.38)',
      '--console-dv-am-b': 'rgba(245,158,11,.38)',
      '--console-dv-red-b': 'rgba(239,68,68,.38)',
      '--console-dv-am-t2': 'rgba(245,158,11,.063)',
      '--console-dv-acc-t2': 'rgba(52,211,153,.084)',
      '--console-dv-surface': 'rgba(52,211,153,.045)',
      '--console-dv-solid': '#10241A',
      '--console-dv-border': 'rgba(52,211,153,.14)',
      '--console-dv-border-s': 'rgba(52,211,153,.22)',
      '--console-dv-shadow': '0 1px 0 rgba(255,255,255,.03) inset',
      '--console-dv-title': '#F1F5F9',
      '--console-dv-sec': '#A3B1C4',
      '--console-dv-dim': '#5B6B80',
      '--console-dv-btn': 'rgba(10,26,16,.55)',
      '--console-dv-btn-h': 'rgba(52,211,153,.08)',
      '--console-dv-input': 'rgba(10,26,16,.6)',
      '--console-dv-track': 'rgba(52,211,153,.08)',
      /* The primary drawer button ink and its press shadow (legacy .dv-btn.pri, the same in both themes). */
      '--console-dv-pri-ink': '#06281C',
      '--console-dv-pri-shadow': '0 1px 0 rgba(255,255,255,.35) inset,0 4px 14px rgba(52,211,153,.14)',
      /* Brief reader body (BriefReader in apps/owner-console/app/src/tabs/review/reader.tsx), read by the shared brief body
         through consoleRole('brief-...'). These are the owner values as they stand today, so the owner reader is a visual
         no-op when it reads them; products.admin overrides them with the legacy admin reader (renderBriefContent in
         apps/admin-console/index.html). Section heading: 14px at 1.6px tracking. Fact key: 9px at 1px (its weight is the
         optional role brief-k-weight, unset here, so the key inherits as it does today). Signal bars: 8px gap, no top margin,
         the track is the accent soft fill, the bar fades to the blue soft fill, no inset. Training frame: the accent soft
         fill as edge and as gradient start. Quote text inside a card: no top margin. Training heading: the section heading
         face (display 14px, caps, 1.6px tracking, 9px above the text). */
      '--console-brief-h-size': '14px',
      '--console-brief-h-track': '1.6px',
      '--console-brief-k-size': '9px',
      '--console-brief-k-track': '1px',
      '--console-brief-sig-gap': '8px',
      '--console-brief-sig-top': '0px',
      '--console-brief-sig-track': 'var(--rv-accent-soft)',
      '--console-brief-sig-tail': 'var(--rv-blue-soft)',
      '--console-brief-sig-inset': 'none',
      '--console-brief-train-edge': 'var(--rv-accent-soft)',
      '--console-brief-train-fill': 'linear-gradient(135deg,var(--rv-accent-soft),transparent 70%)',
      '--console-brief-quote-top': '0px',
      '--console-brief-train-h-font': 'var(--rv-font-display)',
      '--console-brief-train-h-size': '14px',
      '--console-brief-train-h-track': '1.6px',
      '--console-brief-train-h-case': 'uppercase',
      '--console-brief-train-h-gap': '9px',
    },
    light: {
      ...consolesStudio,
      '--surface3': 'rgba(16,185,129,0.12)',
      /* Legacy .tdbtn hover fill (Button variant="action"): --surface3. */
      '--console-act-hover': 'var(--surface3)',
      '--console-fill-3': 'var(--surface3)',
      '--glassDim': 'rgba(255,255,255,0.78)',
      '--glassDeep': 'rgba(255,255,255,0.97)',
      '--rose': '#9F1239',
      '--roseDim': 'rgba(244,63,94,0.12)',
      '--goldDim': 'rgba(234,179,8,0.16)',
      /* Light-only: the bright golf emerald for active nav bars and badges, and the deep ink on them. Dark leaves
         these unset, as the legacy page did. */
      '--brand': '#10B981',
      '--brandHi': '#34D399',
      '--ink': '#052E1C',
      '--edge': 'inset 0 1px 0 rgba(255,255,255,0.9)',
      '--lift': '0 12px 28px rgba(26,26,46,0.10),0 3px 8px rgba(26,26,46,0.06)',
      '--console-panel-shadow': '0 12px 28px rgba(26,26,46,0.10),0 3px 8px rgba(26,26,46,0.06)',
      '--ink-title': '#1A1A2E',
      '--ink-heading': '#1A1A2E',
      '--ink-body': '#2A2F42',
      '--ink-sub': '#3A4052',
      '--ink-meta': '#4B5363',
      '--ink-caption': '#586070',
      '--ink-num': '#1A1A2E',
      '--ink-quote-rule': 'rgba(5,150,105,0.45)',
      '--ng-overview': '#036B4D',
      '--ng-pipeline': '#155E75',
      '--ng-revenue': '#C2410C',
      '--ng-people': '#6D28D9',
      '--ng-growth': '#9F1239',
      '--ng-products': '#854D0E',
      '--ng-admin': '#33374B',
      ...consolesChrome.light,
      ...consolesTypeLight,
      /* Console widget roles, light: white cards, navy hairline, flat shadows, larger and bolder numbers. */
      '--console-card-fill': '#FFFFFF',
      '--console-card-border': 'rgba(26,26,46,0.12)',
      '--console-card-shadow': '0 1px 2px rgba(26,26,46,0.05),0 2px 8px rgba(26,26,46,0.04)',
      '--console-card-pad': '20px 22px',
      '--console-tile-fill': 'linear-gradient(170deg,rgba(16,185,129,0.06),#FFFFFF 55%)',
      '--console-tile-shadow': '0 1px 2px rgba(26,26,46,0.05),0 4px 12px rgba(26,26,46,0.05)',
      '--console-tile-shadow-hover': '0 2px 4px rgba(26,26,46,0.06),0 14px 30px rgba(26,26,46,0.10)',
      '--console-tile-border-hover': 'rgba(5,150,105,0.45)',
      '--console-tile-pad': '18px 20px 16px',
      /* Legacy owner light .tiles gap (admin light stays 14px, set in products.admin). */
      '--console-tile-grid-gap': '16px',
      /* Light paints every tile bar in the brand emerald, whatever the hue (legacy light .tile .accentbar).
         Decorative and aria-hidden: 2.54:1 on white, no contrast floor applies. */
      '--console-tile-bar': 'linear-gradient(180deg,#10B981 10%,rgba(16,185,129,0.10))',
      '--console-tile-bar-width': '4px',
      '--console-tile-label-size': '11.5px',
      '--console-caret-size': '11.5px',
      '--console-tile-value-size': '36px',
      /* Light keeps 36px on phones: the later legacy light .tile .v rule beats the 560px rule. */
      '--console-tile-value-size-sm': '36px',
      '--console-tile-value-weight': '700',
      '--console-tile-value-tracking': '-0.5px',
      '--console-tile-sub-size': '12px',
      '--console-tile-sub-weight': '500',
      /* Light is flat: no value glow, no title bar glow (legacy light text-shadow / box-shadow none). */
      '--console-tile-value-shadow': 'none',
      '--console-title-bar-shadow': 'none',
      '--console-title-size': '15px',
      '--console-aux-size': '12.5px',
      '--console-info-size': '12px',
      /* Light chips are 12px semibold at every size (legacy light .chip and its small-font floor). */
      '--console-chip-size': '12px',
      '--console-chip-size-sm': '12px',
      '--console-chip-weight': '600',
      /* Legacy light .onavb: 12px, the pastel red pair with a red hairline; current item keeps the hairline on the pastel
         emerald with the deep ink. */
      '--console-count-size': '12px',
      '--console-count-ink': '#991B1B',
      '--console-count-fill': '#FEE2E2',
      '--console-count-border': '1px solid rgba(185,28,28,0.25)',
      '--console-count-on-ink': 'var(--ink)',
      '--console-count-on-fill': '#D1FAE5',
      /* Light chips use their own opaque pastel pairs (legacy light .chip.ok/.warn/.cy/.pu/.red). Ink on fill: 13.10,
         8.42, 8.14, 7.57, 6.80:1. */
      '--console-chip-accent-ink': '#052E1C',
      '--console-chip-accent-fill': '#D1FAE5',
      '--console-chip-accent-edge': 'rgba(5,150,105,0.40)',
      '--console-chip-amber-ink': '#7C2D12',
      '--console-chip-amber-fill': '#FEF3C7',
      '--console-chip-amber-edge': 'rgba(180,83,9,0.35)',
      '--console-chip-blue-ink': '#164E63',
      '--console-chip-blue-fill': '#CFFAFE',
      '--console-chip-blue-edge': 'rgba(14,116,144,0.35)',
      '--console-chip-purple-ink': '#5B21B6',
      '--console-chip-purple-fill': '#EDE9FE',
      '--console-chip-purple-edge': 'rgba(109,40,217,0.30)',
      '--console-chip-red-ink': '#991B1B',
      '--console-chip-red-fill': '#FEE2E2',
      '--console-chip-red-edge': 'rgba(185,28,28,0.32)',
      /* Neutral chip: 11.40:1 secondary ink on the faint emerald glass. */
      '--console-chip-neutral-fill': 'rgba(52,211,153,0.04)',
      '--console-chip-neutral-edge': 'rgba(26,26,46,0.16)',
      /* Six hues that stay apart on white (legacy light --t1..--t6): 5.48, 6.70, 7.10, 5.18, 6.29, 5.47:1 on the
         white card, all >= 4.74:1 on the canvas. */
      '--console-title-1': '#047857',
      '--console-title-2': '#1D4ED8',
      '--console-title-3': '#6D28D9',
      '--console-title-4': '#C2410C',
      '--console-title-5': '#BE123C',
      '--console-title-6': '#0F766E',
      '--console-tl-rail': 'linear-gradient(180deg,rgba(52,211,153,0.35),rgba(52,211,153,0.06))',
      /* Light timeline text floors (legacy light .tlstep .rep2/.td, .ldact .day and .ev .t) and the deeper core dot. */
      '--console-tl-dot-core': '#059669',
      '--console-tl-rail-meta-size': '12px',
      '--console-tl-rail-time-size': '12px',
      '--console-tl-time-size': '12px',
      '--console-tl-day-size': '11.5px',
      '--console-tl-day-weight': '700',
      '--console-tl-day-tracking': '1px',
      /* Light drawer palette (legacy light #ocbDrawer). Icon ink on tint over white: accent 4.98, blue 5.81, purple 6.00,
         amber 5.02, red 5.68:1. */
      '--console-tl-hist-text': '#1A1A2E',
      '--console-tl-hist-title': '#111827',
      '--console-tl-hist-sec': '#475569',
      '--console-tl-hist-muted': '#64748B',
      '--console-tl-hist-border': 'rgba(15,23,42,.08)',
      '--console-tl-accent-ink': '#0F7A55',
      '--console-tl-accent-fill': 'rgba(52,211,153,.10)',
      '--console-tl-accent-edge': 'rgba(52,211,153,.42)',
      '--console-tl-blue-ink': '#0E6A7D',
      '--console-tl-blue-fill': 'rgba(34,211,238,.10)',
      '--console-tl-blue-edge': 'rgba(34,211,238,.42)',
      '--console-tl-purple-ink': '#7E3BB8',
      '--console-tl-purple-fill': 'rgba(192,132,252,.10)',
      '--console-tl-purple-edge': 'rgba(192,132,252,.42)',
      '--console-tl-amber-ink': '#9A5B06',
      '--console-tl-amber-fill': 'rgba(245,158,11,.10)',
      '--console-tl-amber-edge': 'rgba(245,158,11,.42)',
      '--console-tl-red-ink': '#B91C1C',
      '--console-tl-red-fill': 'rgba(239,68,68,.10)',
      '--console-tl-red-edge': 'rgba(239,68,68,.42)',
      /* Teal (item 3), the email channel hue, mirroring the light blue chip and timeline. Ink #0C5F59 is 6.3:1 on
         the teal chip fill #D5F0EB. */
      '--console-chip-teal-ink': '#0C5F59',
      '--console-chip-teal-fill': '#D5F0EB',
      '--console-chip-teal-edge': 'rgba(15,118,110,0.35)',
      '--console-tl-teal-ink': '#0C5F59',
      '--console-tl-teal-fill': 'rgba(45,212,191,.10)',
      '--console-tl-teal-edge': 'rgba(45,212,191,.42)',
      /* Light avatar: deep emerald gradient, white 11.5px initials. The light .avatar shadow outranks the presence
         rules in legacy (higher specificity), so light shows no presence ring; kept as shipped. */
      '--console-avatar-fill': 'linear-gradient(150deg,#059669,#047857)',
      '--console-avatar-ink': '#FFFFFF',
      '--console-avatar-shadow': '0 0 0 1px rgba(5,150,105,0.35),0 1px 3px rgba(26,26,46,0.15)',
      '--console-avatar-ring-today': '0 0 0 1px rgba(5,150,105,0.35),0 1px 3px rgba(26,26,46,0.15)',
      '--console-avatar-ring-week': '0 0 0 1px rgba(5,150,105,0.35),0 1px 3px rgba(26,26,46,0.15)',
      '--console-avatar-ring-dormant': '0 0 0 1px rgba(5,150,105,0.35),0 1px 3px rgba(26,26,46,0.15)',
      '--console-avatar-size': '11.5px',
      '--console-avatar-size-sm': '11.5px',
      /* Light keeps the emerald well, fade and glow; only the inset shade turns navy (legacy light .fbar). */
      '--console-bar-track': 'rgba(52,211,153,0.05)',
      '--console-bar-inset': 'inset 0 1px 2px rgba(26,26,46,0.08)',
      '--console-bar-fill-end': 'rgba(52,211,153,0.35)',
      '--console-bar-glow': '0 0 14px rgba(52,211,153,0.2)',
      /* Overlay roles, light: white glass popover and dialog, navy scrims, light drawer v2. */
      '--console-pop-fill': 'var(--glassDeep)',
      /* Dashboard Customize widget control (legacy .wctrls button), read by IconButton variant="ctrl". */
      '--console-ctrl-fill': 'var(--glassDeep)',
      /* Exception count badge, light (legacy :root[data-theme=light] :is(.xbadge,.navbadge)). */
      '--console-exception-size': '11px',
      '--console-navbadge-size': '11px',
      '--console-exception-amber-edge': 'rgba(245,158,11,0.4)',
      '--console-exception-blue-edge': 'rgba(34,211,238,0.4)',
      /* Dashboard edit mode chrome (legacy .editing .grid, .editbar, .gridghost): the legacy emerald in both themes. */
      '--console-edit-line': 'rgba(52,211,153,0.05)',
      '--console-edit-outline': 'rgba(52,211,153,0.22)',
      '--console-edit-bar-edge': 'rgba(52,211,153,0.3)',
      '--console-edit-bar-from': 'rgba(52,211,153,0.14)',
      '--console-edit-bar-to': 'rgba(52,211,153,0.04)',
      '--console-edit-ghost': 'rgba(16,185,129,0.10)',
      '--console-exception-weight': '600',
      /* Dashboard tab row, light (legacy :root[data-theme=light] .dtab). */
      '--console-dtab-size': '11.5px',
      '--console-dtab-size-sm': '11.5px',
      '--console-dtab-weight': '700',
      '--console-dtab-track': '0.8px',
      '--console-dtab-ink': 'var(--rv-label-ter)',
      '--console-dtab-on-fill': '#FFFFFF',
      '--console-dtab-on-edge': 'rgba(4,120,87,0.35)',
      '--console-dtab-on-shadow': '0 1px 2px rgba(15,23,42,0.06),0 4px 14px rgba(15,23,42,0.05)',
      '--console-pop-edge': 'rgba(52,211,153,0.4)',
      '--console-pop-shadow': '0 18px 50px rgba(0,0,0,.55)',
      '--console-pop-title-size': '11.5px',
      '--console-pop-title-weight': '700',
      '--console-pop-title-tracking': '0.8px',
      '--console-pop-meta-size': '12px',
      '--console-dialog-fill': 'var(--glassDeep)',
      '--console-dialog-edge': 'rgba(52,211,153,0.3)',
      '--console-dialog-shadow': '0 34px 90px rgba(0,0,0,.6),var(--edge)',
      '--console-dialog-scrim': 'rgba(26,26,46,.35)',
      '--console-dialog-rule': 'linear-gradient(90deg,rgba(52,211,153,0.35),rgba(52,211,153,0.03) 75%)',
      '--console-warn-edge': 'rgba(239,68,68,.5)',
      '--console-drawer-page': '#F4F6F8',
      '--console-drawer-head': 'linear-gradient(180deg,#FFFFFF 0%,#FBFCFD 100%)',
      '--console-drawer-edge': 'rgba(15,23,42,.08)',
      '--console-drawer-shadow': '-18px 0 44px rgba(26,26,46,.14)',
      '--console-drawer-scrim': 'rgba(26,26,46,.28)',
      '--console-drawer-stripe': 'linear-gradient(90deg,#34D399 0%,#22D3EE 35%,#C084FC 70%,#F59E0B 100%)',
      /* Drawer ink 15.75:1 on the page; tab ink 4.76:1 and active tab 5.34:1 on the white header. */
      '--console-drawer-ink': '#1A1A2E',
      '--console-drawer-tab-ink': '#64748B',
      '--console-drawer-tab-on': '#0F7A55',
      '--console-drawer-tab-bar': '#34D399',
      /* Field, light: white fill, navy edge, emerald focus edge, outline and 5px halo (legacy light .finput and the
         light :focus-visible rule). Text 17.06:1 and placeholder #5E6676 5.77:1 on white. */
      '--console-field-fill': '#FFFFFF',
      '--console-field-edge': 'rgba(26,26,46,0.20)',
      '--console-field-focus-edge': '#059669',
      '--console-field-focus-ring': '0 0 0 5px rgba(16,185,129,0.22)',
      '--console-field-focus-outline': '#047857',
      '--console-field-placeholder': 'var(--rv-label-quad)',
      /* Inline field, light: legacy --card is white. Text 17.06:1 on white. */
      '--console-inline-field-fill': '#FFFFFF',
      /* Table, light: a faint navy header fill and a 1.5px rule, roomier cells, a 3px brand edge on hover. Header ink
         --ink-meta 7.29:1 on its fill. */
      '--console-th-fill': 'rgba(26,26,46,0.03)',
      '--console-th-edge': '1.5px solid rgba(26,26,46,0.18)',
      '--console-th-pad': '10px 12px',
      '--console-th-size': '11.5px',
      '--console-td-pad': '11px 12px',
      '--console-td-edge': 'rgba(26,26,46,0.07)',
      '--console-row-stripe': 'rgba(26,26,46,0.022)',
      '--console-row-hover': 'rgba(16,185,129,0.07)',
      '--console-row-hover-bar': 'inset 3px 0 0 #10B981',
      /* Users drawer v2 palette, light: the fills keep the brand hues, colored text takes the darkened inks. Text
         contrast on the white card / the #F4F6F8 page / its own tint: em 5.34 / 4.93 / 4.98, cy 6.22 / 5.74 / 5.81,
         pu 6.55 / 6.05 / 6.00, am 5.42 / 5.00 / 5.02, red 6.47 / 5.97 / 5.68:1. title 17.74, sec 7.58, am-strong
         7.84:1 on white. dim is the disabled ink only (2.56:1, exempt). */
      '--console-dv-em': '#34D399',
      '--console-dv-cy': '#22D3EE',
      '--console-dv-pu': '#C084FC',
      '--console-dv-am': '#F59E0B',
      '--console-dv-red': '#F87171',
      '--console-dv-em-i': '#0F7A55',
      '--console-dv-cy-i': '#0E6A7D',
      '--console-dv-pu-i': '#7E3BB8',
      '--console-dv-am-i': '#9A5B06',
      '--console-dv-red-i': '#B91C1C',
      '--console-dv-am-strong': '#7A4500',
      '--console-dv-em-t': 'rgba(52,211,153,.10)',
      '--console-dv-cy-t': 'rgba(34,211,238,.10)',
      '--console-dv-pu-t': 'rgba(192,132,252,.10)',
      '--console-dv-am-t': 'rgba(245,158,11,.10)',
      '--console-dv-red-t': 'rgba(239,68,68,.10)',
      '--console-dv-em-b': 'rgba(52,211,153,.42)',
      '--console-dv-cy-b': 'rgba(34,211,238,.42)',
      '--console-dv-pu-b': 'rgba(192,132,252,.42)',
      '--console-dv-am-b': 'rgba(245,158,11,.42)',
      '--console-dv-red-b': 'rgba(239,68,68,.42)',
      '--console-dv-am-t2': 'rgba(245,158,11,.045)',
      '--console-dv-acc-t2': 'rgba(52,211,153,.06)',
      '--console-dv-surface': '#FFFFFF',
      '--console-dv-solid': '#FFFFFF',
      '--console-dv-border': 'rgba(15,23,42,.08)',
      '--console-dv-border-s': 'rgba(15,23,42,.14)',
      '--console-dv-shadow': '0 1px 2px rgba(15,23,42,.04),0 4px 16px rgba(15,23,42,.03)',
      '--console-dv-title': '#111827',
      '--console-dv-sec': '#475569',
      '--console-dv-dim': '#94A3B8',
      '--console-dv-btn': '#FFFFFF',
      '--console-dv-btn-h': '#F8FAFC',
      '--console-dv-input': '#FFFFFF',
      '--console-dv-track': 'rgba(15,23,42,.05)',
      /* Legacy light keeps the dark .dv-btn.pri look; the shadow takes the light em tint. */
      '--console-dv-pri-ink': '#06281C',
      '--console-dv-pri-shadow': '0 1px 0 rgba(255,255,255,.35) inset,0 4px 14px rgba(52,211,153,.10)',
      /* Brief reader body, light: the same owner values as dark (the owner reader draws no per-theme sizes); the colors follow
         the theme through the tokens they name. See the dark block above. */
      '--console-brief-h-size': '14px',
      '--console-brief-h-track': '1.6px',
      '--console-brief-k-size': '9px',
      '--console-brief-k-track': '1px',
      '--console-brief-sig-gap': '8px',
      '--console-brief-sig-top': '0px',
      '--console-brief-sig-track': 'var(--rv-accent-soft)',
      '--console-brief-sig-tail': 'var(--rv-blue-soft)',
      '--console-brief-sig-inset': 'none',
      '--console-brief-train-edge': 'var(--rv-accent-soft)',
      '--console-brief-train-fill': 'linear-gradient(135deg,var(--rv-accent-soft),transparent 70%)',
      '--console-brief-quote-top': '0px',
      '--console-brief-train-h-font': 'var(--rv-font-display)',
      '--console-brief-train-h-size': '14px',
      '--console-brief-train-h-track': '1.6px',
      '--console-brief-train-h-case': 'uppercase',
      '--console-brief-train-h-gap': '9px',
    },
  },
};

/*
 * Shop (shop.repvantage.io). Values are verbatim from the copied :root palette that apps/shop/src/styles.css carried
 * before it read the tokens, so the generated block (apps/shop/scripts/build-tokens.mjs) is a visual no-op. The shop
 * ships the dark theme only, by decision: the store is a dark brand surface, so products.shop has no light block and the
 * storefront has no theme toggle (plan decision D3, issue 3451, Luke, 2026-10-25). Base keys the shop already matches
 * (bg, bgElev, separator, label, labelSec, accent, accentSoft, accentTint, amber, amberSoft, ease) are not repeated here.
 */
const shop: ProductOverride = {
  dark: {
    /* Shop glass fills are fainter than base (3% / 6% vs 10% / 14%): card and button surfaces over the deep field. */
    surface: 'rgba(52,211,153,0.03)',
    surfaceAlt: 'rgba(52,211,153,0.06)',
    /* Shop hover edge is lighter than the base bold separator (25% vs 42%). */
    separatorBold: 'rgba(52,211,153,0.25)',
    /* Shop muted text is slate #64748B, 3.78:1 on bg and 4.07:1 on bgElev. Shipped value, kept for the no-op; raising
       it is a visual change and needs its own founder-visible PR. */
    labelTer: '#64748B',
    /* Ink on the amber CTA fills: 8.80:1 on #F59E0B. Shop ink is darker than base #241A00. */
    onGold: '#131107',
    /* Shop type stacks are the legacy --display / --body / --mono verbatim, so computed font-family matches the shipped
       page exactly (base stacks carry extra system fallbacks). */
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
    /* Shop card hover lift is deeper than the base card depth. */
    shadowCard: '0 18px 44px rgba(0,0,0,0.38)',
  },
  roles: {
    dark: {
      /* Third glass step (perk dot); base surface now carries the 3% step for the shop. */
      '--shop-surface-3': 'rgba(52,211,153,0.10)',
      /* Teal accent for badges and the hero chip; no base token carries #5EEAD4 (12.15:1 on bg). */
      '--shop-accent-bright': '#5EEAD4',
      /* Amber glow under the solid CTA on hover; no base amber shadow exists. */
      '--shop-shadow-amber': '0 10px 30px rgba(245,158,11,0.30)',
      /* Button variant="shop" and "shop-buy" (legacy .btn-solid, .btn-ghost, .buy.live), verbatim from styles.css. */
      /* Top stop of the amber CTA gradient (bottom stop is amber); onGold ink on it is 11.0:1. */
      '--shop-cta-top': '#FDBA3A',
      /* Inset top highlight on the solid CTA and the live buy button. */
      '--shop-cta-gloss': 'rgba(255,255,255,0.35)',
      '--shop-buy-gloss': 'rgba(255,255,255,0.30)',
      /* Idle amber drop shadow under the solid CTA. */
      '--shop-cta-shadow': '0 4px 14px rgba(245,158,11,0.22)',
      /* Hover sheen that sweeps across the solid CTA. */
      '--shop-cta-sheen': 'rgba(255,255,255,0.45)',
      /* Ghost CTA glass fill over the hero photo (bgElev at 40%). */
      '--shop-ghost-fill': 'rgba(6,16,8,0.4)',
      /* Amber glow under the live buy button on hover. */
      '--shop-buy-glow': '0 8px 24px rgba(245,158,11,0.34)',
      /* Swatch (legacy .swatch): inset shade on the color fill, hairline rim on light fills, check mark inks on dark and
         light fills. */
      '--shop-swatch-shade': 'rgba(0,0,0,0.25)',
      '--shop-swatch-rim': 'rgba(10,26,16,0.18)',
      '--shop-swatch-mark-dark': 'rgba(10,26,16,0.85)',
      '--shop-swatch-mark-light': 'rgba(226,232,240,0.9)',
      /* Back-to-top float (legacy .to-top): bgElev at 88% under an 8px blur. */
      '--shop-float-fill': 'rgba(6,16,8,0.88)',
      /* Thumbnail gradient stops read by ThumbButton. Other photo backdrops stay in the Shop local CSS. */
      '--shop-photo-card-hi': '#222222',
      '--shop-photo-thumb-lo': '#131313',
    },
  },
};

/*
 * Admin console (admin.repvantage.io, Teams). Dark is the owner palette: the admin page copied it (#1893), so dark is
 * products.consoles verbatim. Light is the admin v6 light theme, which the owner page never took: every text token
 * clears 4.5:1 on white and on the tinted bg, borders are slate ink, and the emerald hues use the deeper ink step so
 * text set in var(--accent) stays readable. Values are verbatim from the legacy apps/admin-console/index.html v6
 * block, so the inlined token block (scripts/build-console-tokens.mjs) is a visual no-op
 * (apps/admin-console/tools/token-parity.mjs). Light keys not listed here (purple, gold, the owner light roles) equal
 * the owner values.
 */
/* Legacy admin feed (.feed well, .dayhead, .fitem) values both themes share; Timeline variant="feed" reads them as
   optional roles. Admin day headings are accent ink and do not stick; rows are centered flex rows with a 27px icon box
   and no box border. */
const adminFeed: Record<string, string> = {
  '--console-tl-day-tracking': '1.6px',
  '--console-feed-day-ink': 'var(--rv-accent)',
  '--console-feed-day-pad': '10px 8px 5px',
  '--console-feed-day-position': 'static',
  '--console-feed-day-fill': 'transparent',
  '--console-feed-row-align': 'center',
  '--console-feed-row-pad': '9px 8px',
  '--console-feed-row-radius': '8px',
  '--console-feed-row-size': '1em',
  '--console-feed-row-hover': 'var(--rv-surface-alt)',
  '--console-feed-icon-col': '27px',
  '--console-feed-icon-box': '27px',
  '--console-feed-icon-radius': '8px',
  '--console-feed-icon-size': '12px',
  '--console-feed-icon-border': '0 none',
  '--console-feed-accent-fill': 'var(--rv-accent-soft)',
  '--console-feed-name-weight': '700',
  '--console-feed-time-ink': 'var(--rv-label-ter)',
};

/* Legacy admin .btn has no :disabled rule (apps/admin-console/index.html style block: .btn at line 1462, light .btn at line
   2953), so a disabled button draws exactly as an enabled one: full opacity and its own fill, ink, edge and shadow. The base
   light roles grey every disabled console button with one fill for all tones. Admin leaves those four roles unset, so each
   tone falls back to its own enabled paint, as dark already does. */
const {
  '--chrome-btn-disabled': _greyFill,
  '--chrome-btn-disabled-ink': _greyInk,
  '--chrome-btn-disabled-edge': _greyEdge,
  '--chrome-btn-disabled-shadow': _greyShadow,
  ...consolesLightNoGrey
} = consoles.roles?.light ?? {};

/* Legacy .finput keeps full wrapper opacity; select.finput keeps its pointer when disabled. */
const adminDisabledFields: Record<string, string> = {
  '--console-field-disabled-opacity': '1',
  '--console-field-disabled-cursor': 'default',
  '--console-select-disabled-cursor': 'pointer',
};

const ADMIN_KEYBOARD_ROW_FOCUS_RADIUS = '6px';

const admin: ProductOverride = {
  dark: consoles.dark,
  light: {
    ...consoles.light,
    /* Admin light canvas is a green-gray, and the glass fills are tints of the deep emerald #047857. */
    bg: '#F3F6F4',
    surface: 'rgba(4,120,87,0.045)',
    surfaceAlt: 'rgba(4,120,87,0.075)',
    /* Slate borders; hover edge is the deep emerald. */
    separator: 'rgba(15,23,42,0.14)',
    separatorBold: 'rgba(4,120,87,0.55)',
    /* Slate ink ladder, darker than owner navy: #556072 is the lightest text and clears 4.5:1 on the bg. */
    label: '#0B1220',
    labelSec: '#1E293B',
    labelTer: '#3F4B5C',
    labelQuad: '#556072',
    accent: '#046C4E',
    accentSoft: 'rgba(4,120,87,0.10)',
    accentTint: 'rgba(4,120,87,0.28)',
    amber: '#9A3412',
    amberSoft: 'rgba(217,119,6,0.12)',
    purpleSoft: 'rgba(109,40,217,0.10)',
    blue: '#0E6378',
    blueSoft: 'rgba(14,116,144,0.10)',
    redSoft: 'rgba(185,28,28,0.09)',
    rose: '#BE123C',
    roseSoft: 'rgba(190,18,60,0.09)',
  },
  roles: {
    dark: {
      ...consoles.roles?.dark,
      ...adminDisabledFields,
      /* Legacy admin .btn has no :disabled rule (see consolesLightNoGrey), so a disabled button keeps full opacity. An unset
         opacity role would fall back to 0.4, so this one carries the value. */
      '--chrome-btn-disabled-opacity': '1',
      /* Admin dark red chip ink is the admin --red (legacy .chip.red), lighter than the owner chip red. */
      '--console-chip-red-ink': '#FA8080',
      /* Admin widget title (legacy admin .wtitle): plain, no hue bar, secondary ink, a faint emerald rule. The owner
         page restyled its titles (hue bar, hue ink); admin never did. */
      '--console-title-ink': 'var(--rv-label-sec)',
      '--console-title-weight': '400',
      '--console-title-tracking': '1.4px',
      '--console-title-size': '11px',
      '--console-title-bar': 'none',
      '--console-title-rule': 'linear-gradient(90deg,rgba(52,211,153,0.30),rgba(52,211,153,0.03) 75%) 1',
      '--console-title-pad': '10px',
      '--console-title-gap': '13px',
      '--console-aux-size': '10.5px',
      /* Admin meta ink is its --textMuted. */
      '--ink-meta': '#A8B5C5',
      /* Legacy admin .modal h4 and .mbody (the owner .modal is 13.5px heavy, 1.6px tracking): a 12px bold title with the
         close gutter on its right, 13px secondary-ink copy. */
      '--console-dialog-title-size': '12px',
      '--console-dialog-title-weight': '700',
      '--console-dialog-title-tracking': '1.4px',
      '--console-dialog-title-leading': 'normal',
      '--console-dialog-title-pad-right': '42px',
      '--console-dialog-body-size': '13px',
      '--console-dialog-body-ink': 'var(--rv-label-sec)',
      /* Legacy admin dark .tile .k: 10px, regular weight, 1.1px tracking. */
      '--console-tile-label-size': '10px',
      '--console-tile-label-weight': '400',
      '--console-tile-label-tracking': '1.1px',
      /* Legacy admin .feed, .dayhead and .fitem, dark: a dark inset well, faint emerald hairlines, a dimmed heading. */
      ...adminFeed,
      '--console-feed-well-fill': 'rgba(4,12,7,0.3)',
      '--console-feed-well-edge': 'rgba(52,211,153,0.06)',
      '--console-feed-well-shadow': 'inset 0 2px 10px rgba(2,8,4,0.35)',
      '--console-feed-day-opacity': '.85',
      '--console-feed-row-edge': 'rgba(52,211,153,0.05)',
      /* Legacy admin Do today row rule (.attnrow border-bottom) and console toast (.toast): fixed rgba in both themes,
         the legacy light theme never re-tints them. */
      '--console-attnrow-edge': 'rgba(52,211,153,0.08)',
      '--console-toast-shadow': '0 14px 40px rgba(0,0,0,.5)',
      '--console-toast-edge-ok': 'rgba(52,211,153,0.45)',
      '--console-toast-edge-warn': 'rgba(245,158,11,0.45)',
      '--console-toast-edge-err': 'rgba(239,68,68,0.5)',
      /* Legacy admin .tourback scrim and .tourcard edge and shadow, fixed rgba in both themes. */
      '--console-tour-scrim': 'rgba(4,10,6,.72)',
      '--console-tour-edge': 'rgba(52,211,153,0.4)',
      '--console-tour-shadow': '0 34px 90px rgba(0,0,0,.6)',
      '--console-feed-icon-shadow': 'inset 0 1px 0 rgba(52,211,153,0.10)',
      '--console-feed-text-size': '12.5px',
      /* Legacy admin dark .stackbar inset: 2px blur, the owner bar well uses 3px. */
      '--console-stack-inset': 'inset 0 1px 2px rgba(0,0,0,0.3)',
      /* Legacy admin .stackbar (Outcomes mix bar): radius 6, flush segments that grow in over 0.7s. */
      '--console-stack-radius': '6px',
      '--console-stack-gap': '0px',
      '--console-stack-motion': '700ms',
      /* Admin Outcomes cards (legacy .wlpcard), an admin app composition: a fixed emerald fill and a fixed-rgba glow per
         result in both themes (legacy never re-tints them in light), so a mix of the live hue cannot match. */
      '--admin-outcome-fill': 'linear-gradient(165deg,rgba(52,211,153,0.07),rgba(52,211,153,0.02))',
      '--admin-outcome-glow-won': '0 0 16px rgba(52,211,153,0.35)',
      '--admin-outcome-glow-lost': '0 0 16px rgba(239,68,68,0.3)',
      '--admin-outcome-glow-pushed': '0 0 16px rgba(245,158,11,0.3)',
      '--admin-outcome-glow-rate': '0 0 16px rgba(34,211,238,0.3)',
      '--admin-outcome-label-size': '10px',
      '--console-outcome-fill': 'var(--admin-outcome-fill)',
      '--console-outcome-glow-accent': 'var(--admin-outcome-glow-won)',
      '--console-outcome-glow-red': 'var(--admin-outcome-glow-lost)',
      '--console-outcome-glow-amber': 'var(--admin-outcome-glow-pushed)',
      '--console-outcome-glow-blue': 'var(--admin-outcome-glow-rate)',
      '--console-outcome-label-size': 'var(--admin-outcome-label-size)',
      /* Legacy admin tables collapse their borders (border-collapse: collapse). */
      '--console-collapse': 'collapse',
      /* Legacy admin th: 9.5px at weight 600; th.num and td.num set the mono font. */
      '--console-th-size': '9.5px',
      '--console-th-weight': '600',
      '--console-num-font': 'var(--rv-mono)',
      /* Legacy admin .tablewrap: a dark inset well around every table. */
      '--console-well-fill': 'rgba(4,12,7,0.35)',
      '--console-well-edge': '1px solid rgba(52,211,153,0.07)',
      '--console-well-radius': '12px',
      '--console-well-shadow': 'inset 0 2px 10px rgba(2,8,4,0.4)',
      '--console-well-pad': '2px 10px 6px',
      /* Legacy admin td.empty: .empty type (mono 12.5px, 0.3px tracking, 26px 18px pad, muted ink) on the td rule, and
         the .empty::before radar mark (fixed emerald, the same in both themes). */
      '--console-empty-pad': '26px 18px',
      '--console-empty-ink': 'var(--rv-label-ter)',
      '--console-empty-size': '12.5px',
      '--console-empty-font': 'var(--rv-mono)',
      '--console-empty-tracking': '0.3px',
      '--console-empty-edge': '1px solid var(--console-td-edge)',
      '--console-empty-mark': 'block',
      '--console-empty-mark-edge': '1px solid rgba(52,211,153,0.25)',
      '--console-empty-mark-fill': 'radial-gradient(circle,rgba(52,211,153,0.35) 0 2px,transparent 3px),conic-gradient(from 0deg,rgba(52,211,153,0.30),transparent 100deg)',
      /* Legacy admin podium rows (tr.pod1 to pod3): literal hue gradients; pod2 and pod3 keep theirs on hover. */
      '--console-row-amber': 'linear-gradient(90deg,rgba(245,158,11,0.09),rgba(245,158,11,0.015) 70%)',
      '--console-row-amber-hover': 'linear-gradient(90deg,rgba(245,158,11,0.13),rgba(245,158,11,0.03) 70%)',
      '--console-row-amber-edge': 'rgba(245,158,11,0.18)',
      '--console-row-blue': 'linear-gradient(90deg,rgba(34,211,238,0.05),transparent 60%)',
      '--console-row-blue-hover': 'linear-gradient(90deg,rgba(34,211,238,0.05),transparent 60%)',
      '--console-row-purple': 'linear-gradient(90deg,rgba(192,132,252,0.05),transparent 60%)',
      '--console-row-purple-hover': 'linear-gradient(90deg,rgba(192,132,252,0.05),transparent 60%)',
      /* Admin dark .chip.warn and .chip.cy ink is the admin --amber and --cyan, lighter than the owner chip ink. */
      '--console-chip-amber-ink': 'var(--rv-amber)',
      '--console-chip-blue-ink': 'var(--rv-blue)',
      /* Legacy .badge.warn and .badge.ok edges are 30% of the hue; .chip.sel.on edge is 45% accent. */
      '--console-badge-amber-edge': 'rgba(245,158,11,0.3)',
      '--console-badge-accent-edge': 'rgba(52,211,153,0.3)',
      '--console-chip-on-edge': 'rgba(52,211,153,0.45)',
      /* Legacy admin .adv-seg[aria-pressed=true]: the soft drop shadow under the lifted segment (both themes). */
      /* Legacy admin dark .adv-seg:focus-visible offsets the ring 1px; light falls to the global 2px rule. */
      '--console-segment-focus-offset': '1px',
      '--console-segment-lift': '0 1px 2px rgba(0,0,0,.12)',
      /* Legacy admin .adv-pill.pu and .adv-ev .ic.pu edge: the fixed brand purple at 40%, not the lighter text purple. */
      '--console-plate-purple-edge': 'rgba(192,132,252,0.4)',
      /* Legacy admin trendViz email bar (.tcol .b.email): the live blue fades to the dark-theme blue at 26% in both
         themes. */
      '--console-chart-blue-fade': 'rgba(34,211,238,0.26)',
      /* Admin donut charts (legacy kinddonut and tierdonut), an admin app composition: the fifth tier slice is a fixed
         slate at 60% and the hover glow a fixed emerald at 35%, in both themes. The tier legend rows are inline 11px,
         which the light page bumps to 12.5px. */
      '--admin-donut-rest': 'rgba(148,163,184,0.6)',
      '--admin-donut-glow': 'rgba(52,211,153,0.35)',
      '--admin-donut-legend-size': '11px',
      /* Admin pace gauges (legacy .gauge in quotapace), an admin app composition: a fixed emerald card fill in both
         themes (legacy never re-tints it in light), so a mix of the live hue cannot match. */
      '--admin-gauge-fill': 'linear-gradient(170deg,rgba(52,211,153,0.05),rgba(52,211,153,0.015))',
      /* Legacy .nba.watch edge (Pill shape="action", blue): the cyan at 40%; the admin chip edge is 35%. */
      '--console-action-blue-edge': 'rgba(34,211,238,0.4)',
      /* Legacy admin .usetile (Stat look="use"): a fixed emerald fill (legacy never re-tints it in light) and the --edge
         shade. Legacy .pacebar .pb (ProgressBar look="pace"): a 2px inset, the owner bar well uses 3px. Legacy gauge()
         ring (ProgressRing variant="console"): a full-opacity --surface3 track, the center text at the inherited size
         (1em, as `inherit` in a custom property does not reach the var() read), regular weight and normal tracking.
         Dark .usetile type is the core fallback. */
      '--console-use-fill': 'linear-gradient(165deg,rgba(52,211,153,0.06),rgba(52,211,153,0.015))',
      '--console-use-shadow': 'var(--edge)',
      '--console-pace-inset': 'inset 0 1px 2px rgba(0,0,0,0.3)',
      /* Legacy admin .xpbar: a fixed emerald well and a fixed purple fade in both themes. */
      '--console-xp-track': 'rgba(52,211,153,0.06)',
      '--console-xp-fill-end': 'rgba(192,132,252,0.4)',
      '--console-ring-track': 'var(--surface3)',
      '--console-ring-track-opacity': '1',
      '--console-ring-center-size': '1em',
      '--console-ring-center-weight': '400',
      '--console-ring-center-tracking': 'normal',
      /* Legacy admin .fitem rows (ListRow variant="console"): a faint emerald hairline. */
      '--console-listrow-edge': 'rgba(52,211,153,0.05)',
      /* Legacy admin .kbrow:focus-visible, including interactive .fitem feed rows. */
      '--console-listrow-feed-focus-offset': '2px',
      '--console-listrow-feed-focus-radius': ADMIN_KEYBOARD_ROW_FOCUS_RADIUS,
      /* Legacy admin .attnrow (ListRow look="attn", the Provenance rows): the emerald hairline at 8%, in both themes. */
      '--console-attn-edge': 'rgba(52,211,153,0.08)',
      /* Legacy admin Rep Account intel panel (.intel) and its signal bars (.sigrow): fixed cyan rgba in both themes; only the
         panel shadow and the bar well differ in light. */
      '--console-intel-edge': 'rgba(34,211,238,0.28)',
      '--console-intel-fill': 'linear-gradient(165deg,rgba(34,211,238,0.06),rgba(34,211,238,0.015))',
      '--console-intel-shadow': 'inset 0 1px 0 rgba(34,211,238,0.12), 0 10px 30px rgba(2,10,5,.3)',
      '--console-intel-grid': 'rgba(34,211,238,0.03)',
      '--console-intel-bar-track': 'rgba(52,211,153,0.06)',
      '--console-intel-bar-inset': 'none',
      '--console-intel-bar-end': 'rgba(34,211,238,0.4)',
      /* Legacy admin inline row avatar (style="width:26px;...;font-size:10px" in .fitem rows, Avatar console size 26). */
      '--console-avatar-size-row': '10px',
      /* Legacy admin .infoi (title "i" and expand): a 24px hit box, the 17px ring inset 3.5px, negative margins, no gap
         after the title. The owner dot is a bare 17px ring. */
      '--console-info-box': '24px',
      '--console-info-inset': '3.5px',
      '--console-info-margin': '-3.5px -3.5px -3.5px 2.5px',
      '--console-info-row-gap': '0px',
      '--console-title-wrap': 'nowrap',
      '--console-info-row-wrap': 'wrap',
      /* Legacy admin .focusback and .focuscard (widget focus view): a darker scrim than .mback, a deep drop shadow. */
      '--console-focus-scrim': 'rgba(4,10,6,.7)',
      /* Legacy .focuscard paints the page bg (var(--bg)); the core fallback is the dialog surface. */
      '--console-focus-fill': 'var(--rv-bg)',
      '--console-focus-shadow': '0 40px 120px rgba(0,0,0,.6)',
      /* Admin Quota & pace widget (legacy teampace), an admin app composition. Legacy draws these in fixed dark-theme
         rgba in both themes: the bar wells (.pacebar .pb, the runway .qbar), the runway fill fade and cyan projection
         hatch, and the .usetile fill. Only the .pacebar .pb inset changes in light. */
      /* Admin Quota & pace runway (legacy teampace .qbar), an admin app composition. Legacy draws these in fixed
         dark-theme rgba in both themes: the runway well, inset, fill fade and cyan projection hatch. */
      '--admin-pace-track': 'rgba(52,211,153,0.05)',
      '--admin-runway-inset': 'inset 0 1px 2px rgba(0,0,0,.3)',
      '--admin-runway-fill-end': 'rgba(52,211,153,0.5)',
      '--admin-runway-proj': 'repeating-linear-gradient(-45deg,rgba(34,211,238,0.25) 0 6px,rgba(34,211,238,0.08) 6px 12px)',
      /* Admin Team composition (legacy teamcomp), an admin app composition: the fifth layer is a fixed emerald at 55% and
         Others a fixed slate at 50% in both themes; the day hover (svg.charea rect:hover) is the brand emerald at 7%. */
      '--admin-teamcomp-fifth': 'rgba(52,211,153,0.55)',
      '--admin-teamcomp-others': 'rgba(148,163,184,0.5)',
      '--admin-teamcomp-hover': 'rgba(52,211,153,0.07)',
      /* Admin Pending members rows (legacy .reqrow), an admin app composition: legacy draws the row hairline in a
         fixed emerald at 7% in both themes. */
      '--admin-reqrow-edge': 'rgba(52,211,153,0.07)',
      /* Admin Quota attainment spread (legacy quotadist .histo, svg.cumline), an admin app composition: legacy fades the
         bars to a fixed cyan at 30% and glows the cumulative dots in a fixed purple at 50%, in both themes. */
      '--admin-quotadist-bar-end': 'rgba(34,211,238,0.3)',
      '--admin-quotadist-dot-glow': 'drop-shadow(0 0 2px rgba(192,132,252,0.5))',
      /* Admin rank medals (legacy .rank.m2 and .rank.m3, leaderboard and topaccounts), an admin app composition: legacy
         draws the medal edge in the fixed cyan and purple at 45% in both themes, not the theme blue and purple. */
      '--admin-rank-edge-2': 'rgba(34,211,238,0.45)',
      '--admin-rank-edge-3': 'rgba(192,132,252,0.45)',
      /* Admin legacy brief reader (renderBriefContent in apps/admin-console/index.html), dark. Each role is one legacy rule.
         .brsec .brh: display 10.5px, tracking 1.3px. .sigbars: gap 6px, margin-top 9px. .sigrow .sb: fill
         rgba(52,211,153,.06). .sigrow .sb i: gradient from the cyan to rgba(34,211,238,.4). .brtrain: edge rgba(52,211,153,.3),
         fill linear-gradient(135deg,rgba(52,211,153,.07),transparent 70%). .brcard .cd: margin-top 4px, the quote card
         included. .brfact .k keeps the owner 9px and 1px, with no weight (it inherits). The training heading .brtrain .brh
         sits outside .brsec in the legacy markup, so the .brsec .brh rule never reaches it and only its accent color applies:
         it keeps the inherited face and size, with no caps, no tracking and no margin below. */
      '--console-brief-h-size': '10.5px',
      '--console-brief-h-track': '1.3px',
      '--console-brief-sig-gap': '6px',
      '--console-brief-sig-top': '9px',
      '--console-brief-sig-track': 'rgba(52,211,153,0.06)',
      '--console-brief-sig-tail': 'rgba(34,211,238,0.4)',
      '--console-brief-train-edge': 'rgba(52,211,153,0.3)',
      '--console-brief-train-fill': 'linear-gradient(135deg,rgba(52,211,153,0.07),transparent 70%)',
      '--console-brief-quote-top': '4px',
      '--console-brief-train-h-font': 'var(--rv-font)',
      '--console-brief-train-h-size': '1em',
      '--console-brief-train-h-track': 'normal',
      '--console-brief-train-h-case': 'none',
      '--console-brief-train-h-gap': '0px',
      /* Pill shape="outline" (legacy .brfid: edge 1px rgba(34,211,238,.4)) and shape="flat" (legacy .brthreat: .high fill
         rgba(245,158,11,.16), .medium fill rgba(34,211,238,.14); .low takes --surface3 through --console-fill-3). Fixed
         rgba values in both themes, not a mix of the live hue. Their dark sizes (9.5px and 10px) are the component fallbacks. */
      '--console-pill-outline-edge': 'rgba(34,211,238,0.4)',
      '--console-pill-flat-amber-fill': 'rgba(245,158,11,0.16)',
      '--console-pill-flat-blue-fill': 'rgba(34,211,238,0.14)',
      /* Legacy admin .modal .mbody: bold text in a confirm body keeps the body ink (--textSec), not the title ink. The
         corner close X ring is the page-wide button:focus-visible rule, the accent glow (--accentGlow) in dark. */
      '--console-confirm-strong-ink': 'var(--rv-label-sec)',
      '--console-button-focus-ring': 'var(--rv-accent-tint)',
      /* Legacy .kbrow:focus-visible rounds the draft card to 6px, only during keyboard focus. */
      '--console-brief-card-focus-radius': ADMIN_KEYBOARD_ROW_FOCUS_RADIUS,
    },
    light: {
      ...consolesLightNoGrey,
      ...adminDisabledFields,
      '--console-dialog-title-size': '12px',
      '--console-dialog-title-weight': '700',
      '--console-dialog-title-tracking': '1.4px',
      '--console-dialog-title-leading': '18px',
      '--ink-title': 'var(--rv-label)',
      '--console-dialog-title-pad-right': '42px',
      '--console-dialog-body-size': '13px',
      '--console-dialog-body-ink': 'var(--rv-label-sec)',
      '--console-tile-grid-gap': '14px',
      /* Admin light body text stays at weight 400; the owner light theme sets 450. */
      '--chrome-body-weight': '400',
      /* Legacy admin light body (v6): 14.5px with line-height 1.5; read by the console-shell body rule, unset for owner. */
      '--chrome-body-size': '14.5px',
      '--chrome-body-leading': '1.5',
      /* Legacy admin light .navitem (v6): 15px, weight 500; read by the console-shell nav rule, unset for owner. */
      '--chrome-nav-size': '15px',
      /* Legacy admin light meta lines (.rep .meta .em and the small mono lines): 12px, read through consoleMetaSize. */
      '--console-meta-size': '12px',
      /* Legacy admin light .btn and .btn.accent (v6): slate edges and shadows, a teal edge and deep ink on accent. */
      '--chrome-btn-edge': 'rgba(15,23,42,0.22)',
      /* Admin light .btn out-ranks .modal .btn.danger, so the danger tone draws the plain admin button edge. */
      '--chrome-btn-danger-edge': 'rgba(15,23,42,0.22)',
      '--chrome-btn-danger-hover-edge': 'rgba(4,120,87,0.55)',
      '--chrome-btn-shadow': '0 1px 2px rgba(15,23,42,0.06)',
      '--chrome-btn-hover': '#F2F8F5',
      '--chrome-btn-hover-edge': 'rgba(4,120,87,0.55)',
      '--chrome-btn-hover-shadow': '0 4px 12px rgba(15,23,42,0.10)',
      '--chrome-btn-accent-ink': '#06281C',
      '--chrome-btn-accent-edge': '#0E9F6E',
      /* Legacy admin light .btn.accent keeps the slate .btn shadow; the owner light accent shadow is emerald. */
      '--chrome-btn-accent-shadow': '0 1px 2px rgba(15,23,42,0.06)',
      /* Legacy admin light .btn.accent:hover (index.html): `:root[data-theme=light] .btn.accent` keeps the base gradient
         and `:root[data-theme=light] .btn:hover` keeps the slate 0 4px 12px shadow, so only `.btn.accent:hover` lands:
         filter brightness(1.06). The owner light hover (lighter gradient, emerald shadow, no filter) is not this. */
      '--chrome-btn-accent-hover': 'linear-gradient(180deg,#34D399,#10B981)',
      '--chrome-btn-accent-hover-shadow': '0 4px 12px rgba(15,23,42,0.10)',
      '--chrome-btn-accent-hover-filter': 'brightness(1.06)',
      /* Legacy admin light sets every .btn to 13.5px and the accent to 700. */
      '--chrome-btn-size': '13.5px',
      '--chrome-btn-accent-weight': '700',
      /* Legacy admin light .pill (v6): 12.5px at weight 400 in the muted ink on the 2% emerald fill with the slate edge;
         the owner light pill is 12px bold on white. */
      '--chrome-pill': 'rgba(52,211,153,0.02)',
      '--chrome-pill-edge': 'var(--rv-separator)',
      '--chrome-pill-size': '12.5px',
      '--chrome-pill-weight': '400',
      '--chrome-pill-ink': 'var(--rv-label-ter)',
      /* Admin light widget title (v6): primary ink, bold, a deeper emerald rule. */
      '--console-title-ink': 'var(--rv-label)',
      '--console-title-weight': '700',
      '--console-title-tracking': '1px',
      '--console-title-size': '12px',
      '--console-title-bar': 'none',
      '--console-title-rule': 'linear-gradient(90deg,rgba(4,120,87,0.55),rgba(4,120,87,0.05) 75%) 1',
      '--console-title-pad': '10px',
      '--console-title-gap': '13px',
      '--console-aux-size': '12px',
      /* Legacy admin light .widget and .card (v6): solid white, the resting shadow and the dark padding (17px 19px);
         the owner light card is wider and tinted. The slate card border is set with the tile values below. */
      '--console-card-pad': '17px 19px',
      '--console-card-fill': '#FFFFFF',
      '--console-card-shadow': '0 1px 2px rgba(15,23,42,0.06),0 4px 14px rgba(15,23,42,0.05)',
      /* Legacy light .wtitle is bold, and its aux inherits that. */
      '--console-aux-weight': '700',
      /* Legacy admin light .tile: a solid white card at rest elevation with the slate border, the dark padding, and
         the hue bar (owner light paints every bar emerald). Hover: the deep emerald edge and --lift. */
      '--console-tile-fill': 'var(--card)',
      '--console-tile-shadow': 'var(--rest)',
      '--console-tile-shadow-hover': 'var(--lift)',
      '--console-tile-border-hover': 'var(--rv-separator-bold)',
      '--console-card-border': 'var(--rv-separator)',
      /* Legacy admin light .widget body inherits the light body line-height 1.5 (owner light keeps normal). The card
         padding is set with the card values above. */
      '--console-card-leading': '1.5',
      '--console-tile-pad': '16px 17px 14px',
      /* Legacy admin .tile .accentbar per hue: the live hue fades to a fixed rgba of the dark hue in both themes. */
      '--console-tile-bar-neutral': 'linear-gradient(180deg,var(--rv-accent) 10%,rgba(52,211,153,0.04))',
      '--console-tile-bar-blue': 'linear-gradient(180deg,var(--rv-blue) 10%,rgba(34,211,238,0.04))',
      '--console-tile-bar-amber': 'linear-gradient(180deg,var(--rv-amber) 10%,rgba(245,158,11,0.04))',
      '--console-tile-bar-purple': 'linear-gradient(180deg,var(--rv-purple) 10%,rgba(192,132,252,0.04))',
      '--console-tile-bar-rose': 'linear-gradient(180deg,var(--rv-rose) 10%,rgba(251,113,133,0.04))',
      '--console-tile-bar-gold': 'linear-gradient(180deg,var(--rv-gold) 10%,rgba(251,191,36,0.04))',
      /* Legacy admin light .tile .k tracking, .v regular weight and normal tracking in --text on every hue, .d at
         12.5px regular with every mark in accent. */
      '--console-tile-label-tracking': '0.9px',
      '--console-tile-value-weight': '400',
      '--console-tile-value-tracking': 'normal',
      '--console-tile-value-ink': 'var(--rv-label)',
      '--console-tile-sub-size': '12.5px',
      '--console-tile-sub-weight': '400',
      '--console-tile-mark-ink': 'var(--rv-accent)',
      /* Legacy admin light body sets line-height 1.5, which the .tile .k and .d lines inherit. */
      '--console-tile-leading': '1.5',
      /* Light .stackbar inset equals the owner light bar well, so stack-inset stays unset. Light bumps .wlpcard .l to
         11.5px. */
      /* Legacy admin .stackbar (Outcomes mix bar): radius 6, flush segments that grow in over 0.7s. */
      '--console-stack-radius': '6px',
      '--console-stack-gap': '0px',
      '--console-stack-motion': '700ms',
      /* Admin Outcomes cards (legacy .wlpcard), an admin app composition: a fixed emerald fill and a fixed-rgba glow per
         result in both themes (legacy never re-tints them in light), so a mix of the live hue cannot match. */
      '--admin-outcome-fill': 'linear-gradient(165deg,rgba(52,211,153,0.07),rgba(52,211,153,0.02))',
      '--admin-outcome-glow-won': '0 0 16px rgba(52,211,153,0.35)',
      '--admin-outcome-glow-lost': '0 0 16px rgba(239,68,68,0.3)',
      '--admin-outcome-glow-pushed': '0 0 16px rgba(245,158,11,0.3)',
      '--admin-outcome-glow-rate': '0 0 16px rgba(34,211,238,0.3)',
      '--admin-outcome-label-size': '11.5px',
      '--console-outcome-fill': 'var(--admin-outcome-fill)',
      '--console-outcome-glow-accent': 'var(--admin-outcome-glow-won)',
      '--console-outcome-glow-red': 'var(--admin-outcome-glow-lost)',
      '--console-outcome-glow-amber': 'var(--admin-outcome-glow-pushed)',
      '--console-outcome-glow-blue': 'var(--admin-outcome-glow-rate)',
      '--console-outcome-label-size': 'var(--admin-outcome-label-size)',
      /* Admin donut charts: the same fixed fifth slice and glow; legacy light [style*="font-size:11px"] sets the legend rows
         to 12.5px, and the donut text inherits the light body line-height 1.5 (dark keeps normal). */
      '--admin-donut-rest': 'rgba(148,163,184,0.6)',
      '--admin-donut-glow': 'rgba(52,211,153,0.35)',
      '--admin-donut-legend-size': '12.5px',
      '--admin-donut-leading': '1.5',
      /* Legacy admin light .infoi: an 18px ring (inset 3px) in --borderStrong, 11px glyph in --textMuted, and no hover
         change (the light rule outranks .infoi:hover). */
      '--console-info-box': '24px',
      '--console-info-inset': '3px',
      '--console-info-margin': '-3.5px -3.5px -3.5px 2.5px',
      '--console-info-row-gap': '0px',
      '--console-title-wrap': 'nowrap',
      '--console-info-row-wrap': 'wrap',
      '--console-info-size': '11px',
      '--console-info-edge': 'rgba(15,23,42,0.22)',
      '--console-info-edge-hover': 'rgba(15,23,42,0.22)',
      '--console-info-ink-hover': 'var(--rv-label-ter)',
      /* Legacy admin light #mpop: .pt 12px regular at 1.2px tracking, .pw and .pc 11px, lines at the body 1.5. */
      '--console-pop-title-size': '12px',
      '--console-pop-title-weight': '400',
      '--console-pop-title-tracking': '1.2px',
      '--console-pop-meta-size': '11px',
      '--console-pop-leading': '1.5',
      /* Legacy admin light .focusback and .focuscard. */
      '--console-focus-scrim': 'rgba(26,26,46,.35)',
      /* Legacy .focuscard paints the page bg (var(--bg)); the core fallback is the dialog surface. */
      '--console-focus-fill': 'var(--rv-bg)',
      '--console-focus-shadow': '0 40px 120px rgba(26,26,46,.25)',
      /* The .wlpcard .l line inherits the light body line-height 1.5; dark keeps normal. */
      '--admin-outcome-leading': '1.5',
      '--console-outcome-leading': 'var(--admin-outcome-leading)',
      /* Admin chart legend and empty line (legacy .tlegend, .empty) inherit the light body line-height 1.5; dark keeps
         normal. */
      '--admin-chart-leading': '1.5',
      /* Admin pace gauges: the same fixed card fill; legacy light bumps the ring label (.gauge .ring .lab .s) to 11px at
         weight 600 with 0.5px tracking. */
      '--admin-gauge-fill': 'linear-gradient(170deg,rgba(52,211,153,0.05),rgba(52,211,153,0.015))',
      '--admin-gauge-label-size': '11px',
      '--admin-gauge-label-weight': '600',
      '--admin-gauge-label-tracking': '.5px',
      /* Admin Activity rhythm heatmap: legacy light bumps the day labels (.hm .hlab) to 11px at weight 600 with 0.5px
         tracking; dark keeps 9px regular. */
      '--admin-rhythm-label-size': '11px',
      '--admin-rhythm-label-weight': '600',
      '--admin-rhythm-label-tracking': '.5px',
      '--ink-meta': '#3F4B5C',
      /* Legacy admin .chip.ok / .red / .warn / .cy / .pu in light: the admin hue ink, the admin soft fill and the
         dark-theme edge. The owner light chips are opaque pastels with deeper ink. */
      '--console-chip-accent-ink': '#046C4E',
      '--console-chip-accent-fill': 'rgba(4,120,87,0.10)',
      '--console-chip-accent-edge': 'rgba(52,211,153,0.35)',
      '--console-chip-red-ink': '#B91C1C',
      '--console-chip-red-fill': 'rgba(185,28,28,0.09)',
      '--console-chip-red-edge': 'rgba(239,68,68,0.4)',
      '--console-chip-amber-ink': '#9A3412',
      '--console-chip-amber-fill': 'rgba(217,119,6,0.12)',
      '--console-chip-amber-edge': 'rgba(245,158,11,0.4)',
      '--console-chip-blue-ink': '#0E6378',
      '--console-chip-blue-fill': 'rgba(14,116,144,0.10)',
      '--console-chip-blue-edge': 'rgba(34,211,238,0.35)',
      '--console-chip-purple-ink': '#6D28D9',
      '--console-chip-purple-fill': 'rgba(109,40,217,0.10)',
      '--console-chip-purple-edge': 'rgba(192,132,252,0.35)',
      '--surface3': 'rgba(4,120,87,0.12)',
      /* Admin light chrome is opaque white (cards, drawers), not the owner 97% glass. */
      '--glassDim': 'rgba(243,246,244,0.78)',
      '--glassDeep': '#FFFFFF',
      '--rose': '#BE123C',
      '--roseDim': 'rgba(190,18,60,0.09)',
      '--goldDim': 'rgba(161,98,7,0.11)',
      '--lift': '0 18px 40px rgba(15,23,42,0.12),0 3px 10px rgba(15,23,42,0.07)',
      /* Admin light only: the solid card fill, the stronger chrome edge and the resting card shadow (v6 surfaces). */
      '--card': '#FFFFFF',
      '--borderStrong': 'rgba(15,23,42,0.22)',
      '--rest': '0 1px 2px rgba(15,23,42,0.06),0 4px 14px rgba(15,23,42,0.05)',
      /* Admin light chips (.chip) keep regular weight; the owner light theme sets them to 600. */
      '--console-chip-weight': '400',
      /* Legacy admin light .feed, .dayhead and .fitem (v6): a white well with no shadow, slate hairlines, larger row
         text, the light body type (14.5px, 1.5 leading) inside the well. */
      ...adminFeed,
      '--console-feed-well-fill': '#FFFFFF',
      '--console-feed-well-edge': 'var(--rv-separator)',
      '--console-feed-well-shadow': 'none',
      '--console-feed-well-size': '14.5px',
      '--console-feed-well-leading': '1.5',
      '--console-feed-row-edge': 'rgba(15,23,42,0.07)',
      /* Legacy admin Do today row rule (.attnrow border-bottom) and console toast (.toast): fixed rgba in both themes,
         the legacy light theme never re-tints them. */
      '--console-attnrow-edge': 'rgba(52,211,153,0.08)',
      '--console-toast-shadow': '0 14px 40px rgba(0,0,0,.5)',
      '--console-toast-edge-ok': 'rgba(52,211,153,0.45)',
      '--console-toast-edge-warn': 'rgba(245,158,11,0.45)',
      '--console-toast-edge-err': 'rgba(239,68,68,0.5)',
      /* Legacy admin .tourcard edge and shadow, fixed rgba in both themes; the .tourback scrim is the pale navy at 35% in light. */
      '--console-tour-scrim': 'rgba(26,26,46,.35)',
      /* Legacy light small-font floor: the Do today severity chip is 12px (dark keeps 10px). */
      '--console-chip-size-micro': '12px',
      '--console-tour-edge': 'rgba(52,211,153,0.4)',
      '--console-tour-shadow': '0 34px 90px rgba(0,0,0,.6)',
      '--console-feed-icon-shadow': 'inset 0 1px 0 rgba(255,255,255,0.9)',
      '--console-feed-text-size': '13.5px',
      /* Legacy admin light body line-height is 1.5; table cells, chips and badges inherit it. */
      '--console-leading': '1.5',
      /* Legacy admin light .chip is 12.5px; inline 9.5px micro chips floor at 11px. Edges are the slate border. */
      '--console-chip-size': '12.5px',
      '--console-chip-size-xs': '11px',
      '--console-chip-neutral-edge': 'var(--rv-separator)',
      '--console-chip-on-edge': 'rgba(52,211,153,0.45)',
      /* Legacy admin .adv-seg[aria-pressed=true]: the soft drop shadow under the lifted segment (both themes). */
      '--console-segment-lift': '0 1px 2px rgba(0,0,0,.12)',
      /* Legacy admin .adv-pill.pu and .adv-ev .ic.pu edge: the fixed brand purple at 40%, not the lighter text purple. */
      '--console-plate-purple-edge': 'rgba(192,132,252,0.4)',
      /* Legacy admin light .badge: 12px, secondary ink and the strong slate edge on every badge. */
      '--console-badge-size': '12px',
      '--console-badge-ink': 'var(--rv-label-sec)',
      '--console-badge-edge': 'rgba(15,23,42,0.22)',
      '--console-badge-amber-edge': 'rgba(15,23,42,0.22)',
      '--console-badge-accent-edge': 'rgba(15,23,42,0.22)',
      '--console-collapse': 'collapse',
      /* Legacy admin light th (v6): 11px, 0.7px tracking, a pale fill; base pad and rule. Weight 700 is the core value. */
      '--console-th-size': '11px',
      '--console-th-tracking': '0.7px',
      '--console-th-fill': '#F7FAF8',
      '--console-th-pad': '8px 10px',
      '--console-th-edge': '1px solid rgba(52,211,153,0.22)',
      '--console-num-font': 'var(--rv-mono)',
      /* Legacy admin light td and table (v6): secondary ink at 14px, base pad, slate rules, stripe and hover. */
      '--console-td-ink': 'var(--rv-label-sec)',
      '--console-td-size': '14px',
      '--console-td-pad': '10px',
      '--console-td-edge': 'rgba(15,23,42,0.08)',
      '--console-row-stripe': 'rgba(15,23,42,0.025)',
      '--console-row-hover': 'rgba(4,120,87,0.06)',
      '--console-row-hover-bar': 'inset 2px 0 0 var(--rv-accent)',
      /* Legacy admin light .tablewrap: a white well with the slate border, no inset shadow. */
      '--console-well-fill': '#FFFFFF',
      '--console-well-edge': '1px solid var(--rv-separator)',
      '--console-well-radius': '12px',
      '--console-well-shadow': 'none',
      '--console-well-pad': '2px 10px 6px',
      /* Legacy admin light td.empty: as dark, but the light td rule outranks .empty, so the ink is the td ink. */
      '--console-empty-pad': '26px 18px',
      '--console-empty-ink': 'var(--rv-label-sec)',
      '--console-empty-size': '12.5px',
      '--console-empty-font': 'var(--rv-mono)',
      '--console-empty-tracking': '0.3px',
      '--console-empty-edge': '1px solid var(--console-td-edge)',
      '--console-empty-mark': 'block',
      '--console-empty-mark-edge': '1px solid rgba(52,211,153,0.25)',
      '--console-empty-mark-fill': 'radial-gradient(circle,rgba(52,211,153,0.35) 0 2px,transparent 3px),conic-gradient(from 0deg,rgba(52,211,153,0.30),transparent 100deg)',
      /* Light podium rows: the light td rule, stripe and hover outrank tr.pod1-3 in legacy. pod1 and pod3 sit on odd
         rows and keep their gradient; pod2 sits on row 2 and shows the stripe; every pod row hovers emerald. */
      '--console-row-amber': 'linear-gradient(90deg,rgba(245,158,11,0.09),rgba(245,158,11,0.015) 70%)',
      '--console-row-amber-hover': 'rgba(4,120,87,0.06)',
      '--console-row-amber-edge': 'rgba(15,23,42,0.08)',
      '--console-row-blue': 'rgba(15,23,42,0.025)',
      '--console-row-blue-hover': 'rgba(4,120,87,0.06)',
      '--console-row-purple': 'linear-gradient(90deg,rgba(192,132,252,0.05),transparent 60%)',
      '--console-row-purple-hover': 'rgba(4,120,87,0.06)',
      /* Legacy admin trendViz email bar fade: the dark-theme blue at 26%, as in dark. */
      '--console-chart-blue-fade': 'rgba(34,211,238,0.26)',
      /* Legacy admin light bumps the trend day ticks (.tcol .cl: 11px, 600, 0.5px) and the axis max (.trendwrap .ymax:
         11.5px); dark keeps 9px and 10px, the fallbacks. */
      '--console-chart-tick-size': '11px',
      '--console-chart-tick-weight': '600',
      '--console-chart-tick-tracking': '0.5px',
      '--console-chart-max-size': '11.5px',
      /* Legacy .nba.watch edge in light keeps the dark cyan at 40% (no light rule re-tints it). */
      '--console-action-blue-edge': 'rgba(34,211,238,0.4)',
      /* Legacy admin light .avatar: a deeper emerald gradient than owner light, white 11px initials, and the dark
         theme shadows and presence rings (the light theme only re-paints the fill). Dormant is a slate gradient with
         no desaturate (:root[data-theme=light] .avatar.p-dormant). */
      '--console-avatar-fill': 'linear-gradient(150deg,#065F46,#047857)',
      '--console-avatar-shadow': '0 0 0 1px rgba(52,211,153,0.35),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-today': '0 0 0 2px var(--rv-accent),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-week': '0 0 0 2px rgba(52,211,153,0.35),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-ring-dormant': '0 0 0 2px rgba(107,114,128,0.45),0 2px 8px rgba(0,0,0,.3)',
      '--console-avatar-size': '11px',
      '--console-avatar-fill-dormant': 'linear-gradient(150deg,#475569,#64748B)',
      '--console-avatar-filter-dormant': 'none',
      /* Legacy admin light .usetile, .pacebar .pb and gauge() ring: the same fill, --edge shade and ring as dark; light
         bumps .usetile .k to 11px at weight 600 and .5px tracking and .usetile .d to 11.5px, and sets a slate inset on
         .pacebar .pb. */
      '--console-use-fill': 'linear-gradient(165deg,rgba(52,211,153,0.06),rgba(52,211,153,0.015))',
      '--console-use-shadow': 'var(--edge)',
      '--console-use-label-size': '11px',
      '--console-use-label-weight': '600',
      '--console-use-label-tracking': '.5px',
      '--console-use-sub-size': '11.5px',
      /* Legacy admin light timeline (.tlstep): the core dot is the admin accent (not the owner #059669) and .td keeps 11px;
         only .rep2 takes the 12px floor. */
      '--console-tl-dot-core': 'var(--rv-accent)',
      '--console-tl-rail-time-size': '11px',
      '--console-pace-inset': 'inset 0 1px 2px rgba(26,26,46,0.08)',
      '--console-xp-track': 'rgba(52,211,153,0.06)',
      '--console-xp-fill-end': 'rgba(192,132,252,0.4)',
      '--console-ring-track': 'var(--surface3)',
      '--console-ring-track-opacity': '1',
      '--console-ring-center-size': '1em',
      '--console-ring-center-weight': '400',
      '--console-ring-center-tracking': 'normal',
      /* Legacy admin light .fitem rows (ListRow variant="console"): a slate hairline at 7%, the light body type size
         (14.5px) the row inherits, and .fitem .tx at 13.5px. */
      '--console-listrow-edge': 'rgba(15,23,42,0.07)',
      /* Legacy admin .kbrow:focus-visible, including interactive .fitem feed rows. */
      '--console-listrow-feed-focus-offset': '2px',
      '--console-listrow-feed-focus-radius': ADMIN_KEYBOARD_ROW_FOCUS_RADIUS,
      '--console-attn-edge': 'rgba(52,211,153,0.08)',
      /* Legacy admin Rep Account intel panel (.intel) and its signal bars (.sigrow): fixed cyan rgba in both themes; only the
         panel shadow and the bar well differ in light. */
      '--console-intel-edge': 'rgba(34,211,238,0.28)',
      '--console-intel-fill': 'linear-gradient(165deg,rgba(34,211,238,0.06),rgba(34,211,238,0.015))',
      '--console-intel-shadow': 'inset 0 1px 0 rgba(255,255,255,.6), 0 10px 30px rgba(26,26,46,.08)',
      '--console-intel-grid': 'rgba(34,211,238,0.03)',
      '--console-intel-bar-track': 'rgba(52,211,153,0.06)',
      '--console-intel-bar-inset': 'inset 0 1px 2px rgba(26,26,46,.08)',
      '--console-intel-bar-end': 'rgba(34,211,238,0.4)',
      /* Legacy admin light bumps .flabel to 11px, 600, 0.5px tracking (the shared small-label rule). */
      '--console-flabel-size': '11px',
      '--console-flabel-weight': '600',
      '--console-flabel-tracking': '0.5px',
      /* Legacy admin light sets `.docform label` to 12px (the shared small-label rule). */
      '--console-docform-label-size': '12px',
      '--console-listrow-size': '14.5px',
      '--console-listrow-text-size': '13.5px',
      /* Legacy admin light keyboard focus on an Engagement person row is the solid accent, not the dark glow. */
      '--console-listrow-focus': 'var(--rv-accent)',
      /* Legacy admin light bumps every inline font-size:10px to 12px, the 26px row avatar included. */
      '--console-avatar-size-row': '12px',
      /* Admin Quota & pace runway (legacy teampace .qbar): the same fixed rgba as dark, its inline dark inset too. */
      '--admin-pace-track': 'rgba(52,211,153,0.05)',
      '--admin-runway-inset': 'inset 0 1px 2px rgba(0,0,0,.3)',
      '--admin-runway-fill-end': 'rgba(52,211,153,0.5)',
      '--admin-runway-proj': 'repeating-linear-gradient(-45deg,rgba(34,211,238,0.25) 0 6px,rgba(34,211,238,0.08) 6px 12px)',
      /* Admin Team composition: the same fixed layer colors as dark; the light day hover is the deep emerald at 9%. */
      '--admin-teamcomp-fifth': 'rgba(52,211,153,0.55)',
      '--admin-teamcomp-others': 'rgba(148,163,184,0.5)',
      '--admin-teamcomp-hover': 'rgba(16,185,129,0.09)',
      /* Admin Pending members rows (legacy .reqrow): the same fixed emerald hairline as dark; no light rule. */
      '--admin-reqrow-edge': 'rgba(52,211,153,0.07)',
      /* Admin Quota attainment spread (legacy quotadist): the same fixed bar fade and dot glow as dark; no light rule. */
      '--admin-quotadist-bar-end': 'rgba(34,211,238,0.3)',
      '--admin-quotadist-dot-glow': 'drop-shadow(0 0 2px rgba(192,132,252,0.5))',
      /* Legacy admin light bumps every inline font-size:11px to 12.5px, the compact select (Stale reps threshold) too. */
      '--console-field-compact-size': '12.5px',
      /* Legacy admin light .finput: the faint navy fill, and the light input rule paints the --borderStrong slate edge,
         which outranks .finput:focus, so focus keeps the edge and shows only the light :focus-visible accent outline.
         The owner light field (white, navy edge, emerald focus edge and ring) does not apply. */
      '--console-field-fill': 'rgba(26,26,46,0.04)',
      '--console-field-edge': 'rgba(15,23,42,0.22)',
      '--console-field-focus-edge': 'rgba(15,23,42,0.22)',
      '--console-field-focus-ring': 'none',
      '--console-field-focus-outline': 'var(--rv-accent)',
      /* Admin rank medals (legacy .rank.m2 and .rank.m3): the same fixed cyan and purple edges as dark; no light rule. */
      '--admin-rank-edge-2': 'rgba(34,211,238,0.45)',
      '--admin-rank-edge-3': 'rgba(192,132,252,0.45)',
      /* Admin legacy brief reader, light. The dark rules above, plus the light size floors in index.html (all !important):
         .brsec .brh 12px; .brfact .k 11px at weight 600 and .5px tracking; .brfid and .brthreat 11.5px; and the slate inset
         under the signal track (.sigrow .sb, rgba(26,26,46,.08)). The other two floors reuse roles set above in this block:
         .annotmeta 12px is --console-meta-size, and the plain .chip 12.5px at weight 400 is --console-chip-size. */
      '--console-brief-h-size': '12px',
      '--console-brief-h-track': '1.3px',
      '--console-brief-k-size': '11px',
      '--console-brief-k-weight': '600',
      '--console-brief-k-track': '0.5px',
      '--console-brief-sig-gap': '6px',
      '--console-brief-sig-top': '9px',
      '--console-brief-sig-track': 'rgba(52,211,153,0.06)',
      '--console-brief-sig-tail': 'rgba(34,211,238,0.4)',
      '--console-brief-sig-inset': 'inset 0 1px 2px rgba(26,26,46,0.08)',
      '--console-brief-train-edge': 'rgba(52,211,153,0.3)',
      '--console-brief-train-fill': 'linear-gradient(135deg,rgba(52,211,153,0.07),transparent 70%)',
      '--console-brief-quote-top': '4px',
      '--console-brief-train-h-font': 'var(--rv-font)',
      '--console-brief-train-h-size': '1em',
      '--console-brief-train-h-track': 'normal',
      '--console-brief-train-h-case': 'none',
      '--console-brief-train-h-gap': '0px',
      '--console-pill-outline-edge': 'rgba(34,211,238,0.4)',
      '--console-pill-outline-size': '11.5px',
      '--console-pill-flat-size': '11.5px',
      '--console-pill-flat-amber-fill': 'rgba(245,158,11,0.16)',
      '--console-pill-flat-blue-fill': 'rgba(34,211,238,0.14)',
      /* The same two reads in light: the body ink for bold text in a confirm, and the page-wide light ring
         (:root[data-theme=light] :focus-visible) is the solid accent. */
      '--console-confirm-strong-ink': 'var(--rv-label-sec)',
      '--console-button-focus-ring': 'var(--rv-accent)',
      '--console-brief-card-focus-radius': ADMIN_KEYBOARD_ROW_FOCUS_RADIUS,
    },
  },
};

/*
 * Recon (recon.repvantage.io). Values are verbatim from the shipped apps/recon/src/styles.css :root palette, so the
 * token step (component-library spec 2b step 4) is a visual no-op. Base keys Recon already matches (dark bg, canvas,
 * separator, text ladder, every dark hue, soft fill and ink, ease curves, wordmark ink) are not repeated here.
 * Contrast numbers are measured against the Recon card fill (#0E2318 dark, #FFFFFF light).
 */
/* Button variant="recon-*" and IconButton variant="recon" roles (packages/ui/src/internal/reconRoles.ts): the shipped
   .cta, .ghost-btn and .icon-btn values with no base token. One set for both themes, as the shipped rules were. */
const reconButtonRoles: Record<string, string> = {
  /* Hover fill of .ghost-btn and .icon-btn: the third glass step. */
  '--recon-button-hover': 'var(--surface3)',
  /* .ghost-btn.warn and .danger edges: amber at 50% and red at 45%. */
  '--recon-warn-edge': 'rgba(245,158,11,0.5)',
  '--recon-danger-edge': 'rgba(239,68,68,0.45)',
  '--recon-danger-ink': 'var(--red-ink)',
  /* .cta: a lit emerald gradient (lighter top, deeper base), an emerald glow and a white top highlight. */
  '--recon-cta-fill':
    'linear-gradient(180deg, color-mix(in srgb, var(--rv-accent) 86%, #fff) 0%, var(--rv-accent) 55%, color-mix(in srgb, var(--rv-accent) 88%, #000) 100%)',
  '--recon-cta-shadow': '0 12px 32px rgba(52,211,153,0.32), 0 1px 0 rgba(255,255,255,0.35) inset',
  '--recon-cta-hover-shadow': '0 16px 40px rgba(52,211,153,0.4), 0 1px 0 rgba(255,255,255,0.35) inset',
  /* .lightbox .icon-btn: a dark glass disc over the photo in both themes. */
  '--recon-overlay-fill': 'rgba(10,26,16,0.8)',
  '--recon-overlay-edge': 'rgba(52,211,153,0.25)',
  '--recon-overlay-ink': '#E2E8F0',
  /* Tabs variant="reconTheme": the selected segment of .theme-toggle, an opaque card fill with a faint drop and a bold
     inner edge. */
  '--recon-segment-on-fill': 'var(--card-solid)',
  '--recon-segment-on-shadow': '0 1px 2px rgba(0,0,0,0.18), inset 0 0 0 1px var(--rv-separator-bold)',
};

/* Recon art and chart colors with no base token (the --rv-recon-* names keep them in the token namespace), one set for both themes as the shipped rules were: white and black
   mixing stops, the slate steel of the tap-handle drawings and the rival chart shades, and the 15 planogram category
   hues (emitted under the shipped --pg-cat-* names). Static art, not theme ink. */
const reconArtRoles: Record<string, string> = {
  '--rv-recon-white': '#FFFFFF',
  '--rv-recon-black': '#000000',
  '--rv-recon-steel': '#94A3B8',
  '--rv-recon-steel-dark': '#64748B',
  '--rv-recon-steel-light': '#CBD5E1',
  '--rv-recon-cyan-light': '#67E8F9',
  /* Ink on the amber new-tap tag. */
  '--rv-recon-tap-ink': '#1A1204',
  '--rv-recon-overlay-ink': 'var(--recon-overlay-ink)',
  '--pg-cat-premium-domestic': '#F59E0B',
  '--pg-cat-value-domestic': '#D4A373',
  '--pg-cat-import': '#22D3EE',
  '--pg-cat-craft': '#FB923C',
  '--pg-cat-seltzer': '#A3E635',
  '--pg-cat-fmb': '#C084FC',
  '--pg-cat-rtd-cocktail': '#F472B6',
  '--pg-cat-na-beer': '#2DD4BF',
  '--pg-cat-thc-bev': '#E879F9',
  '--pg-cat-cider': '#FCA5A5',
  '--pg-cat-energy': '#FDE047',
  '--pg-cat-csd': '#A8A29E',
  '--pg-cat-water': '#7DD3FC',
  '--pg-cat-functional': '#BEF264',
  '--pg-cat-other': '#64748B',
};

/* Exact Recon scale and glass roles. Local layout variables stay in the app. */
const reconScopeRoles: Record<string, string> = {
  '--rv-recon-fs-12': '0.75rem',
  '--rv-recon-fs-13': '0.8125rem',
  '--rv-recon-fs-15': '0.9375rem',
  '--rv-recon-fs-16': '1rem',
  '--rv-recon-fs-17': '1.0625rem',
  '--rv-recon-fs-20': '1.25rem',
  '--rv-recon-fs-24': '1.5rem',
  '--rv-recon-fs-30': '1.875rem',
  '--rv-recon-fs-38': '2.375rem',
  '--rv-recon-fs-48': '3rem',
  '--rv-recon-r-xs': '4px',
  '--rv-recon-r-sm': '8px',
  '--rv-recon-r-md': '12px',
  '--rv-recon-r-lg': '16px',
  '--rv-recon-r-xl': '24px',
  '--rv-recon-dur-press': '110ms',
  '--rv-recon-dur-hover': '170ms',
  '--rv-recon-dur-enter': '240ms',
  '--rv-recon-dur-exit': '170ms',
  '--rv-recon-dur-reveal': '400ms',
  '--rv-recon-stagger': '40ms',
  '--rv-recon-surface3': 'var(--surface3)',
  '--rv-recon-card': 'var(--card)',
  '--rv-recon-glass-dim': 'var(--glass-dim)',
  '--rv-recon-screen-base': 'var(--screen-base)',
  '--rv-recon-purple-ink': 'var(--purple-ink)',
  '--rv-recon-red-ink': 'var(--red-ink)',
  '--rv-recon-category-premium-domestic': '#F59E0B',
  '--rv-recon-category-value-domestic': '#D4A373',
  '--rv-recon-category-import': '#22D3EE',
  '--rv-recon-category-craft': '#FB923C',
  '--rv-recon-category-seltzer': '#A3E635',
  '--rv-recon-category-fmb': '#C084FC',
  '--rv-recon-category-rtd-cocktail': '#F472B6',
  '--rv-recon-category-na-beer': '#2DD4BF',
  '--rv-recon-category-thc-bev': '#E879F9',
  '--rv-recon-category-cider': '#FCA5A5',
  '--rv-recon-category-energy': '#FDE047',
  '--rv-recon-category-csd': '#A8A29E',
  '--rv-recon-category-water': '#7DD3FC',
  '--rv-recon-category-functional': '#BEF264',
  '--rv-recon-category-other': '#64748B',
};

/* Decorative washes, gradients and depth. Values match the shipped Recon CSS. */
const reconWashRoles: Record<string, string> = {
  /* Splash-only decorative washes retain the shipped alpha values. */
  /* Composite contrast on opaque dark/light card: 1.30:1 / 1.12:1. */
  '--rv-recon-wash-amber-15': 'rgba(245, 158, 11, 0.15)',
  /* Composite contrast on opaque dark/light card: 5.35:1 / 1.85:1. */
  '--rv-recon-wash-amber-80': 'rgba(245, 158, 11, 0.8)',
  /* Composite contrast on opaque dark/light card: 7.05:1 / 2.07:1. */
  '--rv-recon-wash-amber-95': 'rgba(245, 158, 11, 0.95)',
  /* Composite contrast on opaque dark/light card: 8.64:1 / 1.00:1. */
  '--rv-recon-wash-white-70': 'rgba(255, 255, 255, 0.7)',
  /* Composite contrast on opaque dark/light card: 1.08:1 / 1.03:1. */
  '--rv-recon-wash-emerald-04': 'rgba(52, 211, 153, 0.04)',
  /* Composite contrast on opaque dark/light card: 1.47:1 / 1.13:1. */
  '--rv-recon-wash-emerald-18': 'rgba(52, 211, 153, 0.18)',
  /* Composite contrast on opaque dark/light card: 1.62:1 / 1.17:1. */
  '--rv-recon-wash-emerald-22': 'rgba(52, 211, 153, 0.22)',
  /* Composite contrast on opaque dark/light card: 1.87:1 / 1.21:1. */
  '--rv-recon-wash-emerald-28': 'rgba(52, 211, 153, 0.28)',
  /* Composite contrast on opaque dark/light card: 1.97:1 / 1.23:1. */
  '--rv-recon-wash-emerald-30': 'rgba(52, 211, 153, 0.3)',
  /* Composite contrast on opaque dark/light card: 3.97:1 / 1.51:1. */
  '--rv-recon-wash-emerald-60': 'rgba(52, 211, 153, 0.6)',
  /* Composite contrast on opaque dark/light card: 5.43:1 / 1.66:1. */
  '--rv-recon-wash-emerald-75': 'rgba(52, 211, 153, 0.75)',
  /* Composite contrast on opaque dark/light card: 7.21:1 / 1.82:1. */
  '--rv-recon-wash-emerald-90': 'rgba(52, 211, 153, 0.9)',
  /* Composite contrast on opaque dark/light card: 1.01:1 / 1.32:1. */
  '--rv-recon-wash-emerald-ink-14': 'rgba(6, 40, 28, 0.14)',
  /* Composite contrast on opaque dark/light card: 1.03:1 / 5.97:1. */
  '--rv-recon-wash-emerald-ink-70': 'rgba(6, 40, 28, 0.7)',

  /* Composite contrast on opaque dark/light card: 1.06:1 / 1.53:1. */
  '--rv-recon-wash-black-18': 'rgba(0, 0, 0, 0.18)',
  /* Composite contrast on opaque dark/light card: 1.09:1 / 1.83:1. */
  '--rv-recon-wash-black-25': 'rgba(0, 0, 0, 0.25)',
  /* Composite contrast on opaque dark/light card: 1.12:1 / 2.44:1. */
  '--rv-recon-wash-black-35': 'rgba(0, 0, 0, 0.35)',
  /* Composite contrast on opaque dark/light card: 1.02:1 / 1.54:1. */
  '--rv-recon-wash-forest-20': 'rgba(10, 26, 16, 0.2)',
  /* Composite contrast on opaque dark/light card: 1.02:1 / 1.06:1. */
  '--rv-recon-wash-wood-04': 'rgba(120, 83, 50, 0.04)',
  /* Composite contrast on opaque dark/light card: 1.11:1 / 1.27:1. */
  '--rv-recon-wash-wood-16': 'rgba(120, 83, 50, 0.16)',
  /* Composite contrast on opaque dark/light card: 1.68:1 / 1.31:1. */
  '--rv-recon-wash-purple-30': 'rgba(192, 132, 252, 0.3)',
  /* Composite contrast on opaque dark/light card: 1.00:1 / 1.00:1. */
  '--rv-recon-wash-red-00': 'rgba(239, 68, 68, 0)',
  /* Composite contrast on opaque dark/light card: 1.01:1 / 1.03:1. */
  '--rv-recon-wash-red-02': 'rgba(239, 68, 68, 0.02)',
  /* Composite contrast on opaque dark/light card: 1.10:1 / 1.17:1. */
  '--rv-recon-wash-red-12': 'rgba(239, 68, 68, 0.12)',
  /* Composite contrast on opaque dark/light card: 1.75:1 / 1.84:1. */
  '--rv-recon-wash-red-45': 'rgba(239, 68, 68, 0.45)',
  /* Composite contrast on opaque dark/light card: 1.00:1 / 1.00:1. */
  '--rv-recon-wash-amber-00': 'rgba(245, 158, 11, 0)',
  /* Composite contrast on opaque dark/light card: 1.03:1 / 1.02:1. */
  '--rv-recon-wash-amber-02': 'rgba(245, 158, 11, 0.02)',
  /* Composite contrast on opaque dark/light card: 1.05:1 / 1.02:1. */
  '--rv-recon-wash-amber-03': 'rgba(245, 158, 11, 0.03)',
  /* Composite contrast on opaque dark/light card: 1.23:1 / 1.10:1. */
  '--rv-recon-wash-amber-12': 'rgba(245, 158, 11, 0.12)',
  /* Composite contrast on opaque dark/light card: 1.81:1 / 1.26:1. */
  '--rv-recon-wash-amber-30': 'rgba(245, 158, 11, 0.3)',
  /* Composite contrast on opaque dark/light card: 2.03:1 / 1.31:1. */
  '--rv-recon-wash-amber-35': 'rgba(245, 158, 11, 0.35)',
  /* Composite contrast on opaque dark/light card: 2.56:1 / 1.41:1. */
  '--rv-recon-wash-amber-45': 'rgba(245, 158, 11, 0.45)',
  /* Composite contrast on opaque dark/light card: 3.56:1 / 1.59:1. */
  '--rv-recon-wash-amber-60': 'rgba(245, 158, 11, 0.6)',
  /* Composite contrast on opaque dark/light card: 4.39:1 / 1.72:1. */
  '--rv-recon-wash-amber-70': 'rgba(245, 158, 11, 0.7)',
  /* Composite contrast on opaque dark/light card: 2.38:1 / 1.66:1. */
  '--rv-recon-wash-red-ink-50': 'rgba(248, 113, 113, 0.5)',
  /* Composite contrast on opaque dark/light card: 1.00:1 / 1.00:1. */
  '--rv-recon-wash-white-00': 'rgba(255, 255, 255, 0)',
  /* Composite contrast on opaque dark/light card: 1.18:1 / 1.00:1. */
  '--rv-recon-wash-white-06': 'rgba(255, 255, 255, 0.06)',
  /* Composite contrast on opaque dark/light card: 1.22:1 / 1.00:1. */
  '--rv-recon-wash-white-07': 'rgba(255, 255, 255, 0.07)',
  /* Composite contrast on opaque dark/light card: 1.77:1 / 1.00:1. */
  '--rv-recon-wash-white-18': 'rgba(255, 255, 255, 0.18)',
  /* Composite contrast on opaque dark/light card: 2.26:1 / 1.00:1. */
  '--rv-recon-wash-white-25': 'rgba(255, 255, 255, 0.25)',
  /* Composite contrast on opaque dark/light card: 1.20:1 / 17.89:1. */
  '--rv-recon-wash-deep-94': 'rgba(3, 10, 7, 0.94)',
  /* Composite contrast on opaque dark/light card: 1.00:1 / 1.00:1. */
  '--rv-recon-wash-cyan-00': 'rgba(34, 211, 238, 0)',
  /* Composite contrast on opaque dark/light card: 2.01:1 / 1.22:1. */
  '--rv-recon-wash-cyan-30': 'rgba(34, 211, 238, 0.3)',
  /* Composite contrast on opaque dark/light card: 2.59:1 / 1.30:1. */
  '--rv-recon-wash-cyan-40': 'rgba(34, 211, 238, 0.4)',
  /* Composite contrast on opaque dark/light card: 1.00:1 / 1.00:1. */
  '--rv-recon-wash-emerald-00': 'rgba(52, 211, 153, 0)',
  /* Composite contrast on opaque dark/light card: 1.04:1 / 1.01:1. */
  '--rv-recon-wash-emerald-02': 'rgba(52, 211, 153, 0.02)',
  /* Composite contrast on opaque dark/light card: 1.10:1 / 1.04:1. */
  '--rv-recon-wash-emerald-05': 'rgba(52, 211, 153, 0.05)',
  /* Composite contrast on opaque dark/light card: 1.22:1 / 1.07:1. */
  '--rv-recon-wash-emerald-10': 'rgba(52, 211, 153, 0.1)',
  /* Composite contrast on opaque dark/light card: 1.28:1 / 1.09:1. */
  '--rv-recon-wash-emerald-12': 'rgba(52, 211, 153, 0.12)',
  /* Composite contrast on opaque dark/light card: 1.40:1 / 1.12:1. */
  '--rv-recon-wash-emerald-16': 'rgba(52, 211, 153, 0.16)',
  /* Composite contrast on opaque dark/light card: 1.54:1 / 1.15:1. */
  '--rv-recon-wash-emerald-20': 'rgba(52, 211, 153, 0.2)',
  /* Composite contrast on opaque dark/light card: 1.74:1 / 1.19:1. */
  '--rv-recon-wash-emerald-25': 'rgba(52, 211, 153, 0.25)',
  /* Composite contrast on opaque dark/light card: 2.07:1 / 1.25:1. */
  '--rv-recon-wash-emerald-32': 'rgba(52, 211, 153, 0.32)',
  /* Composite contrast on opaque dark/light card: 2.22:1 / 1.27:1. */
  '--rv-recon-wash-emerald-35': 'rgba(52, 211, 153, 0.35)',
  /* Composite contrast on opaque dark/light card: 2.51:1 / 1.32:1. */
  '--rv-recon-wash-emerald-40': 'rgba(52, 211, 153, 0.4)',
  /* Composite contrast on opaque dark/light card: 2.83:1 / 1.36:1. */
  '--rv-recon-wash-emerald-45': 'rgba(52, 211, 153, 0.45)',
  /* Composite contrast on opaque dark/light card: 3.18:1 / 1.41:1. */
  '--rv-recon-wash-emerald-50': 'rgba(52, 211, 153, 0.5)',
  /* Composite contrast on opaque dark/light card: 3.56:1 / 1.46:1. */
  '--rv-recon-wash-emerald-55': 'rgba(52, 211, 153, 0.55)',
  /* Composite contrast on opaque dark/light card: 4.91:1 / 1.61:1. */
  '--rv-recon-wash-emerald-70': 'rgba(52, 211, 153, 0.7)',
  /* Composite contrast on opaque dark/light card: 1.01:1 / 1.57:1. */
  '--rv-recon-wash-emerald-ink-22': 'rgba(6, 40, 28, 0.22)',
  /* Composite contrast on opaque dark/light card: 1.10:1 / 4.29:1. */
  '--rv-recon-wash-shade-55': 'rgba(7, 15, 12, 0.55)',
};

const recon: ProductOverride = {
  dark: {
    /* Recon glass fills are fainter than base (3% / 6% vs 10% / 14%): Recon layers them inside its 72% card. */
    surface: 'rgba(52,211,153,0.03)',
    surfaceAlt: 'rgba(52,211,153,0.06)',
    /* Recon hover edge is lighter than the base bold separator. */
    separatorBold: 'rgba(52,211,153,0.25)',
    /* Recon top and tab bar glass is darker and more transparent than base. */
    barBg: 'rgba(9,20,15,0.82)',
    /* Recon ink on emerald fills is one deep green in both themes (8.23:1 on #34D399). */
    onAccent: '#06281C',
    /* Recon card and sheet depth carry an emerald top highlight; base cards use a white one. */
    shadowCard: '0 8px 32px rgba(0,0,0,0.22), 0 1px 0 rgba(52,211,153,0.06) inset',
    shadowDialog: '0 16px 48px rgba(0,0,0,0.38), 0 1px 0 rgba(52,211,153,0.08) inset',
    /* Recon type stacks are the shipped --font / --mono / --display verbatim, so computed font-family matches exactly
       (base stacks carry extra system fallbacks). */
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
  },
  light: {
    /* Recon light canvas is a cooler near-white with the dark theme's emerald and amber glows, not the base gradient. */
    canvas:
      'radial-gradient(58% 55% at 50% 30%, rgba(52,211,153,0.16) 0%, rgba(52,211,153,0.05) 46%, rgba(250,251,252,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.07) 0%, rgba(250,251,252,0) 60%), linear-gradient(160deg, #EEF7F2 0%, #FAFBFC 55%, #EDF6F0 100%)',
    bg: '#FAFBFC',
    /* Recon light glass and edges are neutral black washes, not base slate. */
    surface: 'rgba(0,0,0,0.02)',
    surfaceAlt: 'rgba(0,0,0,0.04)',
    separator: 'rgba(0,0,0,0.08)',
    separatorBold: 'rgba(0,0,0,0.15)',
    barBg: 'rgba(250,251,252,0.88)',
    /* Recon light text is a navy ink ladder (16.46:1 on #FAFBFC); labelSec matches base. labelTer 4.76:1 on white;
       labelQuad is for rules, bars and icons only, never text. */
    label: '#1A1A2E',
    labelTer: '#64748B',
    labelQuad: '#94A3B8',
    /* Recon keeps the brand hues as fills in light too, unlike the deepened base light hues. Text on a tinted fill
       uses the ink roles below, never the hue. */
    accent: '#34D399',
    accentSoft: 'rgba(52,211,153,0.16)',
    accentTint: 'rgba(52,211,153,0.4)',
    /* 15.81:1 on white; 8.23:1 as ink on #34D399. */
    accentInk: '#06281C',
    onAccent: '#06281C',
    amber: '#F59E0B',
    amberSoft: 'rgba(245,158,11,0.14)',
    /* 7.48:1 on white. */
    amberInk: '#7A4A00',
    purple: '#C084FC',
    purpleSoft: 'rgba(192,132,252,0.16)',
    blue: '#22D3EE',
    blueSoft: 'rgba(34,211,238,0.16)',
    /* 8.37:1 on white. */
    blueInk: '#0E5566',
    red: '#EF4444',
    redSoft: 'rgba(239,68,68,0.12)',
    /* Recon light depth is a flat, faint drop with no highlight. */
    shadowCard: '0 4px 16px rgba(0,0,0,0.06)',
    shadowDialog: '0 8px 24px rgba(0,0,0,0.08)',
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
  },
  roles: {
    dark: {
      '--rv-recon-splash-canvas': "radial-gradient(58% 55% at 50% 42%, rgba(52, 211, 153, 0.2) 0%, rgba(52, 211, 153, 0.06) 46%, rgba(13, 27, 17, 0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245, 158, 11, 0.06) 0%, rgba(13, 27, 17, 0) 60%), linear-gradient(160deg, #0c1e15 0%, #091210 55%, #0d1b11 100%)",
      /* Recon-only roles under their shipped names; base has no token for these. Third glass step (pressed tiles). */
      '--rv-recon-pg-tile-mix': '34%',
      '--rv-recon-pg-shelf': 'rgba(52, 211, 153, 0.22)',
      '--surface3': 'rgba(52,211,153,0.1)',
      /* Translucent card over the canvas, its opaque twin for sticky headers, and the sheet backdrop. */
      '--card': 'rgba(10,26,16,0.72)',
      '--card-solid': '#0E2318',
      /* Canonical opaque-card alias for Recon skip links. Keep the existing product fill. */
      '--rv-recon-card-solid': 'var(--card-solid)',
      '--glass-dim': 'rgba(10,26,16,0.6)',
      /* Page fill under the canvas (first canvas stop), for overscroll. */
      '--screen-base': '#0C1E15',
      /* Purple and red text inks; base has no purpleInk or redInk. 6.25:1 and 5.97:1 on the card. */
      '--purple-ink': '#C084FC',
      '--red-ink': '#F87171',
      /* Card variant="recon" roles (packages/ui/src/internal/reconRoles.ts): the shipped .card fill, edge and frosted
         backdrop. A Recon modifier class sets these on one card to restyle it. */
      '--recon-card-fill': 'var(--card)',
      '--recon-card-edge': 'var(--rv-separator)',
      '--recon-card-backdrop': 'blur(16px) saturate(150%)',
      '--rv-recon-card-radius': '16px',
      '--rv-recon-card-shadow': 'var(--rv-shadow-card)',
      '--rv-recon-card-border-style': 'solid',
      ...reconButtonRoles,
      ...reconArtRoles,
      ...reconScopeRoles,
      ...reconWashRoles,
    },
    light: {
      '--rv-recon-splash-canvas': "radial-gradient(58% 55% at 50% 42%, rgba(52, 211, 153, 0.16) 0%, rgba(52, 211, 153, 0.05) 46%, rgba(250, 251, 252, 0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245, 158, 11, 0.07) 0%, rgba(250, 251, 252, 0) 60%), linear-gradient(160deg, #eef7f2 0%, #fafbfc 55%, #edf6f0 100%)",
      '--rv-recon-pg-tile-mix': '42%',
      '--rv-recon-pg-shelf': 'rgba(0, 0, 0, 0.14)',
      '--surface3': 'rgba(0,0,0,0.06)',
      '--card': 'rgba(255,255,255,0.86)',
      '--card-solid': '#FFFFFF',
      /* Canonical opaque-card alias for Recon skip links. Keep the existing product fill. */
      '--rv-recon-card-solid': 'var(--card-solid)',
      '--glass-dim': 'rgba(0,0,0,0.4)',
      '--screen-base': '#EEF7F2',
      /* 8.72:1 and 6.47:1 on white. */
      '--purple-ink': '#6B21A8',
      '--red-ink': '#B91C1C',
      '--recon-card-fill': 'var(--card)',
      '--recon-card-edge': 'var(--rv-separator)',
      '--recon-card-backdrop': 'blur(16px) saturate(150%)',
      '--rv-recon-card-radius': '16px',
      '--rv-recon-card-shadow': 'var(--rv-shadow-card)',
      '--rv-recon-card-border-style': 'solid',
      ...reconButtonRoles,
      ...reconArtRoles,
      ...reconScopeRoles,
      ...reconWashRoles,
      /* Light-only: the planogram tile edge for your own brand and the landing headline accent, both deeper than the
         light accent so they hold contrast on a white card. */
      '--rv-pg-mine-edge': '#0F9F6E',
      '--rv-recon-site-accent-ink': '#047857',
    },
  },
};

/*
 * Marketing site (repvantage.io, apps/marketing). Values are verbatim from the token block apps/marketing/index.html
 * shipped before spec 2b step 6, so the move is a visual no-op; names that nothing read were dropped.
 * scripts/build-marketing-tokens.mjs inlines them into that page under the marketing selectors (:root and
 * [data-theme='dark'] for dark, [data-theme='light'] for light): the site pins device mocks dark with a nested
 * data-theme, so every pinned subtree must declare the full set, which the [data-rv-product] blocks in tokens.css do
 * not do. Dark base values already equal the marketing ones.
 */
const marketingFonts = {
  /* The site self-hosts all three faces (preloaded WOFF2) and has always shipped the short stacks. */
  font: "'Jost', sans-serif",
  mono: "'Share Tech Mono', monospace",
  fontDisplay: "'Orbitron', sans-serif",
};

const marketing: ProductOverride = {
  dark: { ...marketingFonts },
  light: {
    ...marketingFonts,
    /* Marketing light mirrors the recon page palette: a near-white page, brand hues deepened for text on it. */
    bg: '#FAFBFC',
    /* 5.29:1 on #FAFBFC. */
    accent: '#047857',
    /* 4.85:1 on #FAFBFC. */
    amber: '#B45309',
    /* 6.74:1 on #FAFBFC. */
    purple: '#7E22CE',
    /* 4.66:1 on #FAFBFC. */
    red: '#DC2626',
  },
  /* Marketing-only roles. The names predate the base tokens and every marketing page reads them through T in
     src/lib/rv.jsx, inline <style> blocks and index.html, so they keep their --rv-* names. */
  roles: {
    dark: {
      /* Native sample actions: neutral ink is 15.45:1 on its sample card; primary ink is 9.90:1 on accent. Edge is decorative. */
      '--rv-sample-action-font': '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif',
      '--rv-sample-action-ink': '#EEF3F0',
      '--rv-sample-action-edge': 'rgba(255,255,255,0.09)',
      '--rv-sample-action-on-accent': '#04130B',
      '--rv-sample-action-ease': 'cubic-bezier(0.16,1,0.3,1)',
      '--rv-bg-deep': '#06120B',
      /* Exact public BackToTop glass fill; the accent glyph uses the inherited marketing accent. */
      '--rv-marketing-scroll-fill': 'rgba(10,26,16,0.84)',
      /* BackToTop keeps its fixed shipped curve, independently of the host ease token. */
      '--rv-marketing-scroll-ease': 'cubic-bezier(0.16,1,0.3,1)',
      /* Alternating section band: equals bg-deep in dark. */
      '--rv-band': '#06120B',
      '--rv-s1': 'rgba(52,211,153,0.03)',
      '--rv-s2': 'rgba(52,211,153,0.06)',
      '--rv-s3': 'rgba(52,211,153,0.10)',
      '--rv-border': 'rgba(52,211,153,0.15)',
      '--rv-border-hover': 'rgba(52,211,153,0.28)',
      /* Read by the Remix brand object (glass panels), not by the marketing pages. */
      '--rv-glass-dim': 'rgba(10,26,16,0.6)',
      /* 16.88:1 on #0A1A10. */
      /* Fixed FAQ tab wash; decorative contrast on dark/light canvas is 1.13:1 / 1.05:1. */
      '--rv-pricing-faq-ease': 'cubic-bezier(0.16,1,0.3,1)',
      '--rv-pricing-faq-selected-fill': 'rgba(52,211,153,0.07)',
      '--rv-text': '#F5F8FC',
      '--rv-text-sec': '#AEBACB',
      '--rv-text-muted': '#8C99A9',
      '--rv-text-dim': '#516175',
      '--rv-accent-bright': '#5EEAD4',
      '--rv-accent-glow': 'rgba(52,211,153,0.40)',
      /* Ink on a solid emerald fill (CTA buttons): the brand gate value, not the page bg. */
      '--rv-accent-text': '#06281c',
      '--rv-cyan': '#22D3EE',
      /* Fixed values of the demo kit rail (PR 4207): ink on the emerald CTA, and footer wash. */
      '--rv-cta-ink': '#06281c',
      '--rv-kit-foot-wash': 'rgba(52,211,153,0.045)',
      /* Full-screen fill (splash, device screens): equals base dark canvas. */
      '--rv-screen-bg':
        'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
      /* Button variant="marketing-cta" (CTAButton primary), verbatim from rv.jsx: emerald glow, hover glow, sheen. Same in
         both themes. Later marketing component roles go below this group. */
      '--rv-cta-shadow': '0 10px 30px rgba(52,211,153,0.32)',
      '--rv-cta-hover-shadow': '0 22px 48px rgba(52,211,153,0.48)',
      '--rv-cta-sheen': 'rgba(255,255,255,0.26)',
      /* Card variant="marketing" lift (.rv-card hover), verbatim from rv.jsx. Same in both themes. */
      '--rv-card-lift-border': 'rgba(52,211,153,0.3)',
      '--rv-card-lift-shadow': '0 12px 34px rgba(52,211,153,0.16)',
      /* Shipped demo dialog controls. Hover close ink contrast: 12.12:1 dark, 15.40:1 light. */
      '--rv-dialog-chip-fill': 'rgba(0,0,0,0.25)',
      /* Exact idle fill for Contact mode choices. Secondary text contrast: 9.64:1 on the Contact panel. */
      '--rv-contact-mode-fill': 'rgba(0,0,0,0.25)',
      /* Contact-only motion keeps the shipped 180ms curve local. */
      '--rv-contact-mode-ease': dark.ease,
      /* Shipped resource input fill and font. */
      '--rv-resource-field-fill': 'rgba(6,12,11,0.6)',
      '--rv-resource-field-font': marketingFonts.font,
      /* Shipped dispatch fills and local font. Focus keeps the fixed emerald tint in both themes. */
      '--rv-dispatch-field-fill': 'rgba(0,0,0,0.25)',
      '--rv-dispatch-field-focus-fill': 'rgba(52,211,153,0.04)',
      '--rv-dispatch-field-font': marketingFonts.font,
      /* Dispatch status ink (reply ETA line and SENT badge): the brand amber, as shipped. 8.16:1 on the line wash and
         5.82:1 on the badge wash of the ticket. */
      '--rv-dispatch-status-ink': '#F59E0B',
      '--rv-dialog-cta-hover-shadow': '0 22px 48px rgba(52,211,153,0.46)',
      '--rv-dialog-chip-hover-edge': 'rgba(52,211,153,0.5)',
      '--rv-dialog-close-hover-fill': 'rgba(52,211,153,0.10)',
      '--rv-dialog-close-hover-edge': 'rgba(52,211,153,0.4)',
      '--rv-dialog-hover-ink': '#E2E8F0',
      /* Hardware paints, copied from DeviceFrame. Status text uses fixed screen palettes.
         Fixed screen palettes follow the status prop, independently of the page theme. */
      '--rv-device-rim': 'linear-gradient(180deg, #6B7079 0%, #3A3E45 14%, #25282D 60%, #1B1D21 100%)',
      '--rv-device-rim-side': 'linear-gradient(135deg, #5E636B 0%, #2A2D32 22%, #3A3D43 50%, #1C1E22 76%, #4A4E55 100%)',
      '--rv-device-deck': 'linear-gradient(180deg, #555A62 0%, #34373D 30%, #202226 72%, #111214 100%)',
      '--rv-device-deck-edge': 'rgba(255,255,255,0.22)',
      '--rv-device-scoop': 'linear-gradient(180deg, #16181B, #2C2F34)',
      '--rv-device-button': 'linear-gradient(180deg, #4A4E55, #1E2024)',
      '--rv-device-ground': 'rgba(0,0,0,0.62)',
      '--rv-device-ground-soft': 'rgba(0,0,0,0.38)',
      '--rv-device-lens': 'radial-gradient(circle at 35% 35%, #3B4A5C, #0B0E12 70%)',
      '--rv-device-lens-rim': '0 0 0 1.5px #121418',
      '--rv-device-glow': 'rgba(52,211,153,0.22)',
      '--rv-device-black': '#000',
      '--rv-device-glass': '#07080A',
      '--rv-device-glass-rim': 'inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.10)',
      '--rv-device-scoop-shadow': 'inset 0 1px 2px rgba(0,0,0,0.35)',
      '--rv-device-phone-rim': 'inset 0 0 0 1px rgba(255,255,255,0.06)',
      '--rv-device-notch-rim': 'inset 0 0 0 1px rgba(255,255,255,0.05)',
      /* Status text: 16.30:1 on its fixed dark fill, 18.72:1 on its fixed light fill. */
      '--rv-device-status-dark-ink': '#E8EEF0',
      '--rv-device-status-light-ink': '#0B1220',
      '--rv-device-status-dark-fill': '#06120B',
      '--rv-device-status-light-fill': '#FFFFFF',
      '--rv-device-hinge': 'linear-gradient(180deg, #0C0D0F, #2A2D32)',
      '--rv-device-feet': '#0A0B0C',
      '--rv-device-deck-shadow': 'rgba(0,0,0,0.25)',
      '--rv-device-glare': 'linear-gradient(112deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.035) 26%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.04) 100%)',
      /* Shipped theme selector elevation. Decorative edge and shadow. */
      '--rv-theme-selected-shadow': '0 1px 2px rgba(0,0,0,0.18), inset 0 0 0 1px var(--rv-border-hover)',
    },
    light: {
      /* Native sample actions: neutral ink is 18.72:1 on its sample card; primary ink is 5.48:1 on accent. Edge is decorative. */
      '--rv-sample-action-font': '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif',
      '--rv-sample-action-ink': '#0B1220',
      '--rv-sample-action-edge': 'rgba(15,23,42,0.10)',
      '--rv-sample-action-on-accent': '#FFFFFF',
      '--rv-sample-action-ease': 'cubic-bezier(0.16,1,0.3,1)',
      '--rv-bg-deep': '#F1F6F3',
      /* Exact public BackToTop glass fill in light. */
      '--rv-marketing-scroll-fill': 'rgba(255,255,255,0.9)',
      '--rv-marketing-scroll-ease': 'cubic-bezier(0.16,1,0.3,1)',
      /* A clearer tint than bg-deep so long pages keep their section rhythm on a white page. */
      '--rv-band': '#EAF2EC',
      '--rv-s1': 'rgba(15,23,42,0.025)',
      '--rv-s2': 'rgba(15,23,42,0.045)',
      '--rv-s3': 'rgba(15,23,42,0.07)',
      '--rv-border': 'rgba(15,23,42,0.10)',
      '--rv-border-hover': 'rgba(15,23,42,0.18)',
      '--rv-glass-dim': 'rgba(250,251,252,0.72)',
      /* 16.46:1 on #FAFBFC. */
      /* Fixed FAQ tab wash; decorative contrast on dark/light canvas is 1.13:1 / 1.05:1. */
      '--rv-pricing-faq-ease': 'cubic-bezier(0.16,1,0.3,1)',
      '--rv-pricing-faq-selected-fill': 'rgba(52,211,153,0.07)',
      '--rv-text': '#1A1A2E',
      '--rv-text-sec': '#475569',
      '--rv-text-muted': '#64748B',
      '--rv-text-dim': '#94A3B8',
      '--rv-accent-bright': '#0F766E',
      '--rv-accent-glow': 'rgba(5,150,105,0.28)',
      '--rv-accent-text': '#FFFFFF',
      '--rv-cyan': '#0E7490',
      /* Same fixed values as dark except the footer wash, which is the slate wash it shipped. */
      '--rv-cta-ink': '#06281c',
      '--rv-kit-foot-wash': 'rgba(15,23,42,0.03)',
      '--rv-screen-bg':
        'radial-gradient(58% 55% at 50% 30%, rgba(52,211,153,0.16) 0%, rgba(52,211,153,0.05) 46%, rgba(250,251,252,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.07) 0%, rgba(250,251,252,0) 60%), linear-gradient(160deg, #EEF7F2 0%, #FAFBFC 55%, #EDF6F0 100%)',
      /* Button variant="marketing-cta" (CTAButton primary), verbatim from rv.jsx: emerald glow, hover glow, sheen. Same in
         both themes. Later marketing component roles go below this group. */
      '--rv-cta-shadow': '0 10px 30px rgba(52,211,153,0.32)',
      '--rv-cta-hover-shadow': '0 22px 48px rgba(52,211,153,0.48)',
      '--rv-cta-sheen': 'rgba(255,255,255,0.26)',
      /* Card variant="marketing" lift (.rv-card hover), verbatim from rv.jsx. Same in both themes. */
      '--rv-card-lift-border': 'rgba(52,211,153,0.3)',
      '--rv-card-lift-shadow': '0 12px 34px rgba(52,211,153,0.16)',
      /* Shipped demo dialog controls. Hover close ink contrast: 12.12:1 dark, 15.40:1 light. */
      '--rv-dialog-chip-fill': 'rgba(15,23,42,0.04)',
      /* Exact idle fill for Contact mode choices. Secondary text contrast: 6.00:1 on the Contact panel. */
      '--rv-contact-mode-fill': 'rgba(15,23,42,0.04)',
      /* Contact-only motion keeps the shipped 180ms curve local. */
      '--rv-contact-mode-ease': light.ease,
      /* Shipped resource input fill and font. */
      '--rv-resource-field-fill': '#FFFFFF',
      '--rv-resource-field-font': marketingFonts.font,
      /* Shipped dispatch fills and local font. Focus keeps the fixed emerald tint in both themes. */
      '--rv-dispatch-field-fill': '#FFFFFF',
      '--rv-dispatch-field-focus-fill': 'rgba(52,211,153,0.04)',
      '--rv-dispatch-field-font': marketingFonts.font,
      /* Dispatch status ink: the light amber deepened (issue 2597). The page amber #B45309 read 4.07:1 on the fixed amber
         badge wash of the ticket and 3.90:1 on the note form badge. #9F4908 reads 4.95:1 and 4.74:1 on the badges and
         5.61:1 and 5.42:1 on the ETA lines. */
      '--rv-dispatch-status-ink': '#9F4908',
      '--rv-dialog-cta-hover-shadow': '0 22px 48px rgba(52,211,153,0.46)',
      '--rv-dialog-chip-hover-edge': 'rgba(52,211,153,0.5)',
      '--rv-dialog-close-hover-fill': 'rgba(52,211,153,0.10)',
      '--rv-dialog-close-hover-edge': 'rgba(52,211,153,0.4)',
      '--rv-dialog-hover-ink': '#1A1A2E',
      /* Hardware paints, copied from DeviceFrame. Status text uses fixed screen palettes.
         Fixed screen palettes follow the status prop, independently of the page theme. */
      '--rv-device-rim': 'linear-gradient(180deg, #F3F4F6 0%, #D7DADF 16%, #BFC3C9 62%, #A9AEB5 100%)',
      '--rv-device-rim-side': 'linear-gradient(135deg, #F5F6F8 0%, #C9CDD3 22%, #E2E5E9 50%, #B3B8BF 76%, #EDEFF2 100%)',
      '--rv-device-deck': 'linear-gradient(180deg, #F4F5F7 0%, #DCDFE3 30%, #BEC2C8 74%, #9AA0A7 100%)',
      '--rv-device-deck-edge': 'rgba(255,255,255,0.95)',
      '--rv-device-scoop': 'linear-gradient(180deg, #A3A8AF, #C9CDD2)',
      '--rv-device-button': 'linear-gradient(180deg, #D9DCE0, #A6ABB2)',
      '--rv-device-ground': 'rgba(15,23,42,0.30)',
      '--rv-device-ground-soft': 'rgba(15,23,42,0.12)',
      '--rv-device-lens': 'radial-gradient(circle at 35% 35%, #3B4A5C, #0B0E12 70%)',
      '--rv-device-lens-rim': '0 0 0 1.5px #121418',
      '--rv-device-glow': 'rgba(52,211,153,0.22)',
      '--rv-device-black': '#000',
      '--rv-device-glass': '#07080A',
      '--rv-device-glass-rim': 'inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.10)',
      '--rv-device-scoop-shadow': 'inset 0 1px 2px rgba(0,0,0,0.35)',
      '--rv-device-phone-rim': 'inset 0 0 0 1px rgba(255,255,255,0.06)',
      '--rv-device-notch-rim': 'inset 0 0 0 1px rgba(255,255,255,0.05)',
      /* Status text: 16.30:1 on its fixed dark fill, 18.72:1 on its fixed light fill. */
      '--rv-device-status-dark-ink': '#E8EEF0',
      '--rv-device-status-light-ink': '#0B1220',
      '--rv-device-status-dark-fill': '#06120B',
      '--rv-device-status-light-fill': '#FFFFFF',
      '--rv-device-hinge': 'linear-gradient(180deg, #6E737A, #B3B8BE)',
      '--rv-device-feet': '#7D838A',
      '--rv-device-deck-shadow': 'rgba(0,0,0,0.25)',
      '--rv-device-glare': 'linear-gradient(112deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.035) 26%, rgba(255,255,255,0) 38%, rgba(255,255,255,0) 72%, rgba(255,255,255,0.04) 100%)',
      /* Shipped theme selector elevation. Decorative edge and shadow. */
      '--rv-theme-selected-shadow': '0 1px 2px rgba(0,0,0,0.18), inset 0 0 0 1px var(--rv-border-hover)',
    },
  },
};

/*
 * Remix tool (src/remix/RepVantageRemix.jsx on the marketing site, and the remix.repvantage.io site). Its brand
 * object (src/remix/brand.js) is base dark with the marketing fonts and roles, except this one muted ink. Dark only:
 * the tool screen is always dark.
 */
/* Fixed shared tool palette. Text contrast on tool bg: 14.58:1; secondary text: 7.01:1. */
const REMIX_SHARED_ROLES = Object.freeze({
  '--rv-remix-tool-bg': dark.bg,
  '--rv-remix-tool-edge': dark.separator,
  '--rv-remix-tool-ink': dark.label,
  '--rv-remix-tool-placeholder': dark.labelQuad,
  /* Preserve the old mounted tool rule for adjacent native host inputs. Core Remix fields own their paint. */
  '--rv-remix-host-placeholder': dark.labelQuad,
  '--rv-remix-tool-font': marketingFonts.font,
  '--rv-remix-tool-mono': marketingFonts.mono,
  '--rv-remix-tool-focus-edge': 'rgba(52,211,153,0.55)',
  '--rv-remix-tool-focus-ring': 'rgba(52,211,153,0.13)',
  '--rv-remix-tool-ease': 'cubic-bezier(0.16, 1, 0.3, 1)',
});
const remix: ProductOverride = {
  dark: {
    ...marketingFonts,
    /* The tool has always shipped slate-500 for muted text (3.78:1 on #0A1A10); base #8494A8 is lighter. */
    labelTer: '#64748B',
  },
  // Standalone Remix site values remain fixed across the sun overlay. The app still owns its bootstrap.
  roles: {
    dark: {
      ...REMIX_SHARED_ROLES,
      "--rv-remix-site-screen": "radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)",
      "--rv-remix-site-bg": "#0A1A10",
      "--rv-remix-site-text": "#E2E8F0",
      "--rv-remix-site-text-sec": "#94A3B8",
      "--rv-remix-site-text-muted": "#94A3B8",
      "--rv-remix-site-border": "rgba(52,211,153,0.15)",
      "--rv-remix-site-border-strong": "rgba(52,211,153,0.28)",
      "--rv-remix-site-hair": "rgba(255,255,255,0.08)",
      "--rv-remix-site-glass": "rgba(52,211,153,0.03)",
      "--rv-remix-site-glass-2": "rgba(52,211,153,0.06)",
      "--rv-remix-site-card": "linear-gradient(160deg, rgba(12,30,21,0.86) 0%, rgba(9,18,16,0.86) 100%)",
      "--rv-remix-site-card-solid": "linear-gradient(160deg, rgba(12,30,21,0.97) 0%, rgba(9,18,16,0.98) 100%)",
      "--rv-remix-site-card-end": "rgba(9,18,16,0.92)",
      "--rv-remix-site-pop": "linear-gradient(160deg, #0E2218 0%, #0A1712 100%)",
      "--rv-remix-site-header": "rgba(10,26,16,0.84)",
      "--rv-remix-site-input": "rgba(9,18,16,0.85)",
      "--rv-remix-site-chip": "rgba(10,26,16,0.9)",
      "--rv-remix-site-scrim": "rgba(4,10,7,0.62)",
      "--rv-remix-site-shadow": "0 18px 44px rgba(0,0,0,0.32)",
      "--rv-remix-site-shadow-lg": "0 40px 100px rgba(0,0,0,0.5)",
      "--rv-remix-site-shadow-hover": "0 22px 50px rgba(0,0,0,0.5)",
      "--rv-remix-site-glow-op": "1",
      "--rv-remix-site-frame": "#0D1512",
      "--rv-remix-site-frame-bar": "rgba(9,16,13,0.95)",
      "--rv-remix-site-frame-url": "rgba(0,0,0,0.35)",
      "--rv-remix-site-rep": "#F0F0F0",
      "--rv-remix-site-focus": "#34d399",
      "--rv-remix-site-ink-accent": "#34d399",
      "--rv-remix-site-ink-amber": "#F59E0B",
      "--rv-remix-site-ink-cyan": "#22D3EE",
      "--rv-remix-site-ink-purple": "#C084FC",
      "--rv-remix-site-ink-red": "#EF4444",
      "--rv-remix-site-grad-a": "#34d399",
      "--rv-remix-site-grad-b": "#22D3EE",
      "--rv-remix-site-grad-warm-a": "#F59E0B",
      "--rv-remix-site-grad-warm-b": "#FBBF24",
      "--rv-remix-site-3d-line": "rgba(52,211,153,0.55)",
      "--rv-remix-site-3d-fill": "rgba(52,211,153,0.16)",
    },
    light: {
      ...REMIX_SHARED_ROLES,
      "--rv-remix-site-screen": "radial-gradient(60% 45% at 50% 0%, rgba(52,211,153,0.10) 0%, rgba(52,211,153,0) 70%), linear-gradient(180deg, #FAFBFC 0%, #FAFBFC 100%)",
      "--rv-remix-site-bg": "#FAFBFC",
      "--rv-remix-site-text": "#1A1A2E",
      "--rv-remix-site-text-sec": "#475569",
      "--rv-remix-site-text-muted": "#64748B",
      "--rv-remix-site-border": "rgba(0,0,0,0.08)",
      "--rv-remix-site-border-strong": "rgba(0,0,0,0.15)",
      "--rv-remix-site-hair": "rgba(0,0,0,0.08)",
      "--rv-remix-site-glass": "rgba(0,0,0,0.02)",
      "--rv-remix-site-glass-2": "rgba(0,0,0,0.04)",
      "--rv-remix-site-card": "linear-gradient(160deg, #FFFFFF 0%, #F8FAF9 100%)",
      "--rv-remix-site-card-solid": "linear-gradient(160deg, #FFFFFF 0%, #F8FAF9 100%)",
      "--rv-remix-site-card-end": "#FFFFFF",
      "--rv-remix-site-pop": "#FFFFFF",
      "--rv-remix-site-header": "rgba(250,251,252,0.88)",
      "--rv-remix-site-input": "#FFFFFF",
      "--rv-remix-site-chip": "rgba(255,255,255,0.94)",
      "--rv-remix-site-scrim": "rgba(15,23,42,0.36)",
      "--rv-remix-site-shadow": "0 1px 2px rgba(15,23,42,0.04), 0 10px 28px rgba(15,23,42,0.07)",
      "--rv-remix-site-shadow-lg": "0 2px 6px rgba(15,23,42,0.05), 0 28px 70px rgba(15,23,42,0.14)",
      "--rv-remix-site-shadow-hover": "0 2px 6px rgba(15,23,42,0.05), 0 20px 44px rgba(15,23,42,0.12)",
      "--rv-remix-site-glow-op": "0",
      "--rv-remix-site-frame": "#FFFFFF",
      "--rv-remix-site-frame-bar": "#F1F4F3",
      "--rv-remix-site-frame-url": "#FFFFFF",
      "--rv-remix-site-rep": "#111111",
      "--rv-remix-site-focus": "#047857",
      "--rv-remix-site-ink-accent": "#047857",
      "--rv-remix-site-ink-amber": "#B45309",
      "--rv-remix-site-ink-cyan": "#0E7490",
      "--rv-remix-site-ink-purple": "#7E22CE",
      "--rv-remix-site-ink-red": "#B91C1C",
      "--rv-remix-site-grad-a": "#047857",
      "--rv-remix-site-grad-b": "#0E7490",
      "--rv-remix-site-grad-warm-a": "#B45309",
      "--rv-remix-site-grad-warm-b": "#C2410C",
      "--rv-remix-site-3d-line": "rgba(4,120,87,0.45)",
      "--rv-remix-site-3d-fill": "rgba(52,211,153,0.14)",
    },
  },
};

/*
 * Wedge Brief V2 roles, verbatim from BV2_DARK and BV2_LIGHT in apps/wedge/src/prototype/prep-brief-v2-kit.jsx (the
 * prototype spreads them as --bv2-* on the V2 root). Read by the core variants "wedge-bv2" (Card, Pill, IconButton, Stat).
 * The hero-* group is BV2_HERO_TONE_VARS (the hero band is always dark, so it is the same in both themes) and the
 * stat-* group is the hero stat strip in prep-brief-v2.jsx (data-bv2-stats), also dark in both themes.
 */
/* Fixed splash roles. Reused in both product themes and before token CSS loads. */
export const wedgeShellRoles = Object.freeze({
  '--rv-wedge-shell-bg': '#0A1A10',
  '--rv-wedge-shell-text': '#E2E8F0',
  '--rv-wedge-shell-text-sec': '#94A3B8',
  '--rv-wedge-shell-text-muted': '#64748B',
  '--rv-wedge-shell-text-dim': '#475569',
  '--rv-wedge-shell-accent': '#34d399',
  '--rv-wedge-shell-accent-ink': '#06281c',
  '--rv-wedge-shell-amber': '#F59E0B',
  '--rv-wedge-shell-red': '#EF4444',
  '--rv-wedge-shell-red-text': '#FCA5A5',
  '--rv-wedge-shell-surface': 'rgba(52,211,153,0.05)',
  '--rv-wedge-shell-surface-strong': 'rgba(52,211,153,0.08)',
  '--rv-wedge-shell-border': 'rgba(52,211,153,0.18)',
  '--rv-wedge-shell-border-strong': 'rgba(52,211,153,0.34)',
  '--rv-wedge-shell-font-body': "'Jost', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  '--rv-wedge-shell-font-display': "'Orbitron', sans-serif",
  '--rv-wedge-shell-font-mono': "'Share Tech Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  '--rv-wedge-shell-ease': 'cubic-bezier(0.16, 1, 0.3, 1)',
  // Exact shipped control states. Shadows and borders are decorative; the busy fill marks a disabled control.
  '--rv-wedge-shell-cta-busy': 'rgba(52,211,153,0.42)',
  '--rv-wedge-shell-cta-shadow': '0 6px 18px rgba(52,211,153,0.28)',
  '--rv-wedge-shell-cta-hover-shadow': '0 10px 26px rgba(52,211,153,0.34)',
  '--rv-wedge-shell-glass-hover': 'rgba(52,211,153,0.13)',
  '--rv-wedge-shell-glass-hover-border': 'rgba(52,211,153,0.40)',
  // Released auth focus and recipient edges are decorative. Text keeps the fixed shell ink.
  '--rv-wedge-shell-field-focus-border': 'rgba(52,211,153,0.55)',
  '--rv-wedge-shell-field-focus-surface': 'rgba(52,211,153,0.08)',
  '--rv-wedge-shell-field-focus-shadow': '0 0 0 4px rgba(52,211,153,0.12)',
  '--rv-wedge-shell-recipient-border': 'rgba(255,255,255,0.18)',
  '--rv-wedge-shell-recipient-surface': 'rgba(255,255,255,0.06)',
  // Quiet glass retains the released ghost hover; decorative fill, fixed across themes.
  '--rv-wedge-shell-glass-quiet-hover': 'rgba(52,211,153,0.10)',
  // Released PIN input uses the native mono stack, without the shell display face.
  '--rv-wedge-shell-native-pin-font': 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  // Released Landing fields. Warning/focus edges are decorative; placeholder contrast is 5.33:1 on the field fill over the fixed shell background.
  '--rv-wedge-shell-field-warning-border': 'rgba(245,158,11,0.46)',
  '--rv-wedge-shell-magic-field-focus-shadow': '0 0 0 3px rgba(52,211,153,0.14)',
  '--rv-wedge-shell-magic-field-placeholder': '#8494A8',
  // Released provider paints. Edges/shadows are decorative; native white ink on black is 21:1.
  '--rv-wedge-shell-signin-black': '#000',
  '--rv-wedge-shell-signin-white': '#fff',
  '--rv-wedge-shell-signin-native-ink': '#1f2937',
  '--rv-wedge-shell-signin-google-ink': '#1f1f1f',
  '--rv-wedge-shell-signin-native-light-border': 'rgba(0,0,0,0.14)',
  '--rv-wedge-shell-signin-native-dark-border': 'rgba(255,255,255,0.22)',
  '--rv-wedge-shell-signin-shadow': '0 8px 22px rgba(0,0,0,0.30)',
  '--rv-wedge-shell-signin-hover-shadow': '0 10px 24px rgba(0,0,0,0.45)',
  '--rv-wedge-shell-signin-dark-hover': '#111',
  '--rv-wedge-shell-signin-light-hover': '#f4f4f4',
  '--rv-wedge-shell-signin-web-surface': 'rgba(255,255,255,0.04)',
  '--rv-wedge-shell-signin-web-border': 'rgba(255,255,255,0.10)',
  '--rv-wedge-shell-signin-apple-border': 'rgba(52,211,153,0.15)',
  // Released native account paint. Dark ink contrast is at least 6.24:1 across the solid gradient endpoints.
  "--rv-wedge-shell-account-gradient": "linear-gradient(180deg, #34D399, #10B981)",
  "--rv-wedge-shell-account-ink": "#06281A",
  "--rv-wedge-shell-account-shadow": "0 8px 26px rgba(52,211,153,0.28), 0 1px 0 rgba(255,255,255,0.35) inset",
  "--rv-wedge-shell-account-hover-gradient": "linear-gradient(180deg, #4ADE80, #22C58B)",
  "--rv-wedge-shell-account-hover-shadow": "0 12px 32px rgba(52,211,153,0.38), 0 1px 0 rgba(255,255,255,0.35) inset",
  "--rv-wedge-shell-signup-ink": "#FDE68A",
  "--rv-wedge-shell-signup-border": "rgba(245,158,11,0.5)",
  "--rv-wedge-shell-signup-fill": "linear-gradient(105deg, rgba(245,158,11,0.14), rgba(245,158,11,0.05))",
  "--rv-wedge-shell-signup-pulse-shadow": "0 0 0 1px rgba(245,158,11,0.35), 0 0 16px rgba(245,158,11,0.16)",
  "--rv-wedge-shell-signup-pulse-peak-shadow": "0 0 0 1px rgba(251,191,36,0.7), 0 0 30px rgba(245,158,11,0.42)",
  "--rv-wedge-shell-signup-pulse-peak-border": "rgba(251,191,36,0.85)",
  // Released orphan outline and fill are decorative. Text keeps the fixed shell ink.
  '--rv-wedge-shell-alternate-border': 'rgba(52,211,153,0.30)',
  '--rv-wedge-shell-alternate-surface': 'rgba(52,211,153,0.07)',
  // Released email paint. Warning ink is 8.86:1 on its fill over shell bg; change-address ink is 12.11:1 on shell bg.
  '--rv-wedge-shell-warning-control-ink': '#FFD380',
  '--rv-wedge-shell-warning-control-surface': 'rgba(245,158,11,0.20)',
  '--rv-wedge-shell-domain-surface': 'rgba(52,211,153,0.06)',
  '--rv-wedge-shell-domain-hover-surface': 'rgba(52,211,153,0.18)',
  '--rv-wedge-shell-domain-hover-border': 'rgba(52,211,153,0.42)',
  '--rv-wedge-shell-resend-surface': 'rgba(52,211,153,0.16)',
  '--rv-wedge-shell-resend-border': 'rgba(52,211,153,0.36)',
  '--rv-wedge-shell-resend-muted-border': 'rgba(52,211,153,0.14)',
  '--rv-wedge-shell-email-change-ink': '#CBD5E1',
  // Released native warning controls. Dark ink is 8.39:1 on the fixed amber fill; the border is decorative.
  '--rv-wedge-shell-warning-control-dark-ink': '#231303',
  '--rv-wedge-shell-warning-control-border': 'rgba(245,158,11,0.45)',
  '--rv-wedge-shell-screen-bg': 'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
  /* Released feedback fills, edges and shadow. Existing fixed inks retain their contrast. */
  '--rv-wedge-shell-field-error-surface': 'rgba(239,68,68,0.08)',
  '--rv-wedge-shell-field-error-border': 'rgba(239,68,68,0.22)',
  '--rv-wedge-shell-verify-warning-tint': 'rgba(245,158,11,0.16)',
  '--rv-wedge-shell-verify-success-border': 'rgba(52,211,153,0.5)',
  '--rv-wedge-shell-verify-shadow': '0 8px 24px rgba(0,0,0,0.35)',
  '--rv-wedge-shell-in-app-notice-surface': 'rgba(245,158,11,0.12)',
  '--rv-wedge-shell-in-app-notice-border': 'rgba(245,158,11,0.40)',
  /* Shared native form error fill/edge. redText contrast is 8.30:1 on this fill over the fixed auth background. */
  '--rv-wedge-shell-form-error-surface': 'rgba(252,165,165,0.08)',
  '--rv-wedge-shell-form-error-border': 'rgba(252,165,165,0.20)',
  /* Released top/offline warning edge. Existing inAppNoticeSurface and warningControlInk retain its text contrast. */
  '--rv-wedge-shell-compact-warning-border': 'rgba(245,158,11,0.36)',
  /* Released native feedback inks. Contrast on fixed shell bg: soft error 10.65:1; password error 6.50:1. */
  '--rv-wedge-shell-soft-error-ink': '#FFB4B4',
  '--rv-wedge-shell-password-error-ink': '#F87171',
});

/* One shared alpha ladder for the Wedge shell: black, white and brand emerald, mint, amber, slate, red and field-green
 * borders, washes and glows. Each step holds the exact alpha a shell file shipped with; nothing is rounded. The step name
 * is the colour plus the alpha in hundredths (emerald16 is rgba(52,211,153,0.16)). Extend this block; do not add a second. */
export const wedgeAlphaLadder = Object.freeze({
  emerald00: 'rgba(52,211,153,0)',
  emerald04: 'rgba(52,211,153,0.04)',
  emerald05: 'rgba(52,211,153,0.05)',
  emerald06: 'rgba(52,211,153,0.06)',
  emerald07: 'rgba(52,211,153,0.07)',
  emerald08: 'rgba(52,211,153,0.08)',
  emerald09: 'rgba(52,211,153,0.09)',
  emerald10: 'rgba(52,211,153,0.10)',
  emerald12: 'rgba(52,211,153,0.12)',
  emerald14: 'rgba(52,211,153,0.14)',
  emerald15: 'rgba(52,211,153,0.15)',
  emerald16: 'rgba(52,211,153,0.16)',
  emerald18: 'rgba(52,211,153,0.18)',
  emerald20: 'rgba(52,211,153,0.20)',
  emerald22: 'rgba(52,211,153,0.22)',
  emerald25: 'rgba(52,211,153,0.25)',
  emerald28: 'rgba(52,211,153,0.28)',
  emerald30: 'rgba(52,211,153,0.30)',
  emerald32: 'rgba(52,211,153,0.32)',
  emerald35: 'rgba(52,211,153,0.35)',
  emerald36: 'rgba(52,211,153,0.36)',
  emerald40: 'rgba(52,211,153,0.4)',
  emerald42: 'rgba(52,211,153,0.42)',
  emerald45: 'rgba(52,211,153,0.45)',
  emerald46: 'rgba(52,211,153,0.46)',
  emerald50: 'rgba(52,211,153,0.5)',
  emerald55: 'rgba(52,211,153,0.55)',
  emerald60: 'rgba(52,211,153,0.6)',
  emerald70: 'rgba(52,211,153,0.7)',
  emerald75: 'rgba(52,211,153,0.75)',
  emerald90: 'rgba(52,211,153,0.9)',
  emerald65: 'rgba(52,211,153,0.65)',
  emerald85: 'rgba(52,211,153,0.85)',
  emerald95: 'rgba(52,211,153,0.95)',
  mint40: 'rgba(110,231,183,0.4)',
  mint50: 'rgba(110,231,183,0.5)',
  mint55: 'rgba(110,231,183,0.55)',
  mint90: 'rgba(110,231,183,0.9)',
  amber03: 'rgba(245,158,11,0.03)',
  amber06: 'rgba(245,158,11,0.06)',
  amber07: 'rgba(245,158,11,0.07)',
  amber08: 'rgba(245,158,11,0.08)',
  amber10: 'rgba(245,158,11,0.10)',
  amber22: 'rgba(245,158,11,0.22)',
  amber28: 'rgba(245,158,11,0.28)',
  amber18: 'rgba(245,158,11,0.18)',
  amber30: 'rgba(245,158,11,0.30)',
  amber32: 'rgba(245,158,11,0.32)',
  amber35: 'rgba(245,158,11,0.35)',
  amber36: 'rgba(245,158,11,0.36)',
  amber40: 'rgba(245,158,11,0.4)',
  amber70: 'rgba(245,158,11,0.7)',
  amber90: 'rgba(245,158,11,0.9)',
  amber95: 'rgba(245,158,11,0.95)',
  black00: 'rgba(0,0,0,0)',
  black06: 'rgba(0,0,0,0.06)',
  black14: 'rgba(0,0,0,0.14)',
  black18: 'rgba(0,0,0,0.18)',
  black20: 'rgba(0,0,0,0.2)',
  black22: 'rgba(0,0,0,0.22)',
  black25: 'rgba(0,0,0,0.25)',
  black28: 'rgba(0,0,0,0.28)',
  black30: 'rgba(0,0,0,0.3)',
  black35: 'rgba(0,0,0,0.35)',
  black40: 'rgba(0,0,0,0.4)',
  black45: 'rgba(0,0,0,0.45)',
  black50: 'rgba(0,0,0,0.5)',
  black55: 'rgba(0,0,0,0.55)',
  black60: 'rgba(0,0,0,0.6)',
  black80: 'rgba(0,0,0,0.8)',
  black10: 'rgba(0,0,0,0.1)',
  black16: 'rgba(0,0,0,0.16)',
  white05: 'rgba(255,255,255,0.05)',
  white12: 'rgba(255,255,255,0.12)',
  white18: 'rgba(255,255,255,0.18)',
  white22: 'rgba(255,255,255,0.22)',
  white35: 'rgba(255,255,255,0.35)',
  white45: 'rgba(255,255,255,0.45)',
  white50: 'rgba(255,255,255,0.5)',
  white55: 'rgba(255,255,255,0.55)',
  white70: 'rgba(255,255,255,0.7)',
  white04: 'rgba(255,255,255,0.04)',
  white06: 'rgba(255,255,255,0.06)',
  white08: 'rgba(255,255,255,0.08)',
  white16: 'rgba(255,255,255,0.16)',
  white65: 'rgba(255,255,255,0.65)',
  white72: 'rgba(255,255,255,0.72)',
  white78: 'rgba(255,255,255,0.78)',
  white96: 'rgba(255,255,255,0.96)',
  slate10: 'rgba(148,163,184,0.10)',
  slate28: 'rgba(148,163,184,0.28)',
  slate30: 'rgba(148,163,184,0.3)',
  red10: 'rgba(248,113,113,0.10)',
  red35: 'rgba(248,113,113,0.35)',
  field00: 'rgba(10,26,16,0)',
  amber14: 'rgba(245,158,11,0.14)',
  cyan14: 'rgba(34,211,238,0.14)',
  violet16: 'rgba(139,92,246,0.16)',
  slate16: 'rgba(148,163,184,0.16)',
  field82: 'rgba(10,26,16,0.82)',
  /* wedgeStyles.ts steps (stylesheet shadows, washes, map popups). gray = iOS label gray 60,67. */
  emerald24: 'rgba(52,211,153,0.24)',
  amber00: 'rgba(245,158,11,0)',
  amber45: 'rgba(245,158,11,0.45)',
  amber55: 'rgba(245,158,11,0.55)',
  amber65: 'rgba(245,158,11,0.65)',
  black34: 'rgba(0,0,0,0.34)',
  white03: 'rgba(255,255,255,0.03)',
  white07: 'rgba(255,255,255,0.07)',
  white10: 'rgba(255,255,255,0.10)',
  white14: 'rgba(255,255,255,0.14)',
  white38: 'rgba(255,255,255,0.38)',
  white40: 'rgba(255,255,255,0.4)',
  slate06: 'rgba(148,163,184,0.06)',
  gray07: 'rgba(60,60,67,0.07)',
  gray10: 'rgba(60,60,67,0.10)',
  gray12: 'rgba(60,60,67,0.12)',
  gray18: 'rgba(60,60,67,0.18)',
  gray22: 'rgba(60,60,67,0.22)',
} as const);

export const wedgeAlphaRoles = Object.freeze(Object.fromEntries(
  Object.entries(wedgeAlphaLadder).map(([k, v]) => ['--rv-wedge-alpha-' + k, v]),
) as Record<string, string>);

/* Fixed map pin popup and tooltip inks and surfaces (wedgeStyles.ts .rv-pin-*, .rv-hub-*). Light card, dark card and
   the over-map dark card each keep their own exact paint. Same value in every theme. */
export const wedgeMapPopupRoles = Object.freeze({
  '--rv-wedge-map-popup-tone-fallback': '#0a7ea4',
  '--rv-wedge-map-popup-ink': '#12241b',
  '--rv-wedge-map-popup-deep-ink': '#0b1f16',
  '--rv-wedge-map-popup-muted-ink': '#5c6b63',
  '--rv-wedge-map-popup-faint-ink': '#8a968f',
  '--rv-wedge-map-popup-value-ink': '#10241a',
  '--rv-wedge-map-popup-addr-ink': '#3c4a43',
  '--rv-wedge-map-popup-axis-ink': '#4a5a52',
  '--rv-wedge-map-popup-dim-ink': '#6b7a72',
  '--rv-wedge-map-popup-approx-ink': '#c2410c',
  '--rv-wedge-map-popup-off-ink': '#9aa69f',
  '--rv-wedge-map-popup-link-ink': '#0a66c2',
  '--rv-wedge-map-popup-surface': '#ffffff',
  '--rv-wedge-map-popup-on-tone-light-ink': '#ffffff',
  '--rv-wedge-map-popup-on-tone-ink': '#06170f',
  '--rv-wedge-map-popup-dark-surface': '#0e2c1e',
  '--rv-wedge-map-popup-dark-ink': '#e8f0eb',
  '--rv-wedge-map-popup-dark-name-ink': '#eaf2ec',
  '--rv-wedge-map-popup-dark-dim-ink': '#9fb0a6',
  '--rv-wedge-map-popup-dark-addr-ink': '#b8c7be',
  '--rv-wedge-map-popup-dark-button-ink': '#d7e5db',
  '--rv-wedge-map-popup-dark-off-ink': '#7e9488',
  '--rv-wedge-map-popup-dark-link-ink': '#7db8f0',
  '--rv-wedge-map-popup-map-ink': '#eef4f1',
  '--rv-wedge-map-popup-map-name-ink': '#ffffff',
  '--rv-wedge-map-popup-map-dim-ink': '#b9c7c0',
  '--rv-wedge-map-popup-map-key-ink': '#8ea79a',
  '--rv-wedge-map-popup-map-tone': '#34d399',
} as const);

/* Fixed Landing, magic-link redeem and lite-route paints (solid inks and fills). Same value in every theme. */
export const wedgeLandingRoles = Object.freeze({
  '--rv-wedge-landing-headline-ink': '#F4F5F7',
  '--rv-wedge-landing-mint-ink': '#6EE7B7',
  '--rv-wedge-landing-slate-ink': '#CBD5E1',
  '--rv-wedge-landing-mint-wash-ink': '#ECFDF5',
  '--rv-wedge-landing-autofill-fill': '#0d1a13',
  '--rv-wedge-landing-amber-note-ink': '#FFDBA8',
  '--rv-wedge-landing-suggestion-active-ink': '#A0F0CC',
  '--rv-wedge-landing-success-notice-ink': '#A7F3D0',
  '--rv-wedge-lite-bg': '#09180f',
  '--rv-wedge-lite-card': '#10241a',
  '--rv-wedge-lite-line': '#1f3a2b',
  '--rv-wedge-lite-text': '#F4F4F6',
  '--rv-wedge-lite-dim': '#9fb3a8',
  '--rv-wedge-lite-danger': '#f87171',
});

/* Fixed shared-component paints (prep-components): the exact literals the shared sheet header, the radar splash and the
 * theme preview shipped. Same value in every theme; names say which side paints them. */
export const wedgeSharedRoles = Object.freeze({
  '--rv-wedge-shared-emerald': '#34D399',
  '--rv-wedge-shared-ink-white': '#fff',
  '--rv-wedge-shared-preview-light-bg': '#FAFBFC',
  '--rv-wedge-shared-preview-dark-bg': '#0A1A10',
  '--rv-wedge-shared-radar-ground-top': '#0F2E22',
  '--rv-wedge-shared-radar-ground-mid': '#091210',
  '--rv-wedge-shared-radar-ground-bottom': '#0D1B11',
  '--rv-wedge-shared-radar-cta-wash-end': 'rgba(16,185,129,0.32)',
  '--rv-wedge-shared-radar-cta-border-bottom': 'rgba(0,135,90,0.6)',
  '--rv-wedge-shared-radar-option-idle-fill': 'rgba(255,255,255,0.025)',
  '--rv-wedge-shared-sheet-light-top': '#FFFFFF',
  '--rv-wedge-shared-sheet-light-bottom': '#F3F7F6',
  '--rv-wedge-shared-sheet-dark-top': '#0C1E15',
  '--rv-wedge-shared-sheet-dark-bottom': '#0A1A12',
  '--rv-wedge-shared-sheet-header-ink-light': '#101914',
  '--rv-wedge-shared-sheet-header-ink-dark': '#EAF2EC',
});

/* Fixed Home paints (hero card, Field Sheet tour, territory card, app scrim): the exact literals the prototype shipped.
 * Same value in every theme; light-only and dark-only names say which side paints them. Decorative washes and borders
 * carry no text contrast role; the inks (amber, cyan, lavender) sit on the hero or card surface they shipped on. */
export const wedgeHomeRoles = Object.freeze({
  '--rv-wedge-home-hero-light-frame': 'rgba(31,122,85,0.55)',
  '--rv-wedge-home-hero-light-edge-ink': '#1f7a55',
  '--rv-wedge-home-hero-light-nbm-border': 'rgba(31,122,85,0.2)',
  '--rv-wedge-home-hero-light-search-shadow': 'rgba(31,122,85,0.4)',
  '--rv-wedge-home-hero-light-scan-wash': 'rgba(31,122,85,0.08)',
  '--rv-wedge-home-hero-body-top': '#13301F',
  '--rv-wedge-home-hero-search-top': '#0B2317',
  '--rv-wedge-home-hero-light-mist': '#F1FAF6',
  '--rv-wedge-home-hero-amber-ink': '#FCD34D',
  '--rv-wedge-home-hero-cyan-ink': '#67E8F9',
  '--rv-wedge-home-hero-cyan-icon-wash': 'rgba(34,211,238,0.12)',
  '--rv-wedge-home-hero-cyan-icon-border': 'rgba(34,211,238,0.35)',
  '--rv-wedge-home-hero-light-divider': 'rgba(71,85,105,0.30)',
  '--rv-wedge-home-hero-light-stat-border': 'rgba(0,135,90,0.20)',
  '--rv-wedge-home-hero-light-stat-rule': 'rgba(0,135,90,0.16)',
  '--rv-wedge-home-hero-light-xp-border': 'rgba(0,135,90,0.30)',
  '--rv-wedge-home-hero-light-action-border': 'rgba(0,135,90,0.45)',
  '--rv-wedge-home-hero-meter-flat-on-light': '#6FCF97',
  '--rv-wedge-home-hero-meter-off-light': 'rgba(91,107,123,0.18)',
  '--rv-wedge-home-hero-xp-card-base': '#0E2418',
  '--rv-wedge-home-hero-cta-top': '#45E0A6',
  '--rv-wedge-home-app-scrim-base': 'rgba(8,18,12,0.66)',
  '--rv-wedge-home-tour-light-shadow': 'rgba(17,24,39,.22)',
  '--rv-wedge-home-angle-wash-strong': 'rgba(192,132,252,0.18)',
  '--rv-wedge-home-angle-wash-soft': 'rgba(192,132,252,0.05)',
  '--rv-wedge-home-angle-border': 'rgba(192,132,252,0.40)',
  '--rv-wedge-home-angle-glow': 'rgba(192,132,252,0.45)',
  '--rv-wedge-home-angle-edit-border': 'rgba(192,132,252,0.35)',
  '--rv-wedge-home-angle-edit-wash': 'rgba(192,132,252,0.12)',
  '--rv-wedge-home-angle-label-ink': '#D8B4FE',
});

export const wedgeNoticeRoles = Object.freeze({
  "--rv-wedge-notice-offline-bg": "#7F1D1D",
  "--rv-wedge-notice-offline-text": "#FFE2E2",
  "--rv-wedge-notice-offline-shadow": "0 4px 16px rgba(0,0,0,0.45)",
  "--rv-wedge-notice-wordmark": "#F0F0F0",
  "--rv-wedge-notice-share-card": "rgba(52,211,153,0.04)",
  "--rv-wedge-notice-share-card-border": "1px solid rgba(52,211,153,0.15)",
  "--rv-wedge-notice-share-card-shadow": "0 4px 40px rgba(0,0,0,0.4)",
  "--rv-wedge-notice-share-tagline": "rgba(255,255,255,0.5)",
  "--rv-wedge-notice-share-subtext": "rgba(255,255,255,0.55)",
  "--rv-wedge-notice-share-manual": "rgba(245,158,11,0.06)",
  "--rv-wedge-notice-share-manual-border": "1px solid rgba(245,158,11,0.25)",
  "--rv-wedge-notice-share-button": "#00875A",
  "--rv-wedge-notice-share-button-text": "#fff",
  "--rv-wedge-notice-share-button-shadow": "0 6px 18px rgba(0,135,90,0.25)",
  "--rv-wedge-notice-share-footer-border": "1px solid rgba(255,255,255,0.06)",
  "--rv-wedge-notice-share-footer-text": "rgba(255,255,255,0.30)",
  "--rv-wedge-notice-share-font": "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, 'Helvetica Neue', system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  "--rv-wedge-notice-share-mono": "'SF Mono', ui-monospace, 'Cascadia Mono', Menlo, monospace",
  "--rv-wedge-notice-share-footer-mono": "'SF Mono', ui-monospace, Menlo, monospace",
});

/* Fixed Wedge status bar and offline banner shadows (next views). Exact values of the released inline shadows; decorative, no text contrast role. */
export const wedgeNextStatusRoles = Object.freeze({
  '--rv-wedge-next-status-chip-shadow': '0 8px 20px rgba(0,0,0,0.2)',
  '--rv-wedge-next-status-bar-shadow': '0 10px 28px rgba(0,0,0,0.22)',
  '--rv-wedge-next-offline-banner-shadow': '0 4px 12px rgba(0,0,0,0.12)',
});

/* Fixed paints of Settings and its subscreens that no theme role or ladder step holds. Exact values of the released
 * inline literals; the same in every host theme. Washes and glows are decorative with no text contrast role; rankNumInk
 * is an ink that keeps the fixed fill it shipped on. */
export const wedgeSettingsRoles = Object.freeze({
  '--rv-wedge-settings-mail-cta-shine': 'rgba(255,255,255,0.38)',
  '--rv-wedge-settings-regen-hover-ring': 'rgba(16,185,129,0.22)',
  '--rv-wedge-settings-regen-hover-glow': 'rgba(4,120,87,0.38)',
  '--rv-wedge-settings-amber-deep-glow': 'rgba(178,94,9,0.5)',
  '--rv-wedge-settings-amber-deep-halo': 'rgba(178,94,9,0.06)',
  '--rv-wedge-settings-rank-num-ink': '#1A1026',
});

/* Fixed source fallbacks for NotifPrefs actions. Local legacy roles still take precedence. */
export const wedgeSettingsActionRoles = Object.freeze({
  // Released Phone sheet CTA shadow; decorative, with no text contrast role.
  '--rv-wedge-settings-action-sheet-shadow': '0 6px 18px rgba(0,0,0,0.18)',
  '--rv-wedge-settings-action-accent-fallback': 'rgba(52, 211, 153, 0.10)',
  '--rv-wedge-settings-action-red-fallback': '#EF4444',
  // Released Phone banner shadows and promise strip ink; decorative paint. White on the accent strip matches the shipped strip.
  '--rv-wedge-settings-action-stale-pill-shadow': '0 8px 22px rgba(0,0,0,0.35)',
  '--rv-wedge-settings-action-promise-shadow': '0 1px 4px rgba(0,0,0,0.18)',
  '--rv-wedge-settings-action-promise-ink': '#fff',
  // Released Phone guided tour paints (letterbox, vignette, card shadow) and sign-in boot paints; fixed in every theme, decorative.
  '--rv-wedge-settings-action-tour-letterbox': 'rgba(0,0,0,0.92)',
  '--rv-wedge-settings-action-tour-scrim': 'radial-gradient(120% 80% at 50% 22%, rgba(4,8,6,0.04) 0%, rgba(4,8,6,0.40) 55%, rgba(4,8,6,0.82) 100%)',
  '--rv-wedge-settings-action-tour-card-shadow': '0 40px 120px rgba(0,0,0,0.55)',
  '--rv-wedge-settings-action-boot-ground': '#0A1A10',
  '--rv-wedge-settings-action-boot-accent': '#34D399',
  '--rv-wedge-settings-action-boot-glow': '0 18px 36px rgba(52,211,153,0.32)',
  '--rv-wedge-settings-action-boot-dim': 'rgba(226,232,240,0.55)',
  // Released floating chrome paints (next meeting pill, command palette): fixed in every theme, decorative.
  '--rv-wedge-settings-action-meeting-chip-shadow': '0 8px 20px rgba(0,0,0,0.16)',
  '--rv-wedge-settings-action-meeting-card-shadow': '0 10px 28px rgba(0,0,0,0.14)',
  '--rv-wedge-settings-action-command-scrim': 'rgba(4,10,7,0.55)',
  '--rv-wedge-settings-action-command-shadow': '0 18px 48px rgba(0,0,0,0.4)',
  '--rv-wedge-settings-action-profile-cta-shadow': '0 10px 30px rgba(0,0,0,0.18)',
  '--rv-wedge-settings-action-profile-cta-ink': '#fff',
});

/* Released locked section overlay in the brief fold: a wash over the blurred content, then ink for the label, the quiet lines and the
   unlock pill. Light and dark are separate roles because the shipped rules switch on the Wedge theme attribute. */
export const wedgeBriefFoldRoles = Object.freeze({
  '--rv-wedge-brief-fold-wash-light': 'linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.86) 50%, rgba(255,255,255,0.96) 100%)',
  '--rv-wedge-brief-fold-wash-dark': 'linear-gradient(180deg, rgba(7,20,14,0.55) 0%, rgba(7,20,14,0.86) 50%, rgba(7,20,14,0.96) 100%)',
  '--rv-wedge-brief-fold-label-light': '#0F1A14',
  '--rv-wedge-brief-fold-label-dark': '#E2E8F0',
  '--rv-wedge-brief-fold-quiet-light': '#475569',
  '--rv-wedge-brief-fold-quiet-dark': '#94A3B8',
  '--rv-wedge-brief-fold-underline': '#94A3B8',
  '--rv-wedge-brief-fold-cta-light': '#0F1A14',
  '--rv-wedge-brief-fold-cta-ink-light': '#FFFFFF',
  '--rv-wedge-brief-fold-cta-ink-dark': '#0A1A10',
});

/* Released top bar wash of the app navbar. The bar stays creme in sun, so it cannot use the canvas bar token. Light and dark are separate roles
   because the controller picks one from the theme. */
export const wedgeAppNavbarRoles = Object.freeze({
  '--rv-wedge-app-navbar-bg-light': 'linear-gradient(180deg, #FFFFFF 0%, #F3F7F6 100%)',
  '--rv-wedge-app-navbar-bg-dark': 'linear-gradient(180deg, #0C1E15 0%, #0A1A12 100%)',
});

/* Released Privacy delete mark: white on red is 3.76:1, suitable for this non-text glyph. */
export const wedgePrivacyRoles = Object.freeze({
  '--rv-wedge-privacy-delete-glyph': '#fff',
});

/* Fixed miniature canvases in Appearance. Decorative bars carry no readable text.
   Values match the shipped Light and Dark previews in every host theme. */
export const wedgeAppearanceRoles = Object.freeze({
  '--rv-wedge-appearance-light-canvas': '#FAFBFC',
  '--rv-wedge-appearance-light-title': '#1D1D1F',
  '--rv-wedge-appearance-light-line': '#999',
  '--rv-wedge-appearance-light-detail': '#bbb',
  '--rv-wedge-appearance-dark-canvas': '#0A1A10',
  '--rv-wedge-appearance-dark-title': '#fff',
  '--rv-wedge-appearance-dark-line': '#666',
  '--rv-wedge-appearance-dark-detail': '#444',
});

/* Released Siri hero orb: a white mic on the purple orb and a purple glow under it. Both stay the same in every host
   theme. The glow is the light-theme purple (#6E3CD2) at 35%, fixed. Measured contrast of the white mic on the orb
   fill (T.purple): 2.64:1 dark (#C084FC), 6.48:1 light (#6E3CD2), 8.26:1 sun (#5B2FB8). Dark is under 3:1; it is the
   shipped look, so the port keeps it, and any change is its own PR. Four other roles also carry #fff; the orb keeps
   its own role so a change to another surface does not move it. */
export const wedgeSiriRoles = Object.freeze({
  '--rv-wedge-siri-orb-ink': '#fff',
  '--rv-wedge-siri-orb-glow': 'rgba(110,60,210,0.35)',
});

/* Released Cache dashboard accent actions: the Retry block and the Live toggle in its on state print white ink on the accent
   fill, and the on dot is the same white. It is the same in every host theme. Measured contrast of white on the accent
   fill (T.accent): 1.92:1 dark (#34D399), 4.55:1 light (#00875A), 7.43:1 sun (#016241). Dark is under 3:1; it is the
   shipped look, so the port keeps it, and any change is its own PR. The role is its own so a change to another white
   surface does not move it. */
export const wedgeCacheRoles = Object.freeze({
  '--rv-wedge-cache-accent-ink': '#fff',
});

/* Released Help and Support contact rows: each of the four rows prints a white 16px glyph on a solid icon tile (blue, accent,
   purple, purple). It is the same in every host theme. Measured contrast of white on the tile fills (T.blue, T.accent, T.purple):
   dark 1.81:1 (#22D3EE), 1.92:1 (#34D399), 2.64:1 (#C084FC); light 4.82:1 (#0A66FF), 4.55:1 (#00875A), 6.48:1 (#6E3CD2); sun 4.82:1,
   7.43:1 (#016241), 8.26:1 (#5B2FB8). The glyph is decorative (the tile is aria-hidden and the row carries its title). Dark is under
   3:1; it is the shipped look, so the port keeps it, and any change is its own PR. The role is its own so a change to another white
   surface does not move it. */
export const wedgeHelpRoles = Object.freeze({
  '--rv-wedge-help-row-glyph': '#fff',
});

/* Released follow-up row (Today, Quick triage): the Snooze action cell prints a white refresh glyph on the amber fill, while its
   label reads the theme on-accent ink. The glyph is the same in every host theme. Measured contrast of white on the amber fill
   (T.amber): 2.15:1 dark (#F59E0B), 4.67:1 light (#B25E09), 6.86:1 sun (#8A4A00). Dark is under 3:1; it is the shipped look, so the
   port keeps it, and any change is its own PR. The role is its own so a change to another white surface does not move it. */
export const wedgeFollowUpRoles = Object.freeze({
  '--rv-wedge-follow-up-snooze-glyph': '#fff',
});

/* Released sticky action bar (StickyActionBar appearance="wedge"): the raised shadow under the primary button and the white ink of the
   danger button. Both are the same in every host theme, and neither has a Wedge runtime alias, so each role carries its literal
   fallback in the look. Measured contrast of white on the danger fill (T.red): 3.76:1 dark (#EF4444), 5.38:1 light (#D70015),
   7.36:1 sun (#B00012). Dark is under 4.5:1 for 16px bold text; it is the shipped look, so the port keeps it, and the dark-ink fix
   (CATALOG Q1) is its own PR. The roles are their own so a change to another white or shadow does not move this bar. */
export const wedgeStickyRoles = Object.freeze({
  '--rv-wedge-sticky-shadow': '0 2px 10px rgba(0,0,0,0.18)',
  '--rv-wedge-sticky-danger-ink': '#fff',
});

/* Released daily digest card: each card in the stack casts one soft shadow, 20px down and 50px wide at 10% black. It is the same
   in every host theme, so it is a fixed role and no token carries it. It is a shadow, not text or a control, so it has no contrast
   ratio. The role is its own so a change to another shadow does not move it. */
export const wedgeDigestRoles = Object.freeze({
  '--rv-wedge-digest-card-shadow': '0 20px 50px rgba(0,0,0,0.10)',
});

/* Released Drive screen: the white ink that sits on the accent fill (the playing Listen slab, the first-run hero disc and its Scan an
   account button), and the raised shadow under the Listen to your stops bar (10px down, 26px wide, 16% black). Both are the same in
   every host theme, so they are fixed roles and no token carries them. The ink is the shipped white on the accent fill, so its contrast
   follows the accent of each theme, as it did before. The shadow has no contrast ratio. The roles are their own so a change to another
   white or shadow does not move this screen. */
export const wedgeDriveRoles = Object.freeze({
  '--rv-wedge-drive-ink': '#fff',
  '--rv-wedge-drive-play-day-shadow': '0 10px 26px rgba(0,0,0,0.16)',
  '--rv-wedge-drive-map-loading-ink': 'rgba(16,23,19,0.62)',
  '--rv-wedge-drive-map-panel-bg': 'rgba(16,23,19,0.97)',
  '--rv-wedge-drive-avatar-fallback': '#1F6B4A',
  '--rv-wedge-drive-email-ink': '#A5F3FC',
  '--rv-wedge-drive-email-border': 'rgba(34,211,238,0.35)',
  '--rv-wedge-drive-outcome-ink': '#E9D5FF',
  '--rv-wedge-drive-outcome-border': 'rgba(192,132,252,0.35)',
});

/* Released Live and Recent screen: the idle sort chip fill is a 5% black veil and each score chip fill a 4% one. Both are the fallback of
   `var(--surface-2, ...)`, and nothing in the shell defines `--surface-2`, so the fallback is the paint in every host theme. The chip that
   is on prints a white ink on the accent fill. All three are the same in every host theme, so they are fixed roles and no token carries them. The roles are their own so a change to
   another screen does not move them. They are two veils and an ink, and the on ink has the contrast `wedgeCacheRoles` documents. */
export const wedgeRecentsRoles = Object.freeze({
  '--rv-wedge-recents-sort-fill': 'rgba(0,0,0,0.05)',
  '--rv-wedge-recents-sort-ink': '#fff',
  '--rv-wedge-recents-score-fill': 'rgba(0,0,0,0.04)',
});

/* Released Industry settings: a chosen sub-category chip, the remove mark of a brand chip and the glyph on the vertical's own tile
   print white on a solid fill. It is the same in every host theme. Measured contrast of white on the accent fill (T.accent), the
   fill of the chip and the brand chip: 1.92:1 dark (#34D399), 4.55:1 light (#00875A), 7.43:1 sun (#016241). Dark is under 3:1; it is
   the shipped look, so the port keeps it, and any change is its own PR. The tile fill is the per-vertical tint of the taxonomy data
   and its glyph is decorative (the button carries its own label), so no ratio is printed for it. The role is its own so a change to
   another white surface does not move it. */
export const wedgeIndustryRoles = Object.freeze({
  '--rv-wedge-industry-ink': '#fff',
});

/* Released brief Share sheet: a white ink and one shadow, the same in every host theme. The base token `switchThumb` holds the same
   white in dark and light, but it names the switch thumb; no token carries the shadow. The ink is the glyph on the
   bolt tile of the preview, the glyph on each app tile, the glyph disc of the Redact names toggle and the thumb of that toggle. Measured
   contrast of the white ink on the accent fill (T.accent), which is the preview tile, the glyph disc and the toggle track when the toggle
   is on: 1.92:1 dark (#34D399), 4.55:1 light (#00875A), 7.43:1 sun (#016241). Dark is under 3:1; it is the shipped look, so the port keeps
   it, and any change is its own PR. The app tiles take their fills from the sheet data (three brand colors and the tertiary label) and
   each glyph sits beside a text label, so no ratio is printed for them. The shadow is the 1px lift under the toggle thumb. The role is its
   own so a change to another white surface does not move it. */
export const wedgeShareRoles = Object.freeze({
  '--rv-wedge-share-ink': '#fff',
  '--rv-wedge-share-thumb-shadow': '0 1px 3px rgba(0,0,0,0.2)',
});

/* Released Subscription screen, hero upgrade button and the upgrade rows: a white ink, the green lift under the hero, the frosted tile
   behind its bolt and the soft white of its second line, the same in every host theme. No token carries them. The ink is the hero text
   and glyphs and the bolt in each upgrade button. The hero fills with the accent gradient, so its ink measures 1.92:1 dark (#34D399), 4.55:1
   light (#00875A) on the accent end and 7.43:1 sun (#016241); dark is under 3:1, the shipped look, so the port keeps it and any change is
   its own PR. The role is its own so a change to another white surface does not move it. */
export const wedgeSubscriptionRoles = Object.freeze({
  '--rv-wedge-subscription-ink': '#fff',
  '--rv-wedge-subscription-hero-shadow': '0 8px 24px rgba(0,135,90,0.25)',
  '--rv-wedge-subscription-hero-tile': 'rgba(255,255,255,0.2)',
  '--rv-wedge-subscription-hero-sub': 'rgba(255,255,255,0.9)',
});

/* Released Blip answer bubble: the white ink of the user bubble on the native iOS shell, and the coin shadow, the same in every host theme.
   The web shell reads the on-accent token for the user bubble; the iOS shell forces white so the text reads on the saturated accent
   fill. Measured contrast of the white ink on the accent fill (T.accent) of the user bubble: 1.92:1 dark (#34D399), 4.55:1 light
   (#00875A), 7.43:1 sun (#016241). Dark is under 3:1; it is the shipped look, so the port keeps it, and any change is its own PR. The coin
   shadow is the 1px inner highlight (35% white) over the 2px drop under the amber rim (22% of a dark brown); no token carries it, and the
   coin glyph is decorative, so no ratio is printed. The role is its own so a change to another white surface does not move it. */
export const wedgeBlipRoles = Object.freeze({
  '--rv-wedge-blip-ink': '#fff',
  '--rv-wedge-blip-coin-shadow': 'inset 0 1px 0 rgba(255,255,255,0.35), 0 2px 6px rgba(120,60,6,0.22)',
});

/* Released Saved row (the Saved tab list): four paints that no token carries, the same in every host theme. The Hide and Delete
   swipe cells fill with the fixed red #DC2626. One white ink is the label and glyph of those two cells, the check of a selected
   row on the accent fill, and the desktop delete mark when it turns red. Two shadows: the resting lift of a desktop card (2px down,
   12px wide, 8px in, 45% black) and the soft drop under the desktop delete mark (4px down, 12px wide, 12% black). The shadows are not
   text or a control, so they have no contrast ratio. Measured contrast of the white ink: 4.83:1 on the fixed red; on the accent
   fill (T.accent) 1.92:1 dark (#34D399), 4.55:1 light (#00875A), 7.43:1 sun (#016241); on the hover red (T.red) 3.76:1 dark
   (#EF4444), 5.38:1 light (#D70015), 7.36:1 sun (#B00012). Dark is under 4.5:1 on the check and on the hover mark; it is the shipped
   look, so the port keeps it, and any change is its own PR. The roles are their own so a change to another white, red or shadow
   does not move this row.

   The Saved main column (the header above the list and the stale banner) adds two more. The chosen tab of the List and Board toggle
   lifts with a soft drop (1px down, 3px wide, 10% black). The Refresh oldest button of the stale banner prints white on the amber
   fill (T.amber). Measured contrast of that white: 2.15:1 dark (#F59E0B), 4.67:1 light (#B25E09), 6.86:1 sun (#8A4A00). Dark is under
   3:1; it is the shipped look, so the port keeps it, and any change is its own PR.

   The Saved control band adds one more. The More menu card drops a wide soft shadow (12px down, 32px wide, 28% black). It is a shadow, not
   text or a control, so it has no contrast ratio. */
export const wedgeSavedRoles = Object.freeze({
  '--rv-wedge-saved-swipe-red': '#DC2626',
  '--rv-wedge-saved-ink': '#fff',
  '--rv-wedge-saved-card-shadow': '0 2px 12px -8px rgba(0,0,0,0.45)',
  '--rv-wedge-saved-delete-shadow': '0 4px 12px rgba(0,0,0,0.12)',
  '--rv-wedge-saved-tab-shadow': '0 1px 3px rgba(0,0,0,0.10)',
  '--rv-wedge-saved-stale-ink': '#fff',
  '--rv-wedge-saved-menu-shadow': '0 12px 32px rgba(0,0,0,0.28)',
});

/* Released Saved long-press menu (phone): the peek card and the action menu under it each cast one wide soft shadow, 16px down, 48px
   blur, 40% black. It is the same in every host theme, so it is a fixed role and no token carries it. The shadow is not text or a
   control, so it has no contrast ratio. The role is its own so a change to another Saved shadow does not move this menu. */
export const wedgeSavedPeekRoles = Object.freeze({
  '--rv-wedge-saved-peek-shadow': '0 16px 48px rgba(0,0,0,0.4)',
});

/* Released Batch filter menu (the Batch tab, desktop web): the open menu of a filter or sort dropdown casts one long soft shadow, 24px
   down, 60px blur and 22px in, in solid black. It is the same in every host theme, so it is a fixed role and no token carries it. The
   shadow is not text or a control, so it has no contrast ratio. The role is its own so a change to another shadow does not move this menu. */
export const wedgeBatchRoles = Object.freeze({
  '--rv-wedge-batch-menu-shadow': '0 24px 60px -22px #000',
});

/* Released Outreach stage menu (the stage chip of a lead row): the open list of stages casts one soft shadow, 12px down and 32px blur, in
   black at 30%. It is the same in every host theme, so it is a fixed role and no token carries it. The shadow is not text or a control, so
   it has no contrast ratio. The role is its own so a change to the Saved or Batch menu shadow does not move this menu. */
export const wedgeOutreachRoles = Object.freeze({
  '--rv-wedge-outreach-menu-shadow': '0 12px 32px rgba(0,0,0,0.3)',
});

/* Released territory zone picker (ZoneSmartInput): the open list of matching zones casts one soft shadow, 12px down and 32px blur, in black
   at 18%. It is the same in every host theme, so it is a fixed role and no token carries it. The shadow is not text or a control, so it has
   no contrast ratio. The role is its own so a change to another menu shadow does not move this list. */
export const wedgeZoneInputRoles = Object.freeze({
  '--rv-wedge-zone-input-menu-shadow': '0 12px 32px rgba(0,0,0,0.18)',
});

/* Released Batch staged chip (the Batch tab): the initials disc of a staged account mixes 28% of white into its source tone at the top of
   a gradient. The white is the same in every host theme and no token carries it, so it is a fixed role. It is a tint, not text or a
   control, so it has no contrast ratio. */
export const wedgeBatchChipRoles = Object.freeze({
  '--rv-wedge-batch-chip-glint': '#ffffff',
});

/* Released journal timeline (the Notes card in the brief and the Notes app thread): the read-only outcome and promise cards cast one
   hairline shadow, 1px down and 2px wide at 4% black, in the light themes only, and the Lost result pill prints white ink on the solid
   red fill in the light themes. Both are the same in every host theme, so they are fixed roles and no token carries them. The shadow is
   not text or a control, so it has no contrast ratio. Measured contrast of white on the red fill (T.red) in the themes that print the
   ink: 5.38:1 light (#D70015), 7.36:1 sun (#B00012). The dark theme prints the soft tint pill and never this ink. The roles are their
   own so a change to another white or shadow does not move this timeline. */
export const wedgeJournalRoles = Object.freeze({
  '--rv-wedge-journal-read-shadow': '0 1px 2px rgba(0,0,0,0.04)',
  '--rv-wedge-journal-red-pill-ink': '#fff',
});

/* Released rail notepad (the Notes pad of the Brief when the journal is off): the writing surface casts one soft shadow, 1px down and 8px
   wide at 5% black, the same in every host theme, so it is a fixed role and no token carries it. The shadow is not text or a control, so it
   has no contrast ratio. The role is its own so a change to another shadow does not move the pad. */
export const wedgeRailPadRoles = Object.freeze({
  '--rv-wedge-rail-pad-shadow': '0 1px 8px rgba(0,0,0,0.05)',
});

/* Released route planner stop row (Drive, "Plan my day"): six paints that no token carries, the same in every host theme. The dragged
   row lifts on a 26px shadow (10px down, 24% black). The drop edge rule glows 3px wide in the brand green at 12% (a literal green, not
   the rep's accent). The Booked chip sits on a 12% amber veil. The Closed now chip and the amber hours flag share a 14% olive veil, and
   the amber hours flag prints the olive ink #8A6D00 on it. The red hours flag sits on a 12% red veil. The veils sit on the page fill,
   which differs by theme and screen, and the shadow and glow are not text, so no contrast ratio is printed. The roles are their own so
   a change to another amber, olive or shadow does not move this row. */
export const wedgeRouteStopRoles = Object.freeze({
  '--rv-wedge-route-stop-lift-shadow': '0 10px 26px rgba(0,0,0,0.24)',
  '--rv-wedge-route-stop-drop-glow': '0 0 0 3px rgba(0,135,90,0.12)',
  '--rv-wedge-route-stop-booked-fill': 'rgba(178,94,9,0.12)',
  '--rv-wedge-route-stop-closed-fill': 'rgba(138,109,0,0.14)',
  '--rv-wedge-route-stop-hours-ink': '#8A6D00',
  '--rv-wedge-route-stop-hours-red-fill': 'rgba(192,57,43,0.12)',
});

/* Released achievement card (the milestone overlay after a first brief, a streak, a saved step or a brief count): the card casts one
   two-layer shadow, a 24px drop at 30% black over a 4px drop at 10% black, and the badge glyph prints white ink on the solid fill of
   its kind. Both are the same in every host theme, so they are fixed roles and no token carries them. The shadow is not text or a
   control, so it has no contrast ratio. Measured contrast (WCAG 2) of the white ink on the three badge fills, in the order accent,
   amber, purple: 1.92, 2.15 and 2.64 to 1 in dark; 4.55, 4.67 and 6.48 in light; 7.43, 6.86 and 8.26 in sun. The dark figures sit below
   the 3:1 floor for a graphic. The source prints them, so the port keeps them, and a change to the ink is its own visible change. The
   roles are their own so a change to another white or shadow does not move this card. */
export const wedgeAchievementRoles = Object.freeze({
  '--rv-wedge-achievement-card-shadow': '0 24px 60px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.10)',
  '--rv-wedge-achievement-ink': '#fff',
});

/* Released Selling settings (role, deal size, sales cycle, competitors and Why people pick you): a chosen role, deal size or sales cycle chip,
   and the check glyph inside it, print white on the solid accent fill; the remove mark of a competitor or angle chip is an 18px circle filled with
   an 8% black veil over the soft chip. The white and the veil are the same in every host theme, so they are fixed roles and no token carries them. Measured
   contrast of the white ink on the accent fill (T.accent), the fill of the chosen chip: 1.92:1 dark (#34D399), 4.55:1 light (#00875A),
   7.43:1 sun (#016241). Dark is under 3:1; it is the shipped look, so the port keeps it, and any change is its own PR. The veil fills a
   round control whose glyph is the label ink over the chip, and it is not text, so no ratio is printed for it. The voice preview card
   (the Sheet of Preview my voice, above the deal size) lifts on a two-layer shadow, 4% black on 2px and 6% black on 18px, the same in every
   host theme; it is not text either. The roles are their own so a change to another white, veil or shadow does not move this screen. */
export const wedgeSellingRoles = Object.freeze({
  '--rv-wedge-selling-ink': '#fff',
  '--rv-wedge-selling-remove-fill': 'rgba(0,0,0,0.08)',
  '--rv-wedge-selling-preview-shadow': '0 1px 2px rgba(0,0,0,0.04), 0 6px 18px rgba(0,0,0,0.06)',
});

/* Released territory screen (Drive, the birdseye of the saved accounts): three paints that no token carries, the same in every host theme. The
   sticky "Plan my route" button lifts on a 20px shadow (6px down, 18% black). The map legend names the pipeline status with a literal red dot
   and a literal amber dot. No contrast ratio is printed: the shadow is not text, and the dots sit on the page fill, which differs by theme. The
   roles are their own, so a change to another red, amber or shadow does not move this screen. */
export const wedgeTerritoryRoles = Object.freeze({
  '--rv-wedge-territory-plan-shadow': '0 6px 20px rgba(0,0,0,0.18)',
  '--rv-wedge-territory-legend-red': '#D70015',
  '--rv-wedge-territory-legend-amber': '#B25E09',
});

/* Deal builder toast (the message pill that floats above the bottom of the screen for a moment after a save or a card export): it casts
   one 24px drop at 25% black, the same in every host theme, so it is a fixed role and no token carries it. The pill takes its fill and its
   ink from the label and background roles. The shadow is not text or a control, so it has no contrast ratio. The role is its own so a
   change to another shadow does not move this toast. */
export const wedgeDealRoles = Object.freeze({
  '--rv-wedge-deal-toast-shadow': '0 8px 24px rgba(0,0,0,0.25)',
});

/* Tester Tools Pending approvals and Cold cohort cards (founder-testers only): the Approve & send button and the Nudge button, once copied,
   print white ink on the accent fill. It is the same in every host theme. Measured contrast of white on the accent fill (T.accent): 1.92:1
   dark (#34D399), 4.55:1 light (#00875A), 7.43:1 sun (#016241). Dark is under 3:1; it is the shipped look, so the port keeps it, and any
   change is its own PR. The role is its own so a change to another white surface does not move it. */
export const wedgeTesterOpsRoles = Object.freeze({
  '--rv-wedge-tester-ops-accent-ink': '#fff',
});

/* Released calendar connect preflight (the Sheet that explains the Google consent before the sign-in hop): the two numbered step badges of
   the unverified-app warning print white ink on the solid amber fill, the same in every host theme, so the ink is a fixed role and no token
   carries it. Measured contrast (WCAG 2) of the white ink on the amber fill (T.amber): 2.15:1 dark (#F59E0B), 4.67:1 light and 6.86:1 sun.
   Dark is under 3:1 for a graphic. The source prints it, so the port keeps it, and a change to the ink is its own visible change. The role
   is its own so a change to another white does not move this Sheet. */
export const wedgeCalendarRoles = Object.freeze({
  '--rv-wedge-calendar-ink': '#fff',
});

/* Released Plan event sheet (prep-plan-calendar): the Remove confirm button prints white ink on the solid Plan danger fill, the same in every host
   theme, so the ink is a fixed role and no token carries it. Measured contrast (WCAG 2) of the white ink on the Plan danger fill: 3.76:1 on the
   dark palette (#EF4444) and 4.83:1 on the light palette (#DC2626; sun reads the light palette). The source prints it, so the port keeps it, and a
   change to the ink is its own visible change. The role is its own so a change to another white does not move this sheet. */
export const wedgePlanEventRoles = Object.freeze({
  '--rv-wedge-plan-event-ink': '#fff',
});

/* Released onboarding done screen (prep-screens-1): each Power up tile prints a white glyph on the tile's own solid hue (blue, accent or purple),
   the same in every host theme, so the ink is a fixed role and no token carries it. The source prints it, so the port keeps it, and a change to
   the ink is its own visible change. The role is its own so a change to another white does not move these tiles. */
export const wedgeOnboardDoneRoles = Object.freeze({
  '--rv-wedge-onboard-done-ink': '#fff',
});

/* Tester Tools Recent errors card (founder-testers only): the chosen scope tab (Just me, Org-wide) lifts off the track with one 1px drop at 6%
   black, the same in every host theme, so it is a fixed role and no token carries it. The shadow is not text or a control, so it has no contrast
   ratio. The role is its own so a change to another shadow does not move this tab. */
export const wedgeTesterErrorsRoles = Object.freeze({
  '--rv-wedge-tester-errors-scope-shadow': '0 1px 2px rgba(0,0,0,0.06)',
});

/* Released Wedge account widgets (stat box tip popover): the info tip casts one soft shadow, 16px down and 38px blur, in black at 38%. A second
   ring of 1px in the tile color follows it, so the view joins the two. The shadow is the same in every host theme, so it is a fixed role and no
   token carries it. The shadow is not text or a control, so it has no contrast ratio. The role is its own so a change to another shadow does not
   move this tip. */
export const wedgeAcctRoles = Object.freeze({
  '--rv-wedge-acct-tip-shadow': '0 16px 38px rgba(0,0,0,0.38)',
});

/* Released Wedge brief hero: three fixed paints that no theme changes. The fresh glow around the avatar is a 3px ring and a 32px halo in the
   emerald tone (18% and 32%). The building card title shimmers through a mint midpoint, and its bar carries a white sheen at 55%. All three are
   the same in every host theme, so they are fixed roles and no token carries them. None is text or a control, so none has a contrast ratio. The
   roles are their own so a change to another glow does not move this hero. */
export const wedgeBriefHeroRoles = Object.freeze({
  '--rv-wedge-brief-hero-glow': '0 0 0 3px rgba(52,211,153,0.18), 0 0 32px rgba(52,211,153,0.32)',
  '--rv-wedge-brief-hero-shimmer': '#6EE7B7',
  '--rv-wedge-brief-hero-sheen': 'rgba(255,255,255,0.55)',
});

/* Released Notes app dictation sheet: the round mic prints white ink on the solid red fill while it records (`.rvn-mic.live`). The white is the same
   in every host theme, so it is a fixed role and no token carries it. Measured contrast (WCAG 2) of the white ink on the red fill (T.red): 3.76:1
   dark (#EF4444), 5.38:1 light (#D70015), 7.36:1 sun (#B00012). All three clear the 3:1 floor for a graphic. The role is its own so a change to
   another white does not move this sheet. */
export const wedgeNotesRoles = Object.freeze({
  '--rv-wedge-notes-mic-ink': '#fff',
});

/* The Phone brief close shadow is fixed across host themes. */
export const wedgeBriefCloseRoles = Object.freeze({
  '--rv-wedge-brief-close-shadow': '0 4px 14px rgba(0,0,0,0.22)',
});

/* Released Blip sheet (Wedge): two fixed paints that no token carries, the same in every host theme. The avatar disc in the sheet header
   keeps a white highlight along its top edge (an inset 1px line, 35% white). The send button, when it can send, casts a soft amber lift
   (6px down, 16px wide, 35% amber). */
export const wedgeBlipSheetRoles = Object.freeze({
  '--rv-wedge-blip-sheet-avatar-shadow': 'inset 0 1px 0 rgba(255,255,255,0.35)',
  '--rv-wedge-blip-sheet-send-shadow': '0 6px 16px rgba(245,158,11,0.35)',
  '--rv-wedge-blip-sheet-nudge-shadow': '0 8px 22px rgba(0,0,0,0.35), 0 0 14px rgba(245,158,11,0.25)',
});

/* The decision maker sheet cards lay a soft black drop under the blue tinted glow. The drop is the same in every host theme. */
export const wedgeDmSheetRoles = Object.freeze({
  '--rv-wedge-dm-sheet-card-shadow': '0 1px 3px rgba(0,0,0,0.10)',
});

/* The email composer send circle is a fixed brand gradient per mail client (Gmail red, Outlook blue) with a matching pulse ring and glow, and white ink. All are the same in every host theme. */
export const wedgeEmailComposerRoles = Object.freeze({
  '--rv-wedge-email-composer-gmail-fill': 'linear-gradient(135deg,#F06A5E,#D93025)',
  '--rv-wedge-email-composer-gmail-ring': 'rgba(234,67,53,0.5)',
  '--rv-wedge-email-composer-gmail-glow': 'rgba(217,48,37,0.26)',
  '--rv-wedge-email-composer-outlook-fill': 'linear-gradient(135deg,#2B88D8,#0A5BC4)',
  '--rv-wedge-email-composer-outlook-ring': 'rgba(43,136,216,0.5)',
  '--rv-wedge-email-composer-outlook-glow': 'rgba(10,91,196,0.28)',
  '--rv-wedge-email-composer-ink': '#fff',
  '--rv-wedge-email-composer-regen-fill': 'linear-gradient(135deg,#047857 0%,#065F46 100%)',
  '--rv-wedge-email-composer-regen-border': 'rgba(255,255,255,0.14)',
  '--rv-wedge-email-composer-regen-shadow': '0 6px 16px rgba(4,120,87,0.30), inset 0 1px 0 rgba(255,255,255,0.18)',
  '--rv-wedge-email-composer-regen-icon-fill': 'rgba(255,255,255,0.18)',
  '--rv-wedge-email-composer-green-ring': 'rgba(4,120,87,0.45)',
  '--rv-wedge-email-composer-green-glow': 'rgba(4,120,87,0.3)',
  '--rv-wedge-email-composer-cta-gmail-fill': 'linear-gradient(135deg,#F28B6E 0%,#EA4335 50%,#C5221F 100%)',
  '--rv-wedge-email-composer-cta-gmail-ring': 'rgba(234,67,53,0.45)',
  '--rv-wedge-email-composer-cta-gmail-glow': 'rgba(197,34,31,0.3)',
  '--rv-wedge-email-composer-cta-outlook-fill': 'linear-gradient(135deg,#3B9AE8 0%,#0A64D8 55%,#0848A8 100%)',
  '--rv-wedge-email-composer-cta-outlook-ring': 'rgba(10,100,216,0.45)',
  '--rv-wedge-email-composer-cta-outlook-glow': 'rgba(10,72,168,0.32)',
  '--rv-wedge-email-composer-cta-icon-fill': 'rgba(255,255,255,0.2)',
  '--rv-wedge-email-composer-gmail-dot': '#EA4335',
  '--rv-wedge-email-composer-gmail-dot-ring': 'rgba(234,67,53,0.35)',
  '--rv-wedge-email-composer-outlook-dot': '#0A64D8',
  '--rv-wedge-email-composer-outlook-dot-ring': 'rgba(10,100,216,0.35)',
  '--rv-wedge-email-composer-client-shadow': '0 4px 12px rgba(0,0,0,0.08)',
});

/* The desktop Settings category list sits on an opaque fill in the legacy shell (the surface wash alone bleeds the paywall overlay), and each category tile carries white ink. Both are the same in every host theme. */
export const wedgeSettingsDeskRoles = Object.freeze({
  '--rv-wedge-settings-desk-fill': '#1C1C1E',
  '--rv-wedge-settings-desk-ink': '#fff',
});

/* The brief find bar hangs its result list and its zero hit panel in a fixed drop shadow, and the Ask Blip pill rides a fixed amber wash. Both are the same in every host theme. */
export const wedgeBriefReadRoles = Object.freeze({
  '--rv-wedge-brief-read-shadow': '0 14px 34px rgba(0,0,0,0.38)',
  '--rv-wedge-brief-read-ask-fill': 'rgba(245,158,11,0.14)',
});

/* The floating chrome of a phone brief (the walk-in ask card, the selection pill, the chapter pill) hangs in fixed drop shadows, and the
   chosen chapter chip takes a fixed white ink. All four are the same in every host theme. */
export const wedgeBriefFloatRoles = Object.freeze({
  '--rv-wedge-brief-float-walkin-shadow': '0 16px 48px rgba(0,0,0,0.35)',
  '--rv-wedge-brief-float-sel-shadow': '0 12px 40px rgba(0,0,0,0.28)',
  '--rv-wedge-brief-float-chapter-shadow': '0 6px 24px rgba(0,0,0,0.18)',
  '--rv-wedge-brief-float-chapter-ink': '#fff',
});

/* The Paywall period toggle lifts its chosen option on a fixed drop shadow and prints a fixed white ink on the accent fill. Both are the same in every host theme. */
export const wedgePaywallRoles = Object.freeze({
  '--rv-wedge-paywall-toggle-shadow': '0 2px 8px rgba(0,0,0,0.08)',
  '--rv-wedge-paywall-toggle-ink': '#fff',
});

/* The showcase upsell card of a brief ends its gradient on a fixed deep green and hangs a fixed drop shadow, and the resume pill hangs its own fixed drop shadow. All three are the same in every host theme. */
export const wedgeBriefFooterRoles = Object.freeze({
  '--rv-wedge-brief-footer-upsell-edge': '#1A2A20',
  '--rv-wedge-brief-footer-upsell-shadow': '0 14px 36px rgba(0,0,0,0.25)',
  '--rv-wedge-brief-footer-resume-shadow': '0 10px 28px rgba(0,0,0,0.28)',
});

/* The Today territory card is a fixed dark green card: the same paint in every host theme. These are its fills, edges, inks and shadows. */
export const wedgeTerritoryCardRoles = Object.freeze({
  '--rv-wedge-terr-card-card-border': 'rgba(52,211,153,0.25)',
  '--rv-wedge-terr-card-card-bg': '#0A1A10',
  '--rv-wedge-terr-card-card-shadow': '0 24px 60px rgba(0,0,0,0.5)',
  '--rv-wedge-terr-card-header-bg': 'radial-gradient(120% 140% at 100% 0%, rgba(52,211,153,0.22) 0%, rgba(52,211,153,0.05) 45%, rgba(10,26,16,0) 70%), linear-gradient(160deg, #13301F 0%, #0A1A10 100%)',
  '--rv-wedge-terr-card-header-border': 'rgba(52,211,153,0.22)',
  '--rv-wedge-terr-card-emerald': '#34d399',
  '--rv-wedge-terr-card-amber': '#F59E0B',
  '--rv-wedge-terr-card-title': '#F0F0F0',
  '--rv-wedge-terr-card-view-all-bg': 'rgba(52,211,153,0.1)',
  '--rv-wedge-terr-card-view-all-border': 'rgba(52,211,153,0.35)',
  '--rv-wedge-terr-card-view-all-ink': '#6EE7B7',
  '--rv-wedge-terr-card-cyan': '#22D3EE',
  '--rv-wedge-terr-card-saved-bg': 'rgba(52,211,153,0.08)',
  '--rv-wedge-terr-card-saved-border': 'rgba(52,211,153,0.3)',
  '--rv-wedge-terr-card-towns-bg': 'rgba(34,211,238,0.08)',
  '--rv-wedge-terr-card-towns-border': 'rgba(34,211,238,0.3)',
  '--rv-wedge-terr-card-routable-bg': 'rgba(245,158,11,0.1)',
  '--rv-wedge-terr-card-routable-border': 'rgba(245,158,11,0.35)',
  '--rv-wedge-terr-card-tile-label': '#94A3B8',
  '--rv-wedge-terr-card-map-border': 'rgba(52,211,153,0.28)',
  '--rv-wedge-terr-card-map-bg': '#07140D',
  '--rv-wedge-terr-card-map-shadow': 'inset 0 0 40px rgba(0,0,0,0.6)',
  '--rv-wedge-terr-card-glass': 'rgba(10,26,16,0.85)',
  '--rv-wedge-terr-card-next-border': 'rgba(245,158,11,0.4)',
  '--rv-wedge-terr-card-next-glow': '0 0 8px #F59E0B',
  '--rv-wedge-terr-card-next-ink': '#FCD34D',
  '--rv-wedge-terr-card-zoom-border': 'rgba(52,211,153,0.3)',
  '--rv-wedge-terr-card-zoom-ink': '#E2E8F0',
  '--rv-wedge-terr-card-zoom-divider': 'rgba(52,211,153,0.2)',
  '--rv-wedge-terr-card-fit-border': 'rgba(34,211,238,0.4)',
  '--rv-wedge-terr-card-fit-ink': '#67E8F9',
  '--rv-wedge-terr-card-legend-border': 'rgba(52,211,153,0.2)',
  '--rv-wedge-terr-card-legend-ink': '#CBD5E1',
  '--rv-wedge-terr-card-cta-ink': '#06281c',
  '--rv-wedge-terr-card-cta-shadow': '0 0 0 4px rgba(52,211,153,0.15), 0 10px 28px rgba(52,211,153,0.40)',
});

/* The avatar onboarding hero wordmark and the Scout guide hue are fixed paint. The hero reads the dark pair or the light pair by the theme the
   surface was given, not by the host cascade, so both pairs are always defined. */
export const wedgeAvatarOnboardRoles = Object.freeze({
  '--rv-wedge-avob-hero-ink-dark': '#F0F0F0',
  '--rv-wedge-avob-hero-ink-light': '#111111',
  '--rv-wedge-avob-hero-amber': '#F59E0B',
  '--rv-wedge-avob-tagline-dark': '#B0B0B0',
  '--rv-wedge-avob-tagline-light': '#555555',
  '--rv-wedge-avob-scout-hue': '#f26bb8',
  '--rv-wedge-avob-scout-hue-bg': 'rgba(242,107,184,0.12)',
});

/* The white ink of a chosen outcome tile glyph (Won, Pushed, Lost) on the card that logs a call. The ink is the same in every host theme. */
export const wedgeOutcomeCaptureRoles = Object.freeze({
  '--rv-wedge-outcome-on-ink': '#fff',
});

/* The Brief just-researched notice rings its pill with a soft emerald halo. The halo is the same in every host theme. */
export const wedgeBriefNoticeRoles = Object.freeze({
  '--rv-wedge-brief-notice-fresh-ring': '0 0 0 4px rgba(52,211,153,0.10)',
});

/* The Brief recovery panels (the unconfirmed account and the failed research) paint white ink on the amber or red actions, a fixed dark badge for a repeated failure and two press highlights. They are the same in every host theme. */
export const wedgeBriefRecoveryRoles = Object.freeze({
  '--rv-wedge-brief-recovery-ink': '#fff',
  '--rv-wedge-brief-recovery-tap-cta': 'rgba(255,255,255,0.15)',
  '--rv-wedge-brief-recovery-tap-chip': 'rgba(255,255,255,0.1)',
  '--rv-wedge-brief-recovery-badge-bg': 'rgba(15,23,42,0.55)',
  '--rv-wedge-brief-recovery-badge-border': 'rgba(255,255,255,0.08)',
  '--rv-wedge-brief-recovery-badge-ink': 'rgba(255,255,255,0.78)',
  '--rv-wedge-brief-recovery-badge-icon': 'rgba(255,255,255,0.6)',
});

/* The checked state of a Brief move paints a dark ink on its accent disc. The ink is the same in every host theme. */
export const wedgeBriefMovesRoles = Object.freeze({
  '--rv-wedge-brief-moves-done-ink': '#03110b',
});

/* The first sample brief mission briefing is a fixed dark scene with emerald HUD paint. It is the same in every host theme. */
export const wedgeBriefCineRoles = Object.freeze({
  '--rv-wedge-brief-cine-bg': '#0A1A10',
  '--rv-wedge-brief-cine-ink': '#E6E7EB',
  '--rv-wedge-brief-cine-emerald': '#34D399',
  '--rv-wedge-brief-cine-hud-emerald': 'rgba(52,211,153,0.78)',
  '--rv-wedge-brief-cine-slate20': 'rgba(226,232,240,0.2)',
  '--rv-wedge-brief-cine-slate42': 'rgba(226,232,240,0.42)',
  '--rv-wedge-brief-cine-slate55': 'rgba(226,232,240,0.55)',
  '--rv-wedge-brief-cine-slate62': 'rgba(226,232,240,0.62)',
  '--rv-wedge-brief-cine-slate70': 'rgba(226,232,240,0.7)',
  '--rv-wedge-brief-cine-slate72': 'rgba(226,232,240,0.72)',
  '--rv-wedge-brief-cine-slate86': 'rgba(226,232,240,0.86)',
  '--rv-wedge-brief-cine-grid': 'linear-gradient(rgba(52,211,153,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,0.06) 1px, transparent 1px)',
  '--rv-wedge-brief-cine-aurora': 'radial-gradient(circle at center, rgba(52,211,153,0.40) 0%, rgba(52,211,153,0.18) 24%, rgba(52,211,153,0) 60%)',
  '--rv-wedge-brief-cine-scan': 'linear-gradient(90deg, transparent 0%, rgba(52,211,153,0.0) 8%, rgba(52,211,153,0.65) 50%, rgba(52,211,153,0.0) 92%, transparent 100%)',
  '--rv-wedge-brief-cine-scan-glow': 'drop-shadow(0 0 12px rgba(52,211,153,0.6))',
  '--rv-wedge-brief-cine-radar-disc': 'radial-gradient(circle at 32% 28%, rgba(255,255,255,0.18) 0%, transparent 40%), #34D399',
  '--rv-wedge-brief-cine-bolt-glow': 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
  '--rv-wedge-brief-cine-radar-glow0': '0 0 0 0 rgba(52,211,153,0.0), 0 0 40px 8px rgba(52,211,153,0.20), inset 0 0 20px rgba(52,211,153,0.18)',
  '--rv-wedge-brief-cine-radar-glow50': '0 0 0 8px rgba(52,211,153,0.08), 0 0 60px 16px rgba(52,211,153,0.36), inset 0 0 28px rgba(52,211,153,0.30)',
  '--rv-wedge-brief-cine-cta0': '0 0 0 0 rgba(52,211,153,0.0)',
  '--rv-wedge-brief-cine-cta60': '0 0 0 14px rgba(52,211,153,0.0), 0 18px 40px rgba(52,211,153,0.36)',
  '--rv-wedge-brief-cine-cta100': '0 0 0 0 rgba(52,211,153,0.0), 0 14px 32px rgba(52,211,153,0.28)',
  '--rv-wedge-brief-cine-pulse0': '0 14px 32px rgba(52,211,153,0.28), 0 0 0 0 rgba(52,211,153,0.45)',
  '--rv-wedge-brief-cine-pulse50': '0 14px 32px rgba(52,211,153,0.40), 0 0 0 12px rgba(52,211,153,0.0)',
});

/* The phone Rep XP hub lays three fixed black shadows: the inset groove of its progress tracks, the soft drop under its coaching cards and outcome
   tiles, and the drop of the upgrade callout. Each is the same in every host theme. */
export const wedgeRepXpDashRoles = Object.freeze({
  '--rv-wedge-repxp-dash-track-shadow': 'inset 0 1px 2px rgba(0,0,0,0.14)',
  '--rv-wedge-repxp-dash-tile-drop': '0 1px 3px rgba(0,0,0,0.10)',
  '--rv-wedge-repxp-dash-callout-shadow': '0 10px 30px rgba(0,0,0,0.18)',
});

/* The Brief signal score bars lay a fixed inset groove (black, 12 percent) in their tracks. It is the same in every host theme. */
export const wedgeBriefPeopleRoles = Object.freeze({
  '--rv-wedge-brief-people-track-shadow': 'inset 0 1px 2px rgba(0,0,0,0.12)',
});

/* The phone dock drops a fixed shadow under the raised center action (20 percent black) and under the Go anywhere coin (25 percent black). Both are the same in every host theme. */
export const wedgeTabBarRoles = Object.freeze({
  '--rv-wedge-tab-bar-fab-shadow': '0 10px 24px rgba(0,0,0,0.20)',
  '--rv-wedge-tab-bar-coin-shadow': '0 4px 14px rgba(0,0,0,0.25)',
});

/* The Brief feedback card lays two fixed drops under its container (calm, and the surfaced one with an emerald lift), and its thank-you state paints a
   white check on the tone disc with a fixed emerald ring and a fixed emerald scan line. Each is the same in every host theme. */
export const wedgeBriefFeedbackRoles = Object.freeze({
  '--rv-wedge-brief-feedback-calm-shadow': '0 1px 2px rgba(0,0,0,0.04), 0 6px 18px rgba(0,0,0,0.05)',
  '--rv-wedge-brief-feedback-surfaced-shadow': '0 1px 2px rgba(0,0,0,0.04), 0 10px 28px rgba(0,135,90,0.12)',
  '--rv-wedge-brief-feedback-check-ink': '#fff',
  '--rv-wedge-brief-feedback-check-ring': '0 0 0 6px rgba(52,211,153,0.16), 0 8px 18px rgba(52,211,153,0.30)',
  '--rv-wedge-brief-feedback-scan': 'linear-gradient(90deg, transparent 0%, rgba(52,211,153,0.0) 10%, rgba(52,211,153,0.7) 50%, rgba(52,211,153,0.0) 90%, transparent 100%)',
  '--rv-wedge-brief-feedback-scan-glow': 'drop-shadow(0 0 8px rgba(52,211,153,0.6))',
});

/* Fixed Brief paints outside the Brief palette objects (prep-brief-v2-kit hero constants, the work-this-account tiles, the outreach hover
 * shadow, the LinkedIn mark, the gold "I walked in" CTA). The exact literals the prototype shipped; the same value in every host theme.
 * The channel and tone tables that append a hex alpha (${tone}dd) stay literal in the sources. */
export const wedgeBriefRoles = Object.freeze({
  '--rv-wedge-brief-hero-bg': 'radial-gradient(60% 90% at 80% 30%, rgba(52,211,153,0.22) 0%, rgba(52,211,153,0.05) 50%, rgba(13,27,17,0) 75%), radial-gradient(50% 70% at 100% 0%, rgba(245,158,11,0.10) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
  '--rv-wedge-brief-hero-bg-mobile': 'radial-gradient(80% 70% at 85% 20%, rgba(52,211,153,0.24) 0%, rgba(13,27,17,0) 70%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
  '--rv-wedge-brief-hero-border': '1px solid rgba(52,211,153,0.22)',
  '--rv-wedge-brief-hero-text': '#E2E8F0',
  '--rv-wedge-brief-hero-text-bright': '#F0F0F0',
  '--rv-wedge-brief-hero-text-dim': '#94A3B8',
  '--rv-wedge-brief-hero-shadow': '0 24px 60px rgba(9,18,16,0.28)',
  '--rv-wedge-brief-hero-action-focus': '#34D399',
  '--rv-wedge-brief-tile-emerald-ink': '#34D399',
  '--rv-wedge-brief-tile-cyan-ink': '#22D3EE',
  '--rv-wedge-brief-tile-amber-ink': '#F59E0B',
  '--rv-wedge-brief-tile-violet-ink': '#A78BFA',
  '--rv-wedge-brief-outreach-hover-shadow': 'rgba(15,23,42,0.10)',
  '--rv-wedge-brief-linkedin-blue': '#0A66C2',
  '--rv-wedge-brief-on-fill': '#fff',
  '--rv-wedge-brief-cta-gold': '#F59E0B',
  '--rv-wedge-brief-cta-gold-ink': '#241A00',
});

/* Fixed Pipeline paints (Plan calendar, Pipeline board, Find accounts and Notes): the exact literals the prototype shipped.
 * Same value in every theme. The Plan and Pipeline screens carry a dark and a light palette object; the keys that hold a
 * solid accent (emerald, cyan, violet, amber, slate, danger) stay literal there because the screens append a hex alpha
 * such as ${t.emerald}26 to them, and a var() cannot take that suffix. Everything else in the two palettes is a role below. */
export const wedgePipelineRoles = Object.freeze({
  '--rv-wedge-pipeline-cal-dark-page-bg': 'radial-gradient(70% 40% at 50% 0%, rgba(52,211,153,0.16) 0%, rgba(13,27,17,0) 65%), linear-gradient(170deg, #0C1E15 0%, #091210 60%, #0D1B11 100%)',
  '--rv-wedge-pipeline-board-dark-page-bg': 'radial-gradient(70% 36% at 50% 0%, rgba(52,211,153,0.15) 0%, rgba(13,27,17,0) 62%), linear-gradient(170deg, #0C1E15 0%, #091210 60%, #0D1B11 100%)',
  '--rv-wedge-pipeline-dark-header-bg': 'linear-gradient(180deg, rgba(19,48,31,0.95) 0%, rgba(10,26,16,0.85) 100%)',
  '--rv-wedge-pipeline-cal-dark-surface': 'rgba(6,17,11,0.6)',
  '--rv-wedge-pipeline-board-dark-surface': 'rgba(6,17,11,0.72)',
  '--rv-wedge-pipeline-cal-dark-surface-solid': '#0A1A10',
  '--rv-wedge-pipeline-board-dark-surface-solid': 'rgba(6,17,11,0.6)',
  '--rv-wedge-pipeline-dark-line': 'rgba(52,211,153,0.18)',
  '--rv-wedge-pipeline-cal-dark-line-strong': 'rgba(52,211,153,0.35)',
  '--rv-wedge-pipeline-board-dark-line-strong': 'rgba(52,211,153,0.40)',
  '--rv-wedge-pipeline-cal-dark-grid-bg': 'rgba(6,17,11,0.45)',
  '--rv-wedge-pipeline-cal-dark-hairline': 'rgba(148,163,184,0.10)',
  '--rv-wedge-pipeline-board-dark-hairline': 'rgba(148,163,184,0.22)',
  '--rv-wedge-pipeline-dark-text': '#F0F0F0',
  '--rv-wedge-pipeline-dark-text-mid': '#E2E8F0',
  '--rv-wedge-pipeline-dark-text-dim': '#94A3B8',
  '--rv-wedge-pipeline-dark-text-faint': '#8494A8',
  '--rv-wedge-pipeline-dark-on-accent': '#06281c',
  '--rv-wedge-pipeline-dark-scrim': 'rgba(3,9,6,0.72)',
  '--rv-wedge-pipeline-dark-shadow': '0 16px 40px rgba(0,0,0,0.55)',
  '--rv-wedge-pipeline-cal-dark-scan-highlight': 'rgba(52,211,153,0.07)',
  '--rv-wedge-pipeline-cal-light-page-bg': 'radial-gradient(70% 40% at 50% 0%, rgba(13,148,102,0.10) 0%, rgba(233,243,236,0) 65%), linear-gradient(170deg, #EFF6F1 0%, #F7FAF8 60%, #E9F3EC 100%)',
  '--rv-wedge-pipeline-board-light-page-bg': 'radial-gradient(70% 36% at 50% 0%, rgba(13,148,102,0.10) 0%, rgba(233,243,236,0) 62%), linear-gradient(170deg, #EFF6F1 0%, #F7FAF8 60%, #E9F3EC 100%)',
  '--rv-wedge-pipeline-light-header-bg': 'linear-gradient(180deg, rgba(240,249,244,0.96) 0%, rgba(255,255,255,0.9) 100%)',
  '--rv-wedge-pipeline-light-header-shadow': '0 6px 20px rgba(15,42,30,0.06)',
  '--rv-wedge-pipeline-light-surface': '#FFFFFF',
  '--rv-wedge-pipeline-light-surface-solid': '#FFFFFF',
  '--rv-wedge-pipeline-light-line': 'rgba(13,148,102,0.18)',
  '--rv-wedge-pipeline-cal-light-line-strong': 'rgba(13,148,102,0.35)',
  '--rv-wedge-pipeline-board-light-line-strong': 'rgba(13,148,102,0.40)',
  '--rv-wedge-pipeline-cal-light-grid-bg': '#F2F7F4',
  '--rv-wedge-pipeline-cal-light-hairline': 'rgba(100,116,139,0.20)',
  '--rv-wedge-pipeline-board-light-hairline': 'rgba(100,116,139,0.22)',
  '--rv-wedge-pipeline-light-text': '#0D1A13',
  '--rv-wedge-pipeline-light-text-mid': '#1F2A24',
  '--rv-wedge-pipeline-light-text-dim': '#5B6B78',
  '--rv-wedge-pipeline-light-text-faint': '#56656F',
  '--rv-wedge-pipeline-light-on-accent': '#FFFFFF',
  '--rv-wedge-pipeline-light-scrim': 'rgba(15,42,30,0.35)',
  '--rv-wedge-pipeline-light-shadow': '0 16px 40px rgba(15,42,30,0.14)',
  '--rv-wedge-pipeline-cal-light-block-bg': '#FFFFFF',
  '--rv-wedge-pipeline-cal-light-scan-highlight': 'rgba(13,148,102,0.07)',
  '--rv-wedge-pipeline-board-dark-sticky-bg': 'rgba(9,18,16,0.94)',
  '--rv-wedge-pipeline-board-dark-row-hover': 'rgba(52,211,153,0.06)',
  '--rv-wedge-pipeline-board-light-sticky-bg': 'rgba(247,250,248,0.96)',
  '--rv-wedge-pipeline-board-light-row-hover': 'rgba(13,148,102,0.07)',
  '--rv-wedge-pipeline-fac-placeholder-ink': '#64748B',
  '--rv-wedge-pipeline-fac-focus-ring': '#34d399',
  '--rv-wedge-pipeline-fac-done-fill': '#34d399',
  '--rv-wedge-pipeline-fac-done-ink': '#06281c',
  '--rv-wedge-pipeline-fac-light-frame': '#00875A',
  '--rv-wedge-pipeline-fac-light-surface': '#FFFFFF',
  '--rv-wedge-pipeline-fac-dark-surface': '#0A1A10',
  '--rv-wedge-pipeline-fac-light-shadow': '0 10px 28px rgba(0,135,90,0.12)',
  '--rv-wedge-pipeline-notes-light-red': '#C62828',
  '--rv-wedge-pipeline-notes-light-amber': '#8F5200',
  '--rv-wedge-pipeline-notes-light-green': '#18794A',
  '--rv-wedge-pipeline-notes-light-blue': '#1D5FD1',
  '--rv-wedge-pipeline-notes-light-purple': '#7A3FC2',
  '--rv-wedge-pipeline-notes-light-gray': '#5C646C',
  '--rv-wedge-pipeline-notes-light-hl-yellow': 'rgba(250,204,21,.34)',
  '--rv-wedge-pipeline-notes-light-hl-green': 'rgba(52,211,153,.28)',
  '--rv-wedge-pipeline-notes-light-hl-blue': 'rgba(96,165,250,.28)',
  '--rv-wedge-pipeline-notes-light-hl-pink': 'rgba(244,114,182,.28)',
  '--rv-wedge-pipeline-notes-dark-red': '#FF8A80',
  '--rv-wedge-pipeline-notes-dark-amber': '#FBBF24',
  '--rv-wedge-pipeline-notes-dark-green': '#6EE7A0',
  '--rv-wedge-pipeline-notes-dark-blue': '#8AB4FF',
  '--rv-wedge-pipeline-notes-dark-purple': '#C9A7FF',
  '--rv-wedge-pipeline-notes-dark-gray': '#A3ACB5',
  '--rv-wedge-pipeline-notes-dark-hl-yellow': 'rgba(250,204,21,.22)',
  '--rv-wedge-pipeline-notes-dark-hl-green': 'rgba(52,211,153,.22)',
  '--rv-wedge-pipeline-notes-dark-hl-blue': 'rgba(96,165,250,.24)',
  '--rv-wedge-pipeline-notes-dark-hl-pink': 'rgba(244,114,182,.24)',
  '--rv-wedge-pipeline-notes-follow-ink': '#22D3EE',
  '--rv-wedge-pipeline-notes-white-ink': '#fff',
  '--rv-wedge-pipeline-notes-follow-wash': 'rgba(34,211,238,.14)',
  '--rv-wedge-pipeline-notes-overlay-scrim': 'rgba(10,20,15,.42)',
});

const wedge: ProductOverride = {
  /* Plan decision D1 (issue 3451, Luke, 2026-10-25): the base glass is 3 and 6 percent, the fill the consoles, shop and recon
     ship. The wedge keeps its 10 and 14 percent tiles until the candidate cutover, when Luke views both. The prototype reads
     TOKENS_DARK (base plus this override), so the wedge paints what it painted before. */
  dark: {
    surface: 'rgba(52,211,153,0.10)',
    surfaceAlt: 'rgba(52,211,153,0.14)',
  },
  roles: {
    dark: {
      ...wedgeShellRoles,
      ...wedgeAlphaRoles,
      ...wedgeLandingRoles,
      ...wedgeMapPopupRoles,
      ...wedgeHomeRoles,
      ...wedgePipelineRoles,
      ...wedgeSharedRoles,
      ...wedgeNoticeRoles,
      ...wedgeNextStatusRoles,
      ...wedgeSettingsActionRoles,
      ...wedgeSettingsRoles,
      ...wedgeAppNavbarRoles,
      ...wedgeBriefFoldRoles,
      ...wedgePrivacyRoles,
      ...wedgeAppearanceRoles,
      ...wedgeSiriRoles,
      ...wedgeCacheRoles,
      ...wedgeHelpRoles,
      ...wedgeFollowUpRoles,
      ...wedgeStickyRoles,
      ...wedgeDigestRoles,
      ...wedgeDriveRoles,
      ...wedgeRecentsRoles,
      ...wedgeIndustryRoles,
      ...wedgeShareRoles,
      ...wedgeSubscriptionRoles,
      ...wedgeBlipRoles,
      ...wedgeSavedRoles,
      ...wedgeSavedPeekRoles,
      ...wedgeJournalRoles,
      ...wedgeRailPadRoles,
      ...wedgeBatchRoles,
      ...wedgeOutreachRoles,
      ...wedgeZoneInputRoles,
      ...wedgeBatchChipRoles,
      ...wedgeRouteStopRoles,
      ...wedgeAchievementRoles,
      ...wedgeSellingRoles,
      ...wedgeTerritoryRoles,
      ...wedgeDealRoles,
      ...wedgeTesterOpsRoles,
      ...wedgeCalendarRoles,
      ...wedgePlanEventRoles,
      ...wedgeOnboardDoneRoles,
      ...wedgeTesterErrorsRoles,
      ...wedgeAcctRoles,
      ...wedgeBriefHeroRoles,
      ...wedgeNotesRoles,
      ...wedgeBriefCloseRoles,
      ...wedgeBriefNoticeRoles,
      ...wedgeBriefRecoveryRoles,
      ...wedgeBriefMovesRoles,
      ...wedgeDmSheetRoles,
      ...wedgeBlipSheetRoles,
      ...wedgeEmailComposerRoles,
      ...wedgeSettingsDeskRoles,
      ...wedgeOutcomeCaptureRoles,
      ...wedgeBriefReadRoles,
      ...wedgeBriefFloatRoles,
      ...wedgePaywallRoles,
      ...wedgeBriefFooterRoles,
      ...wedgeTerritoryCardRoles,
      ...wedgeAvatarOnboardRoles,
      ...wedgeBriefCineRoles,
      ...wedgeRepXpDashRoles,
      ...wedgeBriefPeopleRoles,
      ...wedgeTabBarRoles,
      ...wedgeBriefFeedbackRoles,
      ...wedgeBriefRoles,
      '--wedge-bv2-screen': 'radial-gradient(58% 55% at 50% 32%, rgba(52,211,153,0.20) 0%, rgba(52,211,153,0.06) 46%, rgba(13,27,17,0) 72%), radial-gradient(70% 50% at 84% 8%, rgba(245,158,11,0.06) 0%, rgba(13,27,17,0) 60%), linear-gradient(160deg, #0C1E15 0%, #091210 55%, #0D1B11 100%)',
      '--wedge-bv2-card': 'rgba(10,26,16,0.72)',
      '--wedge-bv2-card2': 'rgba(52,211,153,0.04)',
      '--wedge-bv2-line': 'rgba(52,211,153,0.15)',
      '--wedge-bv2-line2': 'rgba(52,211,153,0.25)',
      '--wedge-bv2-text': '#E2E8F0',
      '--wedge-bv2-sec': '#94A3B8',
      '--wedge-bv2-muted': '#94A3B8',
      '--wedge-bv2-acc': '#34d399',
      '--wedge-bv2-acc-ink': '#34d399',
      '--wedge-bv2-acc-soft': 'rgba(52,211,153,0.15)',
      '--wedge-bv2-amber': '#F59E0B',
      '--wedge-bv2-amber-ink': '#F59E0B',
      '--wedge-bv2-amber-soft': 'rgba(245,158,11,0.15)',
      '--wedge-bv2-purple-ink': '#C084FC',
      '--wedge-bv2-purple-soft': 'rgba(192,132,252,0.15)',
      '--wedge-bv2-cyan-ink': '#22D3EE',
      '--wedge-bv2-cyan-soft': 'rgba(34,211,238,0.15)',
      '--wedge-bv2-red-ink': '#EF4444',
      '--wedge-bv2-red-soft': 'rgba(239,68,68,0.15)',
      '--wedge-bv2-slate': '#475569',
      '--wedge-bv2-shadow': '0 12px 32px rgba(0,0,0,0.35)',
      '--wedge-bv2-glass': 'rgba(10,26,16,0.82)',
      '--wedge-bv2-field': 'rgba(52,211,153,0.06)',
      '--wedge-bv2-hero-accent-soft': 'rgba(52,211,153,0.16)',
      '--wedge-bv2-hero-accent-ink': '#34d399',
      '--wedge-bv2-hero-amber-soft': 'rgba(245,158,11,0.15)',
      '--wedge-bv2-hero-amber-ink': '#F59E0B',
      '--wedge-bv2-hero-purple-soft': 'rgba(192,132,252,0.16)',
      '--wedge-bv2-hero-purple-ink': '#C084FC',
      '--wedge-bv2-hero-cyan-soft': 'rgba(34,211,238,0.16)',
      '--wedge-bv2-hero-cyan-ink': '#22D3EE',
      '--wedge-bv2-hero-red-soft': 'rgba(239,68,68,0.18)',
      '--wedge-bv2-hero-red-ink': '#F87171',
      '--wedge-bv2-hero-neutral-soft': 'rgba(148,163,184,0.16)',
      '--wedge-bv2-hero-neutral-ink': '#94A3B8',
      '--wedge-bv2-stat-border': 'rgba(52,211,153,0.16)',
      '--wedge-bv2-stat-bg': 'rgba(52,211,153,0.04)',
      '--wedge-bv2-stat-divider': 'rgba(52,211,153,0.14)',
      '--wedge-bv2-stat-label': '#94A3B8',
      '--wedge-bv2-stat-value': '#F0F0F0',
    },
    light: {
      ...wedgeShellRoles,
      ...wedgeAlphaRoles,
      ...wedgeLandingRoles,
      ...wedgeMapPopupRoles,
      ...wedgeHomeRoles,
      ...wedgePipelineRoles,
      ...wedgeSharedRoles,
      ...wedgeNoticeRoles,
      ...wedgeNextStatusRoles,
      ...wedgeSettingsActionRoles,
      ...wedgeSettingsRoles,
      ...wedgeAppNavbarRoles,
      ...wedgeBriefFoldRoles,
      ...wedgePrivacyRoles,
      ...wedgeAppearanceRoles,
      ...wedgeSiriRoles,
      ...wedgeCacheRoles,
      ...wedgeHelpRoles,
      ...wedgeFollowUpRoles,
      ...wedgeStickyRoles,
      ...wedgeDigestRoles,
      ...wedgeDriveRoles,
      ...wedgeRecentsRoles,
      ...wedgeIndustryRoles,
      ...wedgeShareRoles,
      ...wedgeSubscriptionRoles,
      ...wedgeBlipRoles,
      ...wedgeSavedRoles,
      ...wedgeSavedPeekRoles,
      ...wedgeJournalRoles,
      ...wedgeRailPadRoles,
      ...wedgeBatchRoles,
      ...wedgeOutreachRoles,
      ...wedgeZoneInputRoles,
      ...wedgeBatchChipRoles,
      ...wedgeRouteStopRoles,
      ...wedgeAchievementRoles,
      ...wedgeSellingRoles,
      ...wedgeTerritoryRoles,
      ...wedgeDealRoles,
      ...wedgeTesterOpsRoles,
      ...wedgeCalendarRoles,
      ...wedgePlanEventRoles,
      ...wedgeOnboardDoneRoles,
      ...wedgeTesterErrorsRoles,
      ...wedgeAcctRoles,
      ...wedgeBriefHeroRoles,
      ...wedgeNotesRoles,
      ...wedgeBriefCloseRoles,
      ...wedgeBriefNoticeRoles,
      ...wedgeBriefRecoveryRoles,
      ...wedgeBriefMovesRoles,
      ...wedgeDmSheetRoles,
      ...wedgeBlipSheetRoles,
      ...wedgeEmailComposerRoles,
      ...wedgeSettingsDeskRoles,
      ...wedgeOutcomeCaptureRoles,
      ...wedgeBriefReadRoles,
      ...wedgeBriefFloatRoles,
      ...wedgePaywallRoles,
      ...wedgeBriefFooterRoles,
      ...wedgeTerritoryCardRoles,
      ...wedgeAvatarOnboardRoles,
      ...wedgeBriefCineRoles,
      ...wedgeRepXpDashRoles,
      ...wedgeBriefPeopleRoles,
      ...wedgeTabBarRoles,
      ...wedgeBriefFeedbackRoles,
      ...wedgeBriefRoles,
      '--wedge-bv2-screen': 'linear-gradient(180deg,#FAFBFC 0%,#F1F5F3 100%)',
      '--wedge-bv2-card': '#FFFFFF',
      '--wedge-bv2-card2': '#F6F8F7',
      '--wedge-bv2-line': 'rgba(0,0,0,0.08)',
      '--wedge-bv2-line2': 'rgba(0,0,0,0.14)',
      '--wedge-bv2-text': '#1A1A2E',
      '--wedge-bv2-sec': '#475569',
      '--wedge-bv2-muted': '#64748B',
      '--wedge-bv2-acc': '#34d399',
      '--wedge-bv2-acc-ink': '#047857',
      '--wedge-bv2-acc-soft': 'rgba(52,211,153,0.14)',
      '--wedge-bv2-amber': '#F59E0B',
      '--wedge-bv2-amber-ink': '#B45309',
      '--wedge-bv2-amber-soft': 'rgba(245,158,11,0.14)',
      '--wedge-bv2-purple-ink': '#7E22CE',
      '--wedge-bv2-purple-soft': 'rgba(192,132,252,0.16)',
      '--wedge-bv2-cyan-ink': '#0E7490',
      '--wedge-bv2-cyan-soft': 'rgba(34,211,238,0.14)',
      '--wedge-bv2-red-ink': '#B91C1C',
      '--wedge-bv2-red-soft': 'rgba(239,68,68,0.10)',
      '--wedge-bv2-slate': '#94A3B8',
      '--wedge-bv2-shadow': '0 1px 2px rgba(16,24,20,0.04), 0 10px 30px rgba(16,24,20,0.06)',
      '--wedge-bv2-glass': 'rgba(250,251,252,0.86)',
      '--wedge-bv2-field': '#F1F4F3',
      '--wedge-bv2-hero-accent-soft': 'rgba(52,211,153,0.16)',
      '--wedge-bv2-hero-accent-ink': '#34d399',
      '--wedge-bv2-hero-amber-soft': 'rgba(245,158,11,0.15)',
      '--wedge-bv2-hero-amber-ink': '#F59E0B',
      '--wedge-bv2-hero-purple-soft': 'rgba(192,132,252,0.16)',
      '--wedge-bv2-hero-purple-ink': '#C084FC',
      '--wedge-bv2-hero-cyan-soft': 'rgba(34,211,238,0.16)',
      '--wedge-bv2-hero-cyan-ink': '#22D3EE',
      '--wedge-bv2-hero-red-soft': 'rgba(239,68,68,0.18)',
      '--wedge-bv2-hero-red-ink': '#F87171',
      '--wedge-bv2-hero-neutral-soft': 'rgba(148,163,184,0.16)',
      '--wedge-bv2-hero-neutral-ink': '#94A3B8',
      '--wedge-bv2-stat-border': 'rgba(52,211,153,0.16)',
      '--wedge-bv2-stat-bg': 'rgba(52,211,153,0.04)',
      '--wedge-bv2-stat-divider': 'rgba(52,211,153,0.14)',
      '--wedge-bv2-stat-label': '#94A3B8',
      '--wedge-bv2-stat-value': '#F0F0F0',
    },
  },
};

/*
 * Owner console (owner.repvantage.io, data-rv-product="owner"): products.consoles plus the lane-wide A2
 * token set (issue 2808; docs/handoff/reports/2026-10-17-owner-lane-discovery/PICKUP-PROMPT.md A2 and A3, values from
 * docs/design/home-dashboard-reference/CLAUDE-CODE-HANDOFF.md). This is a separate product on purpose: products.admin
 * is built from products.consoles and the legacy admin page inlines it (scripts/build-console-tokens.mjs),
 * so editing consoles in place would restyle Teams and the legacy admin page. Role names the console already
 * reads keep their names; roles the base has no equivalent for (title ink, track, dim, tint pairs, lines, weights,
 * motion, focus) are new --rv-* roles, so product code that reads them stays inside the ui-ratchet nonRvVar count.
 *
 * Rule for every value: take the A2 value when it clears the gate, else the nearest value that does and keep the A2
 * original in the comment. Gates: text 4.5:1 on the page and on the card, chip text 4.5:1 on its own tint, graphics and
 * focus 3:1. Dark worst case is the brightest point of the page gradient (the canvas emerald radial at 20% over its
 * lightest stop #0C1E15 = rgb(20,66,47)) with the A2 card fill rgba(52,211,153,0.045) on top = rgb(21,73,52); a chip is
 * its hue at 14% over that. Light worst case is the page #F4F6F8 and the white card. packages/tokens/test/
 * owner-product.test.mjs re-measures all of it.
 */
const OWNER_HUES = ['em', 'cy', 'pu', 'am', 'red'] as const;
type OwnerHue = (typeof OWNER_HUES)[number];
/* A2 brights (fills, borders, bars and dots in both themes; dark-mode text before the contrast lift) and their tint RGB. */
const ownerBright: Record<OwnerHue, string> = { em: '#34D399', cy: '#22D3EE', pu: '#C084FC', am: '#F59E0B', red: '#EF4444' };
const ownerRgb: Record<OwnerHue, string> = { em: '52,211,153', cy: '34,211,238', pu: '192,132,252', am: '245,158,11', red: '239,68,68' };
/* A2 colored text on light, and chart lines and bars on light. */
const ownerInkLight: Record<OwnerHue, string> = { em: '#0F7A55', cy: '#0E6A7D', pu: '#7E3BB8', am: '#9A5B06', red: '#B91C1C' };
const ownerLineLight: Record<OwnerHue, string> = { em: '#1F7A52', cy: '#1F6E85', pu: '#6D3FBF', am: '#B5560A', red: '#C43B3B' };
/* Dark colored text: the A2 bright lifted toward white until it reads 4.6:1 on its own 14% chip over the card. A2 brights
   measure 4.11 (em), 4.33 (cy), 3.30 (pu), 3.94 (am) and 2.62 (red) there. */
const ownerInkDark: Record<OwnerHue, string> = { em: '#5DDCAD', cy: '#3FD9F0', pu: '#D4ABFD', am: '#F7B23E', red: '#F69898' };
/* Dark chart lines and bars: the A2 bright, except red. #EF4444 is 2.75:1 on the card and fails the 3:1 graphics gate;
   #F15A5A is 3.14:1. Em, cy, pu and am measure 5.39, 5.73, 3.92 and 4.82 as is. */
const ownerLineDark: Record<OwnerHue, string> = { ...ownerBright, red: '#F15A5A' };

/* Per hue: bright (fill, border, dot), ink (text), line (chart mark), fill and border tints (A2: fill 0.10 light and 0.14
   dark, border 0.42 light and 0.38 dark). */
const ownerHueRoles = (theme: ThemeName): Record<string, string> =>
  Object.fromEntries(
    OWNER_HUES.flatMap((h) => [
      [`--rv-hue-${h}-bright`, ownerBright[h]],
      [`--rv-hue-${h}-ink`, theme === 'light' ? ownerInkLight[h] : ownerInkDark[h]],
      [`--rv-hue-${h}-line`, theme === 'light' ? ownerLineLight[h] : ownerLineDark[h]],
      [`--rv-hue-${h}-fill`, `rgba(${ownerRgb[h]},${theme === 'light' ? '0.10' : '0.14'})`],
      [`--rv-hue-${h}-border`, `rgba(${ownerRgb[h]},${theme === 'light' ? '0.42' : '0.38'})`],
    ]),
  );

/* Roles that do not change between themes. */
const ownerShared: Record<string, string> = {
  /* A2 type: card headlines and owner badges. A2 named Rajdhani 700, off the brand canon; plan decision D9 (issue 3451,
     Luke, 2026-10-25) sets Orbitron 700 at the same size. No component reads the role yet, so the swap paints nothing. */
  '--rv-font-card': "'Orbitron', 'Jost', sans-serif",
  /* A2 weight map "bold": Jost body 500, medium 600, strong 700, title 700, button 600; Orbitron label 800. Share Tech
     Mono is thickened with a text stroke, 0.25px on mono strings and 0.6px on numbers (apps/owner-console/app/src/shell/a2.css). */
  '--rv-w-body': '500',
  '--rv-w-med': '600',
  '--rv-w-strong': '700',
  '--rv-w-title': '700',
  '--rv-w-btn': '600',
  '--rv-w-label': '800',
  '--rv-mono-stroke': '0.25px',
  '--rv-num-stroke': '0.6px',
  /* A2 motion: easing is the base --rv-ease. Hovers 150ms; drawers 400ms in and 250ms out; menus scale from 0.97. */
  '--rv-dur-hover': '150ms',
  '--rv-dur-drawer-in': '400ms',
  '--rv-dur-drawer-out': '250ms',
  '--rv-menu-scale-from': '0.97',
  '--rv-focus-width': '2px',
  '--rv-focus-offset': '2px',
  /* A2: text on solid amber buttons, 7.36:1 on #F59E0B. */
  '--rv-on-amber': '#06281C',
};

const owner: ProductOverride = {
  dark: {
    ...consoles.dark,
    /* A2 dark neutrals. Card fill 0.045 (consoles 0.03) and hairline 0.12 (consoles 0.15 base). Elevated surfaces
       (tooltips, popovers) are the lighter #112419 instead of a shadow: popover text on it measures 7.1:1 or better. */
    surface: 'rgba(52,211,153,0.045)',
    separator: 'rgba(52,211,153,0.12)',
    popoverBg: '#112419',
    /* A2 dark cards are flat: elevation comes from the lighter fill, not a shadow. */
    shadowCard: 'none',
    /* A2 text #E2E8F0 and secondary #A3B1C4 (4.75:1 on the card). A2 muted #8494AB is 3.36:1 on the card and its dim
       #5B6B80 is 1.90:1, so the two quieter levels share #9FAFC6 (4.60:1 on the card). The dim level is the new decorative
       role --rv-dim. */
    label: '#E2E8F0',
    labelSec: '#A3B1C4',
    labelTer: '#9FAFC6',
    labelQuad: '#9FAFC6',
    /* A2 tint fill 0.14 (base 0.15). */
    accentSoft: 'rgba(52,211,153,0.14)',
    blueSoft: 'rgba(34,211,238,0.14)',
    amberSoft: 'rgba(245,158,11,0.14)',
    purpleSoft: 'rgba(192,132,252,0.14)',
    redSoft: 'rgba(239,68,68,0.14)',
    /* Chip text. consoles red #FA8080 and purple #CC99FD read 3.98 and 3.96 on their own chip once the page carries the
       A2 gradient; these two read 4.64 and 4.60. consoles blue #67E8F9 (5.40) and amber #FBB454 (4.74) already clear. */
    red: '#F69898',
    purple: '#D4ABFD',
    accentInk: '#5DDCAD',
    blueInk: '#3FD9F0',
    amberInk: '#F7B23E',
  },
  light: {
    ...consoles.light,
    /* A2 light neutrals: page #F4F6F8 (consoles #F3F5F8), white card (consoles emerald glass 0.045), navy-slate hairline 0.08. */
    bg: '#F4F6F8',
    surface: '#FFFFFF',
    separator: 'rgba(15,23,42,0.08)',
    /* A2 secondary #475569 (7.0:1 on the page). A2 muted #64748B is 4.39:1 on the page (4.76 on white), so muted is
       #607087 (4.6:1); A2 dim #94A3B8 is 2.4:1 and is the decorative role --rv-dim. */
    labelSec: '#475569',
    labelTer: '#607087',
    labelQuad: '#607087',
    shadowCard: '0 1px 2px rgba(15,23,42,0.04)',
    /* A2 inks on light (consoles are deeper: #036B4D, #92400E, #155E75, #6D28D9). Each clears 4.6:1 on the page and on its
       own 10% tint. A2 tint fill is 0.10 (consoles 0.12 to 0.16). */
    accent: '#0F7A55',
    accentSoft: 'rgba(52,211,153,0.10)',
    accentInk: '#0F7A55',
    amber: '#9A5B06',
    amberSoft: 'rgba(245,158,11,0.10)',
    amberInk: '#9A5B06',
    purple: '#7E3BB8',
    purpleSoft: 'rgba(192,132,252,0.10)',
    blue: '#0E6A7D',
    blueSoft: 'rgba(34,211,238,0.10)',
    blueInk: '#0E6A7D',
    red: '#B91C1C',
    redSoft: 'rgba(239,68,68,0.10)',
  },
  roles: {
    dark: {
      ...consoles.roles?.dark,
      ...ownerShared,
      ...ownerHueRoles('dark'),
      /* A2 dark page is the canonical screenBg gradient (base canvas), never flat #0A1A10. consoles painted three faint
         radial glows over the flat color. Long pages stretch the gradient over the document height. */
      '--chrome-page': 'var(--rv-canvas)',
      /* A2 body weight 500 and meta 500 (consoles 400); section labels are Orbitron 800. */
      '--chrome-body-weight': '500',
      '--type-fw-body': '500',
      '--type-fw-meta': '500',
      '--type-fw-heading': '800',
      /* A2 ink levels: title #F1F5F9, body = text, sub = secondary, meta and caption = muted. */
      '--ink-title': '#F1F5F9',
      '--ink-heading': '#F1F5F9',
      '--ink-num': '#F1F5F9',
      '--ink-body': '#E2E8F0',
      '--ink-sub': '#A3B1C4',
      '--ink-meta': '#9FAFC6',
      '--ink-caption': '#9FAFC6',
      /* Cards read the A2 surface, hairline and flat elevation. */
      '--console-card-fill': 'var(--rv-surface)',
      '--console-card-shadow': 'none',
      /* New A2 neutrals with no base token. Dim is decorative only (axis ticks, placeholders, disabled): 1.90:1 on the card. */
      '--rv-title': '#F1F5F9',
      '--rv-dim': '#5B6B80',
      '--rv-track': 'rgba(255,255,255,0.07)',
      '--rv-track-2': 'rgba(255,255,255,0.06)',
      '--rv-border-strong': 'rgba(52,211,153,0.24)',
      '--rv-shadow-pop': '0 8px 24px rgba(0,0,0,0.35)',
      /* A2 rest-state bar rgba(52,211,153,0.55) is 2.75:1 on the card; 0.62 clears 3:1. */
      '--rv-bar-rest': 'rgba(52,211,153,0.62)',
      /* A2 focus ring #34D399, 5.9:1 on the page gradient. */
      '--rv-focus-ring': '#34D399',
    },
    light: {
      ...consoles.roles?.light,
      ...ownerShared,
      ...ownerHueRoles('light'),
      '--chrome-page': 'var(--rv-bg)',
      '--chrome-body-weight': '500',
      '--type-fw-body': '500',
      '--type-fw-meta': '500',
      '--type-fw-heading': '800',
      '--ink-title': '#111827',
      '--ink-heading': '#111827',
      '--ink-num': '#111827',
      '--ink-body': '#1A1A2E',
      '--ink-sub': '#475569',
      '--ink-meta': '#607087',
      '--ink-caption': '#607087',
      '--console-card-fill': 'var(--rv-surface)',
      '--console-card-border': 'var(--rv-separator)',
      '--console-card-shadow': '0 1px 2px rgba(15,23,42,0.04)',
      '--rv-title': '#111827',
      '--rv-dim': '#94A3B8',
      '--rv-track': 'rgba(15,23,42,0.05)',
      '--rv-track-2': 'rgba(15,23,42,0.06)',
      '--rv-border-strong': 'rgba(15,23,42,0.16)',
      '--rv-shadow-pop': '0 8px 24px rgba(15,23,42,0.12)',
      /* A2 rest-state bar rgba(31,122,82,0.55) is 2.29:1 on white; 0.76 clears 3:1 on white and on the page. */
      '--rv-bar-rest': 'rgba(31,122,82,0.76)',
      /* A2 focus ring #34D399 is 1.92:1 on white and fails 3:1; the A2 em ink is 5.3:1 on white and 4.9:1 on the page. */
      '--rv-focus-ring': '#0F7A55',
    },
  },
};

/*
 * Demo apps (apps/demo), seeded verbatim from the shipped T block in apps/demo/src/tokens/theme.js (plan wave 1.6, issue
 * 3451). The demo is dark only, so there is no light block. Base keys that already equal theme.js are left out (since
 * plan decision D1 that includes surface1 and surface2, the 3 and 6 percent base glass). Nothing
 * reads this product yet; the demo adopts the package in plan wave 3. SignShop v4 inside apps/demo keeps products.signshop.
 */
const demo: ProductOverride = {
  dark: {
    /* theme.js borderHover 0.25 (base 0.42). */
    separatorBold: 'rgba(52,211,153,0.25)',
    /* theme.js textMuted: slate-500, 3.78:1 on #0A1A10, as remix ships; base #8494A8 is lighter. */
    labelTer: '#64748B',
    /* theme.js stacks have no system fallbacks. */
    font: "'Jost', sans-serif",
    mono: "'Share Tech Mono', monospace",
    fontDisplay: "'Orbitron', sans-serif",
  },
  roles: {
    /* theme.js keys with no base token: the deeper field, the third glass step, the dim scrim, the bright accent, and the
       glass and glass2 card shells. */
    dark: {
      '--rv-demo-bg-deeper': '#061008',
      '--rv-demo-surface-3': 'rgba(52,211,153,0.10)',
      '--rv-demo-glass-dim': 'rgba(10,26,16,0.6)',
      '--rv-demo-accent-bright': '#5EEAD4',
      '--rv-demo-glass-radius': '14px',
      '--rv-demo-glass-shadow': '0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(52,211,153,0.05)',
      '--rv-demo-glass-2-shadow': '0 16px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(52,211,153,0.08)',
    },
  },
};

export const products: Record<ProductName, ProductOverride> = {
  wedge,
  marketing,
  remix,
  recon,
  consoles,
  owner,
  shop,
  admin,
  comply: {},
  demo,
  signshop: {
    roles: {
      light: {
        "--rv-signshop-bg": "#F7F5F0",
        "--rv-signshop-bg-card": "#FFFFFF",
        "--rv-signshop-bg-sunken": "#EDEAE3",
        "--rv-signshop-ink": "#0F172A",
        "--rv-signshop-ink-2": "#334155",
        "--rv-signshop-ink-3": "#64748B",
        "--rv-signshop-border": "rgba(15,23,42,0.08)",
        "--rv-signshop-border-2": "rgba(15,23,42,0.14)",
        "--rv-signshop-rule": "#1A1A1A",
        "--rv-signshop-d-bg": "#0A1A10",
        "--rv-signshop-d-card": "rgba(255,255,255,0.04)",
        "--rv-signshop-d-ink": "#E2E8F0",
        "--rv-signshop-d-ink-2": "#94A3B8",
        "--rv-signshop-d-border": "rgba(255,255,255,0.08)",
        "--rv-signshop-f-display": "'Orbitron', sans-serif",
        "--rv-signshop-f-body": "'Jost', sans-serif",
        "--rv-signshop-f-mono": "'Share Tech Mono', monospace",
        "--rv-signshop-f-script": "'Caveat', cursive",
        "--rv-signshop-f-serif": "'Playfair Display', serif",
        "--rv-signshop-s-1": "4px",
        "--rv-signshop-s-2": "8px",
        "--rv-signshop-s-3": "12px",
        "--rv-signshop-s-4": "16px",
        "--rv-signshop-s-5": "20px",
        "--rv-signshop-s-6": "24px",
        "--rv-signshop-s-7": "32px",
        "--rv-signshop-s-8": "48px",
        "--rv-signshop-s-9": "64px",
        "--rv-signshop-r-xs": "3px",
        "--rv-signshop-r-sm": "6px",
        "--rv-signshop-r-md": "8px",
        "--rv-signshop-r-lg": "12px",
        "--rv-signshop-r-xl": "16px",
        "--rv-signshop-r-2xl": "20px",
        "--rv-signshop-sh-1": "0 2px 8px rgba(15,23,42,0.05), 0 1px 2px rgba(15,23,42,0.04)",
        "--rv-signshop-sh-2": "0 8px 24px rgba(15,23,42,0.08), 0 2px 4px rgba(15,23,42,0.04)",
        "--rv-signshop-sh-3": "0 16px 48px rgba(15,23,42,0.12), 0 4px 8px rgba(15,23,42,0.06)",
        "--rv-signshop-sh-press": "inset 0 0 0 1px rgba(15,23,42,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
        "--rv-signshop-ease": "cubic-bezier(0.16, 1, 0.3, 1)",
        "--rv-signshop-ease-spring": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
      dark: {
        "--rv-signshop-bg": "#0A1A10",
        "--rv-signshop-bg-card": "rgba(255,255,255,0.04)",
        "--rv-signshop-bg-sunken": "rgba(0,0,0,0.25)",
        "--rv-signshop-ink": "#E2E8F0",
        "--rv-signshop-ink-2": "#94A3B8",
        "--rv-signshop-ink-3": "#64748B",
        "--rv-signshop-border": "rgba(255,255,255,0.08)",
        "--rv-signshop-border-2": "rgba(255,255,255,0.14)",
        "--rv-signshop-rule": "rgba(255,255,255,0.6)",
        "--rv-signshop-d-bg": "#0A1A10",
        "--rv-signshop-d-card": "rgba(255,255,255,0.04)",
        "--rv-signshop-d-ink": "#E2E8F0",
        "--rv-signshop-d-ink-2": "#94A3B8",
        "--rv-signshop-d-border": "rgba(255,255,255,0.08)",
        "--rv-signshop-f-display": "'Orbitron', sans-serif",
        "--rv-signshop-f-body": "'Jost', sans-serif",
        "--rv-signshop-f-mono": "'Share Tech Mono', monospace",
        "--rv-signshop-f-script": "'Caveat', cursive",
        "--rv-signshop-f-serif": "'Playfair Display', serif",
        "--rv-signshop-s-1": "4px",
        "--rv-signshop-s-2": "8px",
        "--rv-signshop-s-3": "12px",
        "--rv-signshop-s-4": "16px",
        "--rv-signshop-s-5": "20px",
        "--rv-signshop-s-6": "24px",
        "--rv-signshop-s-7": "32px",
        "--rv-signshop-s-8": "48px",
        "--rv-signshop-s-9": "64px",
        "--rv-signshop-r-xs": "3px",
        "--rv-signshop-r-sm": "6px",
        "--rv-signshop-r-md": "8px",
        "--rv-signshop-r-lg": "12px",
        "--rv-signshop-r-xl": "16px",
        "--rv-signshop-r-2xl": "20px",
        "--rv-signshop-sh-1": "0 2px 8px rgba(0,0,0,0.4)",
        "--rv-signshop-sh-2": "0 8px 24px rgba(0,0,0,0.5)",
        "--rv-signshop-sh-3": "0 16px 48px rgba(0,0,0,0.6)",
        "--rv-signshop-sh-press": "inset 0 0 0 1px rgba(15,23,42,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
        "--rv-signshop-ease": "cubic-bezier(0.16, 1, 0.3, 1)",
        "--rv-signshop-ease-spring": "cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
    },
  },
};
